import _ from 'lodash'
import * as THREE from 'three'
import { v4 as uuidv4 } from 'uuid'

import Utils from '@/utils/utils'
import Config from '@glass-designer/config'
import { EditorUserError } from './../utils/editorErrors'
import MathsUtils from '@/utils/mathsUtils'
import EditorHullUtils from './../utils/editorHullUtils'
import EditorUtils from './../utils/editorUtils'
import EditorCutoutUtils from './../utils/editorCutoutUtils'
import EditorDatumUtils from './../utils/editorDatumUtils'
import EditorShapeUVGenerator from './../utils/editorShapeUVGenerator'

import GlassBaseHole from './../model/glassBaseHole'
import GlassPanelItem from './../model/glassPanelItem'
import GlassDatumLine from './../model/glassDatumLine'
import GlassSplitLine from './../model/glassSplitLine'
import BaseObject from '../objects/baseObject'
import LineObject from '../library/three/LineObject'
import GlassShapeGroup from './glassShapeGroup'
import ShapeCutout from './shapeCutout'
import RadiusCut from './radiusCut'
import EditorClipper from './../utils/editorClipper'
import GlassSegment from '@/model/glassSegment'
import CustomHole from '@glass-designer/model/holes/customHole'
import AssemblyGroup from '@glass-designer/model/assemblyGroup'
import ApprovedIconObject from '@glass-designer/objects/approvedIconObject'
import ShapeWarningIconObject from '@glass-designer/objects/shapeWarningIconObject'

import GlassDesignerTypes from '../types'
import { FilterOptionTypes, OrderEditorCutoutTypes, OrderEditorGeometryShapeTypes, AccountSettingKeys, StockFaceTypes, OrderEditorEdgeworkSettingTypes, StockGrainDirectionTypes, StockMaterialTypes, OrderItemEdgeworkTypes } from '@/store/types'
import { AlignmentTypes, AlignmentTypeLookup } from '@/store/mathTypes'


/*
 * GlassShape
 * The main glass shape item representing a panel. A GlassShape starts as a pre-defined shape & then gets modified
 * The shape of the GlassShape is built up from an array of GlassSegments
 */
export default class GlassShape extends BaseObject {
  constructor(editor, itemData = {}) {
    super(editor)
    this.objectClassName = 'GlassShape'

    // Glass Shape Settings
    this.shapeType = GlassDesignerTypes.LibraryItemShapeTypes.custom
    this.glassColour = editor.getTheme().panelColour
    this.defaultEdgeworkConfigId = itemData.defaultEdgeworkConfigId || 0
    this.isStamped = !!itemData.isStamped

    // Assembly link info
    this.assemblyLinkUUID = uuidv4();

    // Rendering details
    this.sceneObject = new THREE.Object3D()
    this.editor.scene.add(this.sceneObject)
    this.mesh = null
    this.material = null
    this.outlineMaterial = null
    this.outlineObj = null
    this.cuttingLineMaterial = null
    this.selectedSegmentObjects = []
    this.polishedSegmentObjects = []
    this.edgeworkFlagObjects = []

    // Internal details
    this._editingSegmentList = false

    // Approval tick
    this.approvedIconPosition = new THREE.Vector2()
    this.approvedIconObj = new ApprovedIconObject(this.editor, { colour: '#20D020', iconSize: 24 })
    this.addChildObject(this.approvedIconObj)

    // Warning exclamation
    this.warningIconPosition = new THREE.Vector2()
    this.warningIconObj = new ShapeWarningIconObject(this.editor, { colour: '#f8ac59', iconSize: 32 })
    this.addChildObject(this.warningIconObj)

    // Cut regions display object. Used to see how the shape is cut from glass
    this.cutShapeInfoObj = null

    this.shapeHighlightOnly = false     // If highlight only, only displays the outline (used for editing mode)
    this.shapeModified = false

    // 3D Viewing mode
    this.view3DModeOffset = new THREE.Vector3(0, 0, 0)
    this.view3DModeRotation = 0

    // Settings
    this.segmentScaleMax = 3.0
    this.segmentVertexScaleMax = 3.0
    this.segmentListEditable = true

    // Glass Item data
    // Same format as OrderItem
    this.itemData = {
      orderItemId: itemData.id,
      stockId: parseInt(itemData.stockId),
      stockPriceGroupId: parseInt(itemData.stockPriceGroupId),
      description: itemData.description || '',
      mark: itemData.mark || '',
      notes: itemData.notes || '',
      quantity: parseInt(itemData.quantity),
      stock: _.cloneDeep(itemData.stock || {}),
      stockPriceGroup: _.cloneDeep(itemData.stockPriceGroup || {}),
      glassLevelId: parseInt(itemData.glassLevelId || 0),
      glassLevel: _.cloneDeep(itemData.glassLevel || {}),
      unitPrice: parseFloat(itemData.unitPrice || 0),
      unitPriceOverridden: !!itemData.unitPriceOverridden,
      originalUnitPrice: parseFloat(itemData.originalUnitPrice || 0),
      requiresCustomerApproval: !!itemData.requiresCustomerApproval,
      isExternal: !!itemData.isExternal,
      isCancelled: !!itemData.isCancelled,
      isNCRLocked: !!itemData.isNCRLocked,
      isInvoiced: !!itemData.isInvoiced,
      isDespatched: !!itemData.isDespatched,
      invoicedQuantity: itemData.invoicedQuantity || 0,
      despatchedQuantity: itemData.despatchedQuantity || 0,
      grainDirection: itemData.grainDirection,
      stockFaceSideUpType: itemData.stockFaceSideUpType
    }

    // An array of GlassSegment that build up the piece of glass
    // GlassSegment's contain all the edge-cuts / corner-cuts as well as all the edge details
    this.segmentList = []

    // Cached full point list generated from the segments
    // Used for point-in-poly testing. Cached for performance
    this.cachedPointList = []

    // List of reviewed approval items for the shape
    // Used by the editor review sidebar before finishing the order editor
    this.reviewApprovalList = []
    this.isReviewApproved = false     // Set if all approval items are approved

    // Review Approval Override
    // After opening an order, all panels are automatically approved until modified
    this.reviewApprovalOverride = false

    this.listenMouseEvents = true
  }


  /**
   * Update the view level on the glass shapes
   * Adjusts the scene object matrix for editing / viewing modes
   */
  viewLevelUpdated(viewLevel) {
    if(this.shapeHighlightOnly)
      return

    if(viewLevel == GlassDesignerTypes.EditorViewLevelTypes.view3D) {
      let bounds = this.calculateSegmentBounds()
      if(bounds.isEmpty())
        return

      this.sceneObject.position.set(this.view3DModeOffset.x, this.view3DModeOffset.y, this.view3DModeOffset.z + bounds.max.y)
      this.sceneObject.rotation.set(MathsUtils.degreesToRadians(-90), 0, this.view3DModeRotation)

    } else {
      this.sceneObject.position.set(0, 0, 0)
      this.sceneObject.rotation.set(0, 0, 0)
    }

    this.updateSceneObject()
  }


  /**
   * Update the item data from an OrderItem based object
   */
  updateOrderItemData(orderItem, orderItemList) {    
    this.itemData.orderItemId = orderItem.id
    this.itemData.stockId = parseInt(orderItem.stockId)
    this.itemData.stockPriceGroupId = parseInt(orderItem.stockPriceGroupId)
    this.itemData.glassLevelId = parseInt(orderItem.glassLevelId || 0)

    this.itemData.description = orderItem.description || ''
    this.itemData.mark = orderItem.mark || ''
    this.itemData.notes = orderItem.notes || ''

    this.itemData.quantity = parseInt(orderItem.quantity)
    this.itemData.unitPrice = parseFloat(orderItem.unitPrice || 0)
    this.itemData.unitPriceOverridden = !!orderItem.unitPriceOverridden
    this.itemData.originalUnitPrice = parseFloat(orderItem.originalUnitPrice || 0)

    this.itemData.stock = _.cloneDeep(orderItem.stock || {})
    this.itemData.stockPriceGroup = _.cloneDeep(orderItem.stockPriceGroup || {})
    this.itemData.glassLevel = _.cloneDeep(orderItem.glassLevel || {})

    this.itemData.requiresCustomerApproval = !!orderItem.requiresCustomerApproval
    this.itemData.isExternal = !!orderItem.isExternal
    this.itemData.isCancelled = !!orderItem.isCancelled
    this.itemData.isNCRLocked = !!orderItem.isNCRLocked
    this.itemData.isInvoiced = !!orderItem.isInvoiced
    this.itemData.isDespatched = !!orderItem.isDespatched
    this.itemData.invoicedQuantity = parseInt(orderItem.invoicedQuantity || 0)
    this.itemData.despatchedQuantity = parseInt(orderItem.despatchedQuantity || 0)

    this.itemData.discountRate = parseFloat(orderItem.discountRate || 0)
    this.itemData.discountAmount = parseFloat(orderItem.discountAmount || 0)

    this.itemData.grainDirection = orderItem.grainDirection
    this.itemData.stockFaceSideUpType = orderItem.stockFaceSideUpType
  }


  /**
   * Get the shape type
   * Set internally via shape classes
   */
  getShapeType() {
    return this.shapeType
  }


  /**
   * Get the name for the glass item
   */
  getItemName() {
    let itemMark = (this.itemData.mark || '').trim()

    return _.compact([itemMark, this.itemData.description, (this.itemData.stockPriceGroup || {}).name]).join(' - ')
  }


  /**
   * Check if the shape is a stock-linked shape
   */
  isStockLinked() {
    if(!this.itemData.stockPriceGroup)
      return false
    return !!this.itemData.stockPriceGroup.isStockLinked
  }


  /**
   * Check if the shape is from a cancelled order item
   */
  isCancelled() {
    return !!this.itemData.isCancelled
  }


  /**
   * Check if the shape is from an NCR locked order item
   */
  isNCRLocked() {
    return !!this.itemData.isNCRLocked
  }


  /**
   * Check if the order item linked to the shape has been invoiced has all
   */
  isInvoiced() {
    return !!this.itemData.isInvoiced || (this.itemData.invoicedQuantity || 0) > 0
  }


  /**
   * Check if the order item linked to the shape has been despatched at all
   */
  isDespatched() {
    return !!this.itemData.isDespatched || (this.itemData.despatchedQuantity || 0) > 0
  }


  /**
   * Get the glass type of the shape
   * Based on the stock details set
   */
  getGlassType() {
    return this.itemData.stock.glassType
  }


  /**
   * Get the isStockUsingFaces flag set in the stock
   * Based on the stock details set
   */
  isStockUsingFaces() {
    return this.itemData.stock.isStockUsingFaces
  }


  /**
   * Get the isStockUsingGrain flag set in the stock
   * Based on the stock details set
   */
  isStockUsingGrain() {
    return this.itemData.stock.isStockUsingGrain
  }


  /**
   * Get the material type set in the stock
   * Based on the stock details set
   */
  getStockMaterialType() {
    return this.itemData.stock.stockMaterialType
  }


  /**
   * Check if the shape item is editable
   * Some items (eg, cancelled items) are not editable
   */
  isItemEditable() {
    if(this.isCancelled() || this.isNCRLocked() || this.isInvoiced() || this.isDespatched())
      return false
    return true
  }


  /**
   * Check if the shape item is part of an assembly group
   */
  isPartOfAssemblyGroup() {
    if(this.parentObject && this.parentObject instanceof AssemblyGroup)
      return true
    return false
  }


  /**
   * Check if the segment list shape is editable
   * This is shape, not segment list settings (eg, edgework is available for editing)
   * Some shapes (eg, circles/ovals) should not have the shape editable
   */
  isSegmentListShapeEditable() {
    return this.segmentListEditable
  }


  /**
   * Generate the THREE.Shape object based on the hole segment list
   */
  generateShape() {
    return MathsUtils.generateShapeFromSegmentList(this.segmentList, { isHole: false })
  }


  /**
   * Start editing the segment list
   * If editing, will not run recalculations on the segment list via segmentListUpdated
   * Used if making numerous edits to the segment list in one block
   */
  _startEditingSegmentList() {
    this._editingSegmentList = true
  }


  /**
   * Finish editing the segment list
   */
  _finishEditingSegmentList() {
    this._editingSegmentList = false
    this.segmentListUpdated()
  }


  /**
   * Segment list updated
   * Called whenever the shape segment list is modified
   * If the segment list is currently locked for editing, will not do anything
   */
  segmentListUpdated() {
    if(this._editingSegmentList)
      return

    // Recalculate the edgework availability flags for segments
    var hullPoints = EditorHullUtils.generateSegmentListConvexHull(this.segmentList)
    if(MathsUtils.is2DPolygonClosed(hullPoints))
      hullPoints.pop()
    if(MathsUtils.get2DPolygonOrientation(hullPoints) > 0)
      hullPoints.reverse()

    this.segmentList.forEach(segment => {
      segment.isEdgeworkAvailable = false
      let hullTestThreshold = 0.1
      var isOnHull = false

      if(segment.type == GlassDesignerTypes.SegmentTypes.line) {

        // If the midpoint of the segment is on or near a part of the convex hull, it is available for edgework
        let segMidPoint = segment.getMidpoint()
        for(let j=0; j<hullPoints.length && !isOnHull; j++) {
          let hullPnt1 = hullPoints[j]
          let hullPnt2 = hullPoints[(j+1) % hullPoints.length]

          isOnHull = MathsUtils.point2DOnLineSegment(hullPnt1, hullPnt2, segMidPoint, hullTestThreshold) || isOnHull
        }
      }

      if(segment.type == GlassDesignerTypes.SegmentTypes.arc) {
        // Take 3 points of the arc (25%, 50%, 75%)
        // If any of these points are on or near a part of the convex hull, it is available for edgework
        let segPointA = segment.getPoint(0.25)
        let segPointB = segment.getPoint(0.50)
        let segPointC = segment.getPoint(0.75)
        for(let j=0; j<hullPoints.length && !isOnHull; j++) {
          let hullPnt1 = hullPoints[j]
          let hullPnt2 = hullPoints[(j+1) % hullPoints.length]

          isOnHull = MathsUtils.point2DOnLineSegment(hullPnt1, hullPnt2, segPointA, hullTestThreshold) || isOnHull
          isOnHull = MathsUtils.point2DOnLineSegment(hullPnt1, hullPnt2, segPointB, hullTestThreshold) || isOnHull
          isOnHull = MathsUtils.point2DOnLineSegment(hullPnt1, hullPnt2, segPointC, hullTestThreshold) || isOnHull
        }
      }

      if(isOnHull)
        segment.isEdgeworkAvailable = true
    })

    MathsUtils.segmentListUpdated(this.segmentList)

    // Recalculate shape groups
    this.shapeGroupsUpdated()

    // Recalculate center cutouts in case any use the segments updated as datum links
    this.internalItemsUpdated()
    this.updateSceneObject()

    // Reset review approvals as the shape has changed
    this.resetReviewApprovals()

    // Recalculate the icon positions
    // Find closest start start point to top-left
    var segBounds = this.calculateSegmentBounds()
    let startSegmentIndex = this.calculateClosestSegmentStartToPoint(segBounds.min)
    if(startSegmentIndex >= 0) {
      this.approvedIconPosition.copy(this.segmentList[startSegmentIndex].startPosition)
      this.warningIconPosition.copy(this.segmentList[startSegmentIndex].startPosition)
    } else {
      this.approvedIconPosition.copy(segBounds.min)
      this.warningIconPosition.copy(segBounds.min)
    }

    // Notify parent
    if(this.getParentObject()) {
      this.getParentObject().childObjectUpdated(this)
    }    
  }


  /**
   * Get the list of child holes / center cutouts
   */
  getCenterCutoutList() {
    return this.children.filter(childObj => childObj instanceof GlassBaseHole)
  }


  /**
   * Add an internal hole to the shape (hole being a center cutout)
   * Does not modify the shape outline
   * Forces a scene object redraw
   */
  addCenterCutout(hole) {
    // Run checks if the hole is intersecting / outside the shape
    this._checkHoleIsValid(hole)

    this.addChildObject(hole)
    this.internalItemsUpdated()    
  }


  /**
   * Move a center cutout to a new position in the shape
   */
  moveCenterCutout(hole, position) {
    EditorDatumUtils.calculateClosestDatumLinks(this, hole.datumLinks, position, { ignoreAllHoles: false, ignoreHoleId: hole.id })
    hole.rebuildHoleShape()

    this._checkHoleIsValid(hole)

    this.internalItemsUpdated()
  }


  /**
   * Remove an internal hole to the shape (hole being a center cutout)
   * Does not modify the shape outline
   * Forces a scene object redraw
   */
  removeCenterCutout(hole) {
    this.removeChildObject(hole)
    hole.dispose()
    
    this.internalItemsUpdated()
  }


  /**
   * Combine a list of selected center cutouts into 1 cutout by unioning the shapes together into 1 shape
   * Converts the first center cutout to a custom shape & then applies clipping operations and removes the other cutouts
   * Throws an error if the cutouts do not all intersect
   */
  combineCenterCutouts(cutoutList) {
    if(cutoutList.length <= 1)
      return

    let baseCutout = cutoutList[0]
    cutoutList.splice(0, 1)

    // Convert the first cutout to custom hole & replace the cutout
    // The remaining cutouts will be removed & combined into the 1st cutout
    if(!(baseCutout instanceof CustomHole)) {
      let newHole = new CustomHole(this.editor, {
        isOpenShape: false,
        isCornerShape: false,
        segmentList: baseCutout.segmentList,
        isCutoutPolished: baseCutout.isCutoutPolished
      })

      // Copy datum links across
      newHole.setAnchorInfo(baseCutout.measureToHorizontal, baseCutout.measureToVertical)
      newHole.setDatumLinks(baseCutout.datumLinks)

      this.addChildObject(newHole)
      this.removeChildObject(baseCutout)
      baseCutout.dispose()

      baseCutout = newHole
    }

    // Go through the rest of the cutouts
    // Finds each cutout that intersects first. So if 3 cutouts are intersecting via 3rd cutout, uses 3rd that first
    let baseCutoutSegmentList = baseCutout.segmentList

    let safety = cutoutList.length * cutoutList.length
    while(cutoutList.length > 0 && --safety >= 0) {
      for(let i=0; i<cutoutList.length; i++) {
        let otherCutout = cutoutList[i]

        // Ignore if not intersecting
        if(baseCutoutSegmentList.find(seg => { return otherCutout.checkGlassSegmentIntersection(seg) }) == null)
          continue

        // Run clipping
        // Run as additive, as the holes are built up as polygons
        let clipper = new EditorClipper()
        let clipResult = clipper.performSegmentListClippingOperation(baseCutoutSegmentList, otherCutout.segmentList, true)
        if(clipResult.segmentList.length == 0)
          throw new EditorUserError('Could not combine the cutouts')

        baseCutoutSegmentList = clipResult.segmentList
        cutoutList.splice(i, 1)

        // Remove the other cutout from the shape
        this.removeChildObject(otherCutout)
        otherCutout.dispose()
      }
    }

    baseCutout.updateSourceSegmentList(baseCutoutSegmentList)

    // Recalculate the datum links for the new cutout object
    // Force new object to be measured from center
    let updatedTotalBounds = MathsUtils.calculateGlassSegmentListBounds(baseCutoutSegmentList)
    EditorDatumUtils.calculateClosestDatumLinks(this, baseCutout.datumLinks, updatedTotalBounds.getCenter(new THREE.Vector2()), { ignoreAllHoles: true })
    baseCutout.setAnchorInfo(GlassDesignerTypes.HoleMeasurePointTypes.center, GlassDesignerTypes.HoleMeasurePointTypes.middle)

    this.internalItemsUpdated()
  }


  /**
   * Run updates on the shape after hole / center cutouts have been modified
   * Recalculates all datums & rebuilds the shape
   */
  internalItemsUpdated() {
    // Rebuild all holes & panel items, will recalculate all datum links
    this.getCenterCutoutList().forEach(hole => {
      hole.rebuildHoleShape()
      hole.updateDatumLinkWorldPoint({ validateDatums: true })

      // If the hole has invalid datum links (eg, pointing to an object that is now removed) then recalculate the datum
      if(hole.hasInvalidDatumLink()) {

        EditorDatumUtils.calculateClosestDatumLinks(this, hole.datumLinks, hole.worldPoint, { ignoreAllHoles: true })
        hole.rebuildHoleShape()
      }
    })
    this.getPanelItemList().forEach(item => {
      item.updateSceneObject()
      item.updateDatumLinkWorldPoint({ validateDatums: true })

      // If the panel item has invalid datum links (eg, pointing to an object that is now removed) then recalculate the datum
      if(item.hasInvalidDatumLink()) {

        EditorDatumUtils.calculateClosestDatumLinks(this, item.datumLinks, item.worldPoint)
        item.updateSceneObject()
      }
    })
    this.getSplitLineList().forEach(splitLine => {
      splitLine.rebuildSplitLine()
    })

    // TODO: Run production warning checks
    // Don't throw exceptions, just display error icons on cutouts
   
    // Reset review approvals as the shape has changed
    this.resetReviewApprovals()

    this.updateSceneObject()
  }


  /**
   * Run checks on a center cutout hole in the shape & ensure it is in a valid position
   * Throws an EditorUserError if the hole in the shape is invalid
   * Returns true if valid
   */
  _checkHoleIsValid(hole) {
    let holeBounds = hole.calculateSegmentBounds()
    let holeCenter = new THREE.Vector2()
    holeBounds.getCenter(holeCenter)

    hole.segmentList.forEach(holeSegment => {

      // If any segment intersects with any other segment of the shape, throw an error
      if(this.checkGlassSegmentIntersection(holeSegment) != null)
        throw new EditorUserError('Center cutout intersects with glass panel edge')
    })

    return true
  }


  /**
   * Checks if a Box2 object fits in the shape at a position without intersection with the shape edge or holes
   * Returns a boolean, set to true if the Box2 fits in at the position
   */
  calculateBoxInShapeValid(box) {
    if(!this.isPointInsideShape(box.getCenter(new THREE.Vector2())))
      return false

    // Check segment intersection
    for(var shapeSegment of this.segmentList) {
      if(shapeSegment.calculateBox2Intersection(box).length > 0)
        return false
    }

    // Check hole intersection
    // Just does simple box-box test
    for(var existingHole of this.getCenterCutoutList()) {
      if(existingHole.calculateSegmentBounds().intersectsBox(box))
        return false
    }

    return true
  }


  /**
   * Finds a free position in the shape that can fit in a box area
   * Starting at a position in the shape and expands outwards for tests
   * Returns a Vector2 object with the valid position
   * Returns null if no valid positions were found
   */
  findValidEmptyRegionInShape(testPosition, regionBounds) {
    let shapeBounds = this.calculateSegmentBounds()
    let shapeWidth = shapeBounds.max.x - shapeBounds.min.x
    let shapeHeight = shapeBounds.max.y - shapeBounds.min.y

    var checkBox = new THREE.Box2()
    var checkPos = new THREE.Vector2()
    var regionSize = new THREE.Vector2(regionBounds.max.x - regionBounds.min.x, regionBounds.max.y - regionBounds.min.y)

    var testList = [
      { x: 1, y: 1 },
      { x: -1, y: 1 },
      { x: -1, y: -1 },
      { x: 1, y: -1 },
    ]

    let stepX = Math.floor((regionBounds.max.x - regionBounds.min.x) / 4)
    let stepY = Math.floor((regionBounds.max.y - regionBounds.min.y) / 4)
    for(var offsetY=0; offsetY<shapeHeight; offsetY += stepY) {
      for(var offsetX=0; offsetX<shapeWidth; offsetX += stepX) {
        for(var i=0; i<testList.length; i++) {

          checkPos.set(testPosition.x + offsetX * testList[i].x, testPosition.y + offsetY * testList[i].y)
          checkBox.setFromCenterAndSize(checkPos, regionSize)
          if(this.calculateBoxInShapeValid(checkBox))
            return checkPos
        }
      }
    }

    // No valid position found
    return null
  }


  /**
   * Get a segment given an index
   * Wraps index around if the index is out of bounds
   */
  getSegmentForIndex(index) {
    if(index < 0)
      index += this.segmentList.length
    index = index % this.segmentList.length

    return this.segmentList[index]
  }


  /**
   * Get a segment given an id
   * Returns null if no segment found
   */
  getSegmentById(id) {
    return this.segmentList.find(segment => segment.id == id)
  }


  /**
   * Find a segment index given a segment id
   * Returns -1 if no segment index found
   */
  findSegmentIndexById(id) {
    return this.segmentList.findIndex(segment => segment.id == id)
  }


  /**
   * Get a hole / center cutout given an id
   * Returns null if no hole found
   */
  getHoleById(id) {
    return this.getCenterCutoutList().find(hole => hole.id == id)
  }


  /**
   * Get the list of child datum lines
   */
  getDatumLineList() {
    return this.children.filter(childObj => childObj instanceof GlassDatumLine)
  }


  /**
   * Get a datum line object given an id
   * Returns null if no datum line found
   */
  getDatumLineById(id) {
    return this.getDatumLineList().find(datumLine => datumLine.id == id)
  }


  /**
   * Add a datum line to the shape
   */
  addDatumLine(datumLine) {
    this.addChildObject(datumLine)
    this.resetReviewApprovals()
  }


  /**
   * Remove a datum line from the shape
   */
  removeDatumLine(datumLine) {
    this.removeChildObject(datumLine)
    datumLine.dispose()

    this.updateSceneObject()
    this.resetReviewApprovals()
  }


  /**
   * Get the list of child split lines
   * Split lines are sorted by split index
   */
  getSplitLineList() {
    var splitLineList = this.children.filter(childObj => childObj instanceof GlassSplitLine)
    splitLineList.sort((slA, slB) => slA.splitIndex - slB.splitIndex)

    return splitLineList
  }


  /**
   * Get a split line object given an id
   * Returns null if no split line found
   */
  getSplitLineById(id) {
    return this.getSplitLineList().find(splitLine => splitLine.id == id)
  }


  /**
   * Add a split line to the shape
   */
  addSplitLine(splitLine) {
    let splitLineList = this.getSplitLineList()

    // Get next split line index
    var lastSplitIndex = splitLineList.reduce((curValue, split) => {
      if(split.splitIndex > curValue)
        return split.splitIndex
      return curValue
    }, -1)

    splitLine.splitIndex = lastSplitIndex + 1
    splitLine.updateSceneObject()

    this.addChildObject(splitLine)
    this.resetReviewApprovals()
  }


  /**
   * Remove a split line from the shape
   */
  removeSplitLine(splitLine) {
    this.removeChildObject(splitLine)
    splitLine.dispose()

    // Recalculate split lines & how they extend out
    this._recalculateSplitLines()

    this.updateSceneObject()
    this.resetReviewApprovals()
  }


  /**
   * Recalculate the split lines in the glass when some get removed
   * Extends out split lines if they no longer have preceeding split lines blocking the straight line split
   */
  _recalculateSplitLines() {
    // Go through list & recalculate extents & update indexes
    // Note: getSplitLineList() Gets the list in correct order, which is vital for this function
    var splitLineList = this.getSplitLineList()
    splitLineList.forEach((splitLine, index) => {
      splitLine.splitIndex = index
    })

    splitLineList.forEach(splitLine => {
      this._recalculateSplitLineExtents(splitLine, splitLineList)

      splitLine.updateSceneObject()
    })
  }


  /**
   * Recalculate the extents of a split line in the shape
   * Done after split line(s) have been removed and the existing split lines can extend out
   */
  _recalculateSplitLineExtents(splitLine, splitLineList) {
    let shapeSize = new THREE.Vector2()
    let shapeBounds = this.calculateSegmentBounds()
    shapeBounds.getSize(shapeSize)

    let rayTraceMaxLength = Math.max(shapeSize.x, shapeSize.y) * 2

    var lineStart = splitLine.startPosition.clone()
    var lineEnd = splitLine.endPosition.clone()
    var lineDir = lineEnd.clone().sub(lineStart)
    var lineMid = new THREE.Vector2((lineStart.x + lineEnd.x) / 2.0, (lineStart.y + lineEnd.y) / 2.0)
    lineDir.normalize()
    
    // Cast ray way out of shape bounds
    lineStart.addScaledVector(lineDir, -rayTraceMaxLength)
    lineEnd.addScaledVector(lineDir, rayTraceMaxLength)

    // Ignore all split lines including & after this
    var ignoreSplitLineIdList = splitLineList.filter(otherSplit => otherSplit.splitIndex >= splitLine.splitIndex).map(split => split.id)

    // Find the inner intersections
    let intersections = this.calculateIntersectionWithLine(lineStart, lineEnd, { ignoreDatumLines: true, ignoreSplitLineIdList: ignoreSplitLineIdList })
    if(intersections.length >= 2) {
      var i = 0
      var startIndex = 0
      var endIndex = intersections.length-1
      for(i=0; i<intersections.length; i++) {
        if(MathsUtils.calculateDistanceToPlane2D(lineDir, lineMid, intersections[i]) < 0) {
          startIndex = i
        }
      }
      for(i=intersections.length-1; i>=0; i--) {
        if(MathsUtils.calculateDistanceToPlane2D(lineDir, lineMid, intersections[i]) > 0) {
          endIndex = i
        }
      }

      // Reset extents
      if(startIndex != endIndex) {
        splitLine.startPosition.set(intersections[startIndex].x, intersections[startIndex].y)
        splitLine.endPosition.set(intersections[endIndex].x, intersections[endIndex].y)
        splitLine.parentOffset.copy(splitLine.startPosition.clone().sub(shapeBounds.min))
      }
    }
  }


  /**
   * Calculate the rectangles created by the split lines
   * Returns an array of Box2 objects
   * Returns an empty array if there are no split lines
   */
  calculateSplittingRectangles() {
    let shapeBounds = this.calculateSegmentBounds()
    let shapeWidth = shapeBounds.max.x - shapeBounds.min.x
    let shapeHeight = shapeBounds.max.y - shapeBounds.min.y

    let splitLines = this.getSplitLineList()
    if(splitLines.length == 0)
      return []

    var rectangles = [shapeBounds.clone()]

    splitLines.forEach(splitLine => {
      let splitMid = splitLine.getMidpoint()
      let splitHorz = splitLine.isHorizontal()

      // Split the 1st rectangle this split line exists inside of
      let rectIndex = rectangles.findIndex(rect => rect.containsPoint(splitMid))
      if(rectIndex != -1) {
        var existingRect = rectangles[rectIndex]
        var newRect = new THREE.Box2()

        // Split the rectangle
        if(splitHorz) {
          newRect.set(new THREE.Vector2(existingRect.min.x, splitMid.y), existingRect.max.clone())
          existingRect.max.y = splitMid.y
        } else {
          newRect.set(new THREE.Vector2(splitMid.x, existingRect.min.y), existingRect.max.clone())
          existingRect.max.x = splitMid.x
        }
        rectangles.splice(rectIndex+1, 0, newRect)

      } else {
        console.warn(`No rectangles found containing split line at ${splitMid.x},${splitMid.y}`)
      }
    })

    // If only 1 rectangle (the original shape) then ignore it & return nothing
    if(rectangles.length == 1)
      return []

    return rectangles
  }


  /**
   * Get the list of child panel items
   */
  getPanelItemList() {
    return this.children.filter(childObj => childObj instanceof GlassPanelItem)
  }


  /**
   * Add a panel item to the shape
   */
  addPanelItem(panelItem) {
    this.addChildObject(panelItem)

    this.internalItemsUpdated()
  }


  /**
   * Move a panel item to a new position in the shape
   */
   movePanelItem(panelItem, position) {
    EditorDatumUtils.calculateClosestDatumLinks(this, panelItem.datumLinks, position, { ignoreAllHoles: true })
    panelItem.updateDatumLinkWorldPoint()

    this.internalItemsUpdated()
  }


  /**
   * Remove a panel item from the shape
   */
  removePanelItem(panelItem) {

    // If this is a special stamp panel item, clear stamped flag from the shape
    if(panelItem.isOrderStampItem) {
      this.isStamped = false
    }

    this.removeChildObject(panelItem)
    panelItem.dispose()

    this.internalItemsUpdated()
  }


  /**
   * Calculate the list of edges in the shape that are available for edgework
   * 
   * Returns an object containing:
   *    segmentList - List of segments with edgework details + allowances + feed info
   *    edgeworkList - List of the chargable edgework service required for the cutting lines / arcs pre-cutouts
   */
  calculateEdgeworkEdgeList() {
    var cuttingLineList = this.calculateCuttingLineList()
    var outputData = {
      segmentList: [],
      edgeworkChargeList: []
    }

    var cncCutoutList = this.calculateCNCCutoutList()

    let glassPanelShapeTypeId = this.calculateShapePriceTypeId()
    let thickness = this.getThickness()

    // No edgework available for stock linked
    if(this.isStockLinked())
      return outputData

    this.segmentList.map((segment, segmentIndex) => {
      if(!segment.isEdgeworkAvailable || segment.orderEditorEdgeworkConfigId == 0)
        return

      // Make sure the edgework config is available
      let edgeworkConfig = this.editor.getEditorConfigurationData().edgeworkList.find(edgeworkConfig => edgeworkConfig.id == segment.orderEditorEdgeworkConfigId)
      if(edgeworkConfig == null)
        return

      // Filter through stock rules
      let edgeworkStock = edgeworkConfig.orderEditorEdgeworkStocks.find(edgeworkStock => {
        if(edgeworkConfig.settingType != OrderEditorEdgeworkSettingTypes.none) {
          if(segment.edgeworkSettingValue < edgeworkStock.settingValueFilterMin || segment.edgeworkSettingValue > edgeworkStock.settingValueFilterMax)
            return false
        }
        if(edgeworkStock.orderEditorShapePriceTypeId) {
          if(edgeworkStock.orderEditorShapePriceTypeId != glassPanelShapeTypeId)
            return false
        }
        return true
      })
      if(!edgeworkStock)
        return

      // Calculate the edgework allowance amount required for the segment
      let edgeworkAllowanceAmount = 0
      let edgeworkAllowanceAmountArc = 0

      let edgeworkAllowanceConfig = edgeworkConfig.orderEditorEdgeworkAllowances.find(edgeworkAllowance => {
        if(thickness < edgeworkAllowance.servicePriceRange.minRange || thickness >= edgeworkAllowance.servicePriceRange.maxRange)
          return false

        return (edgeworkAllowance.servicePriceRange.stockPriceGroups.find(stockPriceGroup => stockPriceGroup.id == this.itemData.stockPriceGroupId) != null)
      })
      if(edgeworkAllowanceConfig) {
        edgeworkAllowanceAmount = edgeworkAllowanceConfig.allowanceAmount
        edgeworkAllowanceAmountArc = edgeworkAllowanceConfig.allowanceAmountArc
      }

      // Check if the glass stock has overrides for the edgework allowance
      if(edgeworkConfig.orderEditorEdgeworkStockAllowances) {
        let edgeworkStockOverride = edgeworkConfig.orderEditorEdgeworkStockAllowances.find(edgeworkOverride => edgeworkOverride.stockId == this.itemData.stockId)
        if(edgeworkStockOverride && edgeworkStockOverride.allowanceAmount !== null)
          edgeworkAllowanceAmount = edgeworkStockOverride.allowanceAmount
        if(edgeworkStockOverride && edgeworkStockOverride.allowanceAmountArc !== null)
          edgeworkAllowanceAmountArc = edgeworkStockOverride.allowanceAmountArc
      }

      var edgeworkFeedHeight = 0
      var edgeworkLength = segment.getLength()
      var cuttingLine = null

      // Find the cutting line the segment rests on
      // Use the cutting line for edgework length
      if(segment.type == GlassDesignerTypes.SegmentTypes.line) {
        cuttingLine = cuttingLineList.filter(cutLine => {

          // Segment must match cutting line
          if(Math.abs(cutLine.segDir.dot(segment.getDirection())) > 0.95) {

            let cutDist = MathsUtils.calculateDistanceToPlane2D(cutLine.planeNormal, cutLine.segMidPoint, segment.getMidpoint())
            if(Math.abs(cutDist) < 1.0)
              return true
          }

          return false
        })

        let closestCuttingLineIndex = 0

        // Take the first cutting line that matches
        // If there are multiple cutting lines, we should take the one that is closest to the segment midpoint
        if(cuttingLine.length > 1) {

          let closestDist = Infinity
          for(let i=0; i<cuttingLine.length; i++) {
            let cutLine = cuttingLine[i]
            let dist = cutLine.segMidPoint.distanceTo(segment.getMidpoint())
            if(dist < closestDist) {
              closestDist = dist
              closestCuttingLineIndex = i
            }
          }
        }
        cuttingLine = cuttingLine[closestCuttingLineIndex]
        if(cuttingLine == null)
          return

        // Note: We base the edgework length on the cutting line as that is what is run through edgework
        // As CNC cutouts are applied after edgework
        // If the next or previous segments are arcs, that are not part of CNC cutouts, we stop the edgework line there
        var edgeworkStart = cuttingLine.startPosition.clone()
        var edgeworkEnd = cuttingLine.endPosition.clone()

        let prevSeg = this.getSegmentForIndex(segmentIndex-1)
        let nextSeg = this.getSegmentForIndex(segmentIndex+1)

        if(prevSeg.type == GlassDesignerTypes.SegmentTypes.arc) {
          // Make sure the previous arc isn't part of a CNC cutout
          if(!this._findSegmentInCNCCutoutList(cncCutoutList, prevSeg))
            edgeworkStart = segment.startPosition.clone()
        }
        if(nextSeg.type == GlassDesignerTypes.SegmentTypes.arc) {
          // Make sure the next arc isn't part of a CNC cutout
          if(!this._findSegmentInCNCCutoutList(cncCutoutList, nextSeg))
            edgeworkEnd = segment.endPosition.clone()
        }

        // Calculate edgework flag
        // Used for descriptions
        let edgeworkEdgeFlag = this._calculateEdgeworkFlag(edgeworkStart, edgeworkEnd)

        edgeworkLength = edgeworkStart.distanceTo(edgeworkEnd)
        var cuttingLineBounds = MathsUtils.calculate2DPointListBounds([edgeworkStart, edgeworkEnd])

        // Add to charge list if the cutting line has not already been added
        let existingCharge = outputData.edgeworkChargeList.find(chargeInfo => {
          if(chargeInfo.segmentType != GlassDesignerTypes.SegmentTypes.line)
            return false
          return chargeInfo.cutLineId == cuttingLine.id
        })
        if(!existingCharge) {
          outputData.edgeworkChargeList.push({
            orderEditorEdgeworkConfigId: segment.orderEditorEdgeworkConfigId,
            edgeworkStockId: edgeworkStock.stockId,
            segmentType: segment.type,
            cutLineId: cuttingLine.id,
            length: edgeworkLength,
            edgeworkEdgeFlag: edgeworkEdgeFlag,
            name: edgeworkConfig.name,
            settingType: edgeworkConfig.settingType,
            highlightColour: edgeworkConfig ? edgeworkConfig.highlightColour : '#000000',
            edgeworkValue: segment.edgeworkSettingValue,
            cutLineBounds: cuttingLineBounds
          })
        }
      }

      // Note: Arcs that have edgework available & set config will automatically get added to the edgework list
      // In the case of circles or D ends
      if(segment.type == GlassDesignerTypes.SegmentTypes.arc) {
        // Calculate edgework flag
        // Used for descriptions
        let edgeworkEdgeFlag = this._calculateEdgeworkFlag(segment.startPosition, segment.endPosition)

        outputData.edgeworkChargeList.push({
          orderEditorEdgeworkConfigId: segment.orderEditorEdgeworkConfigId,
          edgeworkStockId: edgeworkStock.stockId,
          segmentType: segment.type,
          cutLineId: null,
          length: segment.getLength(),
          edgeworkEdgeFlag: edgeworkEdgeFlag,
          name: edgeworkConfig.name,
          settingType: edgeworkConfig.settingType,
          highlightColour: edgeworkConfig ? edgeworkConfig.highlightColour : '#000000',
          edgeworkValue: segment.edgeworkSettingValue,
          cutLineBounds: segment.calculateBounds()
        })
      }

      // Calculate the feed height required for the edgework in the machine
      var distRangeInfo = MathsUtils.calculateSegmentListDistanceRangeForDirection(this.segmentList, segment.getOutDirection().negate(), segment.getMidpoint())
      edgeworkFeedHeight = distRangeInfo.max

      outputData.segmentList.push({
        orderEditorEdgeworkConfigId: segment.orderEditorEdgeworkConfigId,
        edgeworkStockId: edgeworkStock.stockId,
        segmentType: segment.type,
        segmentId: segment.id,
        segStartPosition: segment.startPosition,
        segEndPosition: segment.endPosition,
        startPosition: cuttingLine ? cuttingLine.startPosition : null,
        endPosition: cuttingLine ? cuttingLine.endPosition : null,
        segLength: segment.getLength(),
        length: edgeworkLength,
        name: edgeworkConfig.name,
        settingType: edgeworkConfig.settingType,
        highlightColour: edgeworkConfig ? edgeworkConfig.highlightColour : '#000000',
        edgeworkValue: segment.edgeworkSettingValue,
        edgeworkAllowance: segment.type == GlassDesignerTypes.SegmentTypes.line ? edgeworkAllowanceAmount : edgeworkAllowanceAmountArc,
        cornerEdgeworkAllowance: edgeworkAllowanceAmountArc,
        edgeworkFeedHeight: edgeworkFeedHeight
      })
    })

    return outputData
  }


  /**
   * Apply the default edgework to the shape
   * Done after creation or editing of shape details via dialog (where stock/price group updates)
   */
  applyDefaultEdgework() {
    if(!this.defaultEdgeworkConfigId)
      return

    // Ignore for stock linked
    if(this.isStockLinked())
      return

    // Apply to all segments, even if edgework not available on the segment
    // Only applies if no edgework specified already
    // If the segment gets edited & becomes available, it'll automatically inherit the default edgework
    // OrderHelper will ensure only segments available for edgework get charged & assigned for edgework
    this.segmentList.forEach(segment => {
      if(!segment.orderEditorEdgeworkConfigId)
        segment.orderEditorEdgeworkConfigId = this.defaultEdgeworkConfigId
    })
  }


  /**
   * Update a segments edgework details
   */
  updateSegmentEdgework(segment, edgeworkConfigId, edgeworkSettingValue) {
    segment.orderEditorEdgeworkConfigId = edgeworkConfigId
    segment.edgeworkSettingValue = edgeworkSettingValue

    // Reset review approvals as the shape has changed
    this.resetReviewApprovals()
  }


  /**
   * Apply the default panel items to the shape
   * Done after creation or editing of shape details via dialog (where stock/price group updates)
   */
  applyDefaultPanelItems() {
    let curPanelItems = this.getPanelItemList()
    let contactId = this.editor.getOrder().contactId

    let defaultPanelItems = this.editor.getEditorConfigurationData().applyDefaultPanelItems || []
    defaultPanelItems.forEach(panelItem => {
      // If the shape already has this item, ignore this
      if(curPanelItems.find(curPanelItem => curPanelItem.orderEditorLibraryItemId == panelItem.id) != null)
        return

      // Check price group & customer filters
      let priceGroupFilter = panelItem.defaultPriceGroupFilter || {}
      let customerFilter = panelItem.defaultCustomerFilter || {}

      if(priceGroupFilter.type == FilterOptionTypes.include) {
        if(priceGroupFilter.valueList.find(id => id == this.itemData.stockPriceGroupId) == null)
          return
      }
      else if(priceGroupFilter.type == FilterOptionTypes.exclude) {
        if(priceGroupFilter.valueList.find(id => id == this.itemData.stockPriceGroupId) != null)
          return
      }
      if(customerFilter.type == FilterOptionTypes.include) {
        if(customerFilter.valueList.find(id => id == contactId) == null)
          return
      }
      else if(customerFilter.type == FilterOptionTypes.exclude) {
        if(customerFilter.valueList.find(id => id == contactId) != null)
          return
      }

      let newPanelItem = null
      try {
        // Place in the location required
        // Just based around bounds, does not calculate cutouts / segments of the panel
        let itemData = panelItem.itemData || {}

        newPanelItem = new GlassPanelItem(this.editor, {
          width: parseInt(itemData.width || 100),
          height: parseInt(itemData.height || 50),
          angle: parseInt(itemData.angle || 0),
          stockId: parseInt(panelItem.stockId || 0),
          orderEditorLibraryItemId: panelItem.id
        })
        
        let datumLinks = []
        let availablePosition = this.calculateDefaultPanelItemPosition(panelItem.defaultAlignHorz, panelItem.defaultAlignVert, newPanelItem.width, newPanelItem.height)
        EditorDatumUtils.calculateClosestDatumLinks(this, datumLinks, availablePosition, { ignoreAllHoles: true })

        newPanelItem.setDatumLinks(datumLinks)
        this.addPanelItem(newPanelItem)
      } catch(err) {
        console.error(err)

        if(newPanelItem) {
          newPanelItem.dispose()
        }
      }
    })
  }


  /**
   * Update the stamp panel item if required for the shape
   * Done after creation or editing of shape details via dialog (where stock/price group updates)
   */
  updateDefaultStampItem() {
    var stampPanelItem = this.findStampPanelItem()
    var orderStampConfig = this.editor.getEditorConfigurationData().orderStampConfig

    // Remove stamp item if it exists & shape is no longer stamped
    if(stampPanelItem && !this.isStamped) {
      this.removePanelItem(stampPanelItem)
      return
    }

    if(!stampPanelItem && this.isStamped) {
      try {
        // Place in the location required
        // Just based around bounds, does not calculate cutouts / segments of the panel
        stampPanelItem = new GlassPanelItem(this.editor, {
          width: parseInt(orderStampConfig.stampWidth || 100),
          height: parseInt(orderStampConfig.stampHeight || 50),
          stockId: parseInt(orderStampConfig.stampRequiredStockId || 0),
          isOrderStampItem: true
        })
        
        let datumLinks = []
        let availablePosition = this.calculateDefaultPanelItemPosition(orderStampConfig.stampAlignHorz, orderStampConfig.stampAlignVert, stampPanelItem.width, stampPanelItem.height)
        EditorDatumUtils.calculateClosestDatumLinks(this, datumLinks, availablePosition, { ignoreAllHoles: true })

        stampPanelItem.setDatumLinks(datumLinks)
        this.addPanelItem(stampPanelItem)
      } catch(err) {
        console.error(err)

        if(stampPanelItem) {
          stampPanelItem.dispose()
        }
      }
    }
  }


  /**
   * Find if the shape has a panel item flagged as the stamp
   * Returns the GlassPanelItem with isOrderStampItem set
   * Returns null if no stamp panel item found
   */
  findStampPanelItem() {
    return this.getPanelItemList().find(item => item.isOrderStampItem)
  }


  /**
   * Calculates the default panel item position 2D coordinate 
   * Used for panel items automatically added to the shape via applyDefaultPanelItems
   * Returns 2D position of the center of the panel item
   */
  calculateDefaultPanelItemPosition(alignHorz, alignVert, itemWidth, itemHeight) {
    let shapeBounds = this.calculateSegmentBounds()

    let availablePosition = new THREE.Vector2(0, 0)
    switch(alignHorz) {
      case AlignmentTypes.left:
        availablePosition.x = shapeBounds.min.x + itemWidth / 2
        break
      case AlignmentTypes.center:
        availablePosition.x = (shapeBounds.min.x + shapeBounds.max.x) / 2
        break
      case AlignmentTypes.right:
        availablePosition.x = shapeBounds.max.x - itemWidth / 2
        break
    }
    switch(alignVert) {
      case AlignmentTypes.top:
        availablePosition.y = shapeBounds.min.y + itemHeight / 2
        break
      case AlignmentTypes.middle:
        availablePosition.y = (shapeBounds.min.y + shapeBounds.max.y) / 2
        break
      case AlignmentTypes.bottom:
        availablePosition.y = shapeBounds.max.y - itemHeight / 2
        break
    }

    return availablePosition
  }


  /**
   * Calculate the list of CNC cutouts in the shape that require cutout services
   * 
   * Returns an array of objects with details for each cutout
   */
  calculateCNCCutoutList() {
    var cuttingLineList = this.calculateCuttingLineList()

    var hullPoints = EditorHullUtils.generateSegmentListConvexHull(this.segmentList)
    if(MathsUtils.is2DPolygonClosed(hullPoints))
      hullPoints.pop()
    if(MathsUtils.get2DPolygonOrientation(hullPoints) > 0)
      hullPoints.reverse()

    var cutoutList = EditorCutoutUtils.generateCutoutListInConvexHull(this.segmentList, hullPoints, cuttingLineList)

    cutoutList = cutoutList.filter(cutout => {
      return cutout.displayPointList && cutout.displayPointList.length > 2
    })

    var cncCutoutList = []

    // Corner / Edge cutouts
    cutoutList.forEach(hullCutout => {
      if(!hullCutout.displayPointList || hullCutout.displayPointList.length <= 2)
        return

      let bounds = MathsUtils.calculate2DPointListBounds(hullCutout.displayPointList)
      let pathLength = MathsUtils.calculate2DPointListLength(hullCutout.displayPointList)

      var shapeCutout = new ShapeCutout({
        type: hullCutout.isCorner ? OrderEditorCutoutTypes.cornerCutOut : OrderEditorCutoutTypes.edgeCutOut,
        geometryShapeType: OrderEditorGeometryShapeTypes.polygon,
        points: hullCutout.points,
        displayPointList: hullCutout.displayPointList.map(p => p.clone()),
        pathSegmentList: hullCutout.pathSegmentList,
        segmentIdList: hullCutout.segmentIdList,
        pathLength: pathLength,
        bounds: bounds,
        cutoutWidth: bounds.max.x - bounds.min.x,
        cutoutHeight: bounds.max.y - bounds.min.y,
        maxDimension: 0,
        isCutoutPolished: hullCutout.isCutoutPolished,
        isCountersunk: false,
        countersunkRadius: 0
      })

      // Calculate axis aligned max dimension
      shapeCutout.maxDimension = Math.max(bounds.max.x - bounds.min.x, bounds.max.y - bounds.min.y)

      cncCutoutList.push(shapeCutout)
    })


    // Go through all the center cutout holes
    this.getCenterCutoutList().forEach(centerCutout => {

      let holeShape = centerCutout.generateShape()
      let holePoints = holeShape.getPoints(Config.Settings.arcSubdivisions)
      if(!holePoints || holePoints.length <= 2)
        return

      var pathSegmentList = centerCutout.segmentList.map(segment => segment.clone())

      let bounds = MathsUtils.calculate2DPointListBounds(holePoints)
      let pathLength = MathsUtils.calculate2DPointListLength(holePoints)
      var radius = 0

      var geometryShapeType = OrderEditorGeometryShapeTypes.rectangle
      if(centerCutout.holeType == GlassDesignerTypes.LibraryItemShapeTypes.circle) {
        geometryShapeType = OrderEditorGeometryShapeTypes.circle
        radius = centerCutout.radius
      }

      var shapeCutout = new ShapeCutout({
        type: OrderEditorCutoutTypes.centerCutOut,
        geometryShapeType: geometryShapeType,
        points: holePoints,
        displayPointList: holePoints,
        pathSegmentList: pathSegmentList,
        pathLength: pathLength,
        bounds: bounds,
        radius: radius,
        cutoutWidth: bounds.max.x - bounds.min.x,
        cutoutHeight: bounds.max.y - bounds.min.y,
        maxDimension: 0,
        isCutoutPolished: centerCutout.isCutoutPolished,
        isCountersunk: centerCutout.isCountersunk,
        countersunkRadius: centerCutout.countersunkRadius,
        cutoutObjectId: centerCutout.id
      })

      // Calculate axis aligned max dimension
      shapeCutout.maxDimension = Math.max(bounds.max.x - bounds.min.x, bounds.max.y - bounds.min.y)

      cncCutoutList.push(shapeCutout)
    })
    
    return cncCutoutList
  }


  /**
   * Calculate the list of radius cuts in the glass panel
   * Ignores radius corners associated with cutouts
   */
  calculateRadiusCutList() {
    var radiusCutList = []
    var cncCutoutList = this.calculateCNCCutoutList()

    this.segmentList.forEach(segment => {

      if(segment.type != GlassDesignerTypes.SegmentTypes.line)
        return
      if(segment.startCornerRadius <= 0.0)
        return

      // If the segment is in the cutout list, ignore it
      let linkedCutout = cncCutoutList.find(cutout => cutout.segmentIdList.indexOf(segment.id) != -1)
      if(linkedCutout != null)
        return

      var segDir = segment.getDirection()
      var segOutDir = segment.getOutDirection()

      var bounds = new THREE.Box2()
      bounds.setFromPoints([
        segment.startPosition,
        segment.startPosition.clone().addScaledVector(segOutDir, -segment.startCornerRadius),
        segment.startPosition.clone().addScaledVector(segDir, segment.startCornerRadius)
      ])

      radiusCutList.push(new RadiusCut({
        radius: segment.startCornerRadius,
        isPolished: segment.isStartCornerPolished,
        segmentId: segment.id,
        bounds: bounds
      }))
    })

    return radiusCutList
  }


  /**
   * Calculate the runouts in the panel
   * Currently just used for order editor review purposes
   */
  calculateRunoutList() {
    var runOutList = []
    var shapeBounds = this.calculateSegmentBounds();
    var cncCutoutList = this.calculateCNCCutoutList()
    
    var cuttingLineList = this.calculateCuttingLineList()
    cuttingLineList = cuttingLineList.filter(cutLine => !cutLine.isOnBounds)

    var minRunoutAngle = 0.01;
    var maxRunoutAngle = 5.0;
    var minRunoutSegmentLength = 10;

    var shapeCenter = shapeBounds.getCenter(new THREE.Vector2())

    this.segmentList.forEach((segment, segmentIndex) => {
      if(segment.type != GlassDesignerTypes.SegmentTypes.line)
        return

      // If the segment is in the cutout list, ignore it
      let linkedCutout = cncCutoutList.find(cutout => cutout.segmentIdList.indexOf(segment.id) != -1)
      if(linkedCutout != null)
        return

      let prevSeg = this.getSegmentForIndex(segmentIndex-1)

      if(prevSeg.type != GlassDesignerTypes.SegmentTypes.line)
        return

      // Ignore tiny segments
      if(segment.getLength() < minRunoutSegmentLength)
        return

      // Assume lines will be at 90degree angles. Anything within +/- 5degrees of the 90 corner would be potentially runout
      // Anything large would be part of a bigger shape polygon
      var segDirection = segment.getDirection()
      var segOutDirection = segment.getOutDirection()
      var prevSegAngle = MathsUtils.wrapAngleDegrees(MathsUtils.radiansToDegrees(prevSeg.getAngle()))
      var curSegAngle = MathsUtils.wrapAngleDegrees(MathsUtils.radiansToDegrees(segment.getAngle()))

      var prevAngleDiff = MathsUtils.calculateShortestAngleDistanceDegrees(prevSegAngle, curSegAngle)

      // Calculate if the segment has a slight angle versus the previous line
      var runoutAngle = Math.abs(prevAngleDiff - 90)
      if(runoutAngle >= minRunoutAngle && runoutAngle < maxRunoutAngle) {
        // If this segment is axis aligned, then the previous segment must be the one running out
        if(MathsUtils.isLineAxisAligned(segment.startPosition, segment.endPosition, 0.1))
          return

        // This segment must be matching a straight line cutout
        // Fixes issue where small segments around side cutouts where only being used for runouts instead of the entire edge (cutting line)
        var linkedStraightCut = cuttingLineList.find(cutLine => {
          if(Math.abs(cutLine.planeNormal.dot(segOutDirection)) < 1.0 - 0.001)
            return false
          if(Math.abs(MathsUtils.calculateDistanceToPlane2D(cutLine.planeNormal, cutLine.segMidPoint, segment.getMidpoint())) > 0.01)
            return false

          return true
        })
        if(!linkedStraightCut)
          return

        var runoutBounds = new THREE.Box2()
        runoutBounds.expandByPoint(linkedStraightCut.startPosition)
        runoutBounds.expandByPoint(linkedStraightCut.endPosition)

        // Figure out if the start or end of the cutting line is the part kicking out from the shape (not in)
        var runoutPoint = linkedStraightCut.startPosition
        var runoutAmount = 0
        var directionDescription = ''

        if(Math.abs(segDirection.x) > Math.abs(segDirection.y)) {
          if(Math.abs(linkedStraightCut.endPosition.y - shapeCenter.y) > Math.abs(linkedStraightCut.startPosition.y - shapeCenter.y))
            runoutPoint = linkedStraightCut.endPosition

          runoutAmount = Math.round(Math.abs(linkedStraightCut.endPosition.y - linkedStraightCut.startPosition.y))

          directionDescription = runoutPoint.y < shapeCenter.y ? 'Up' : 'Down'
        } else {
          if(Math.abs(linkedStraightCut.endPosition.x - shapeCenter.x) > Math.abs(linkedStraightCut.startPosition.x - shapeCenter.x))
            runoutPoint = linkedStraightCut.endPosition

          runoutAmount = Math.round(Math.abs(linkedStraightCut.endPosition.x - linkedStraightCut.startPosition.x))

          directionDescription = runoutPoint.x < shapeCenter.x ? 'Left' : 'Right'
        }

        // Calculate the description of the runout (eg, top left) compared to the shape
        var alignment = MathsUtils.calculatePointInBoundsAlignmentType(shapeBounds, runoutPoint)
        var description = `${directionDescription} ${runoutAmount}mm at ${Utils.getLookupName(AlignmentTypeLookup, alignment.vertical)} ${Utils.getLookupName(AlignmentTypeLookup, alignment.horizontal)}`

        runOutList.push({
          amount: runoutAmount,
          description: description,
          segmentId: segment.id,
          bounds: runoutBounds
        })
      }
    })

    return runOutList
  }


  // Update the scene object
  updateSceneObject() {
    this.glassColour = this.editor.getTheme().panelColour
    var glassOpacity = 0.5

    // Set from stock
    if(this.itemData.stock) {
      if(this.itemData.stock.stockColour1)
        this.glassColour = this.itemData.stock.stockColour1

      if(this.itemData.stock.stockColourOpacity !== undefined)
        glassOpacity = parseInt(this.itemData.stock.stockColourOpacity) / 100.0
    }

    if(this.isCancelled() || this.isNCRLocked())
      this.glassColour = this.editor.getTheme().panelCancelledColour

    if((this.isInvoiced() || this.isDespatched()) && this.editor.isEditingAvailable()) {
      this.glassColour = this.editor.getTheme().panelLockedColour
      glassOpacity = 0.33
    }

    var shapeDivisions = Config.Settings.arcSubdivisions

    // TODO: Might need to store structures & dispose of them properly

    this.selectedSegmentObjects.forEach(lineObj => {
      lineObj.dispose()
    })
    this.polishedSegmentObjects.forEach(lineObj => {
      lineObj.dispose()
    })
    this.edgeworkFlagObjects.forEach(flagObj => {
      if(flagObj instanceof LineObject)
        flagObj.dispose()
    })
    this.selectedSegmentObjects = []
    this.polishedSegmentObjects = []
    this.clearSceneObject()

    // Build the shape path from the segment list
    var extrudeShape = this.generateShape()

    // Updated cached point list
    // Use less subdivisions for performance
    this.cachedPointList = extrudeShape.getPoints(10)

    // Generate the hole shapes
    // If any center cutouts intersect with each other, don't cut the holes in the shape
    // that creates wierd graphical artifacts. Just show the outline & wait for the cutouts to be resolved
    let centerCutoutList = this.getCenterCutoutList()
    let centerCutoutIntersections = []
    centerCutoutList.forEach(hole => {
      if(!hole.intersectsOtherHoles(centerCutoutList)) {
        let holeShape = hole.generateShape()
        extrudeShape.holes.push(holeShape)
      } else {
        centerCutoutIntersections.push(hole)
      }
    })

    var displayShape = this.editor.getViewLevel() == GlassDesignerTypes.EditorViewLevelTypes.normal ||
      this.editor.getViewLevel() == GlassDesignerTypes.EditorViewLevelTypes.view3D ||
      this.editor.getViewLevel() == GlassDesignerTypes.EditorViewLevelTypes.technical
    
    if(!this.shapeHighlightOnly && displayShape) {
      var shapeBounds = this.calculateSegmentBounds();

      // Grain texture
      var mapTexture = null
      var textureScale = new THREE.Vector2(shapeBounds.max.x - shapeBounds.min.x, shapeBounds.max.y - shapeBounds.min.y)

      switch(this.getStockMaterialType()) {
        // Linear Gradient
        case StockMaterialTypes.gradient:
          textureScale.set((shapeBounds.max.x - shapeBounds.min.x) * 1.5, (shapeBounds.max.y - shapeBounds.min.y) * 1.5)
          mapTexture = this.editor.createLinearGradientTexture(this.itemData.stock.stockColour1, this.itemData.stock.stockColour2, Math.PI / 4)
          break

        // Reeded Texture
        case StockMaterialTypes.reedTexture:
          if(this.isStockUsingGrain() && this.itemData.grainDirection == StockGrainDirectionTypes.horizontal) {
            mapTexture = this.editor.resources.panelGrainTextureHorizontal
            textureScale.set(shapeBounds.max.x - shapeBounds.min.x, 2000)
          } else {
            mapTexture = this.editor.resources.panelGrainTextureVertical
            textureScale.set(2000, shapeBounds.max.y - shapeBounds.min.y)
          }
          break

        // Cathedral texture
        case StockMaterialTypes.cathedralTexture:
          mapTexture = this.editor.resources.panelCathedralTexture
          break

        // Satinlite texture
        case StockMaterialTypes.satinliteTexture:
          mapTexture = this.editor.resources.panelSatinliteTexture
          break

        // Spotswood texture
        case StockMaterialTypes.spotswoodTexture:
          mapTexture = this.editor.resources.panelSpotswoodTexture
          break
      }

      let extrudeParameters = {
        curveSegments: shapeDivisions,
        depth: this.getThickness(),
        bevelEnabled: false,
        UVGenerator: new EditorShapeUVGenerator(this, textureScale)
      }

      var geometry = new THREE.ExtrudeGeometry(extrudeShape, extrudeParameters)

      var material = new THREE.MeshBasicMaterial({
        color: this.glassColour,
        map: mapTexture,
        transparent: true,
        opacity: glassOpacity
      })

      var shapeObject = new THREE.Mesh(geometry, material)
      shapeObject.position.z = -99
      this.sceneObject.add(shapeObject)
    }

    // Build outline for the shape to highlight it
    if(displayShape) {
      let outlineColour = new THREE.Color(this.editor.getTheme().panelOutlineColour)
      var outlineGeometry = new THREE.BufferGeometry();
      var vertices = []
      var colours = []

      // TODO: Figure out how to find if a vertex is on a polished segment or not
      // TODO: Alternatively, overlap more lines for polished segments only (easier)


      var shapePoints = extrudeShape.getPoints(shapeDivisions)
      for(var i = 0; i < shapePoints.length - 1; i++) {
        vertices.push(shapePoints[i].x , shapePoints[i].y, 0, shapePoints[(i + 1) % shapePoints.length].x , shapePoints[(i + 1) % shapePoints.length].y, 0)
        colours.push(outlineColour.r, outlineColour.g, outlineColour.b, this.editor.getTheme().panelOutlineOpacity)
        colours.push(outlineColour.r, outlineColour.g, outlineColour.b, this.editor.getTheme().panelOutlineOpacity)
      }

      // Add outlines for the holes
      centerCutoutList.forEach(hole => {
        let holeOutlineColour = new THREE.Color(hole.isCutoutPolished ? this.editor.getTheme().cutoutCenterPolishedColour : this.editor.getTheme().panelOutlineColour)
        let holeOutlineOpacity = hole.isCutoutPolished ? this.editor.getTheme().cutoutCenterPolishedOpacity : this.editor.getTheme().panelOutlineOpacity

        // If this hole has intersections, display as red
        if(centerCutoutIntersections.find(intHole => intHole.id == hole.id) != null) {
          holeOutlineColour.setRGB(1, 0, 0)
          holeOutlineOpacity = 1
        }

        let holeShape = hole.generateShape()
        shapePoints = holeShape.getPoints()
        for(var i = 0; i < shapePoints.length - 1; i++) {
          vertices.push(shapePoints[i].x , shapePoints[i].y, 0, shapePoints[(i + 1) % shapePoints.length].x , shapePoints[(i + 1) % shapePoints.length].y, 0)

          colours.push(holeOutlineColour.r, holeOutlineColour.g, holeOutlineColour.b, holeOutlineOpacity)
          colours.push(holeOutlineColour.r, holeOutlineColour.g, holeOutlineColour.b, holeOutlineOpacity)
        }
      })

      var outlineMaterialInfo = {
        transparent: true,
        vertexColors: true
      }

      if(this.shapeHighlightOnly) {
        outlineMaterialInfo.color = this.editor.getTheme().panelHighlightColour
        outlineMaterialInfo.transparent = true
        outlineMaterialInfo.opacity = this.editor.getTheme().panelHighlightOpacity
        outlineMaterialInfo.vertexColors = false
        outlineMaterialInfo.dashSize = 6
        outlineMaterialInfo.gapSize = 3
        outlineMaterialInfo.scale = 1
      }

      outlineGeometry.setAttribute( 'position', new THREE.Float32BufferAttribute(vertices, 3))
      outlineGeometry.setAttribute( 'color', new THREE.Float32BufferAttribute(colours, 4))

      if(this.shapeHighlightOnly)
        this.outlineMaterial = new THREE.LineDashedMaterial(outlineMaterialInfo)
      else
        this.outlineMaterial = new THREE.LineBasicMaterial(outlineMaterialInfo)

      this.outlineObj = new THREE.LineSegments(outlineGeometry, this.outlineMaterial)
      this.outlineObj.computeLineDistances()
      this.sceneObject.add(this.outlineObj)
    }

    // Add in the extra segment details for vertices / selections / polished
    if(!this.shapeHighlightOnly && this.editor.getViewLevel() == GlassDesignerTypes.EditorViewLevelTypes.normal) {
      this.segmentList.forEach((segment, index) => {

        // Create the segment selection & vertex objects
        this._createSegmentSelectionObject(segment, index)
        this._createSegmentStartObjects(segment, index)

        if(segment.isCutoutPolished || segment.isStartCornerPolished)
          this._createSegmentPolishedObjects(segment, index)
      })
    }

    // Create the edgework highlights
    if(!this.shapeHighlightOnly && this.editor.getOptionDisplayEdgework()) {
      this._createEdgeworkHighlights()
    }    

    // Generate the addition detail objects
    // Useful for education/display/info
    // Controlled by layers
    if(!this.shapeHighlightOnly) {
      this._createShapeDetailObjects()
    }

    // Icons
    var displayApproval = false
    var displayWarning = false
    if((this.isCancelled() || this.isInvoiced() || this.isDespatched() || this.isNCRLocked()) && this.editor.isEditingAvailable())
      displayWarning = true
    
    if(!this.shapeHighlightOnly && displayShape && (this.isReviewApproved || this.reviewApprovalOverride) && !displayWarning)
      displayApproval = true

    if(this.approvedIconObj) {
      this.approvedIconObj.setVisible(displayApproval)
      if(displayApproval)
        this.approvedIconObj.setPosition(this.approvedIconPosition)
    }

    if(this.warningIconObj) {
      this.warningIconObj.setVisible(displayWarning)
      if(displayWarning)
        this.warningIconObj.setPosition(this.warningIconPosition)
    }
  }


  /**
   * Create the edgework highlight details
   */
  _createEdgeworkHighlights() {
    let edgeworkEdgeInfo = this.calculateEdgeworkEdgeList()

    if(edgeworkEdgeInfo.segmentList.length == 0)
      return
      
    let defaultFlagWidth = 200
    var flagHeight = 8
    
    edgeworkEdgeInfo.segmentList.forEach(edge => {

      // Line
      if(edge.segmentType == GlassDesignerTypes.SegmentTypes.line) {
        var segLength = edge.segLength
        
        var edgeDir = new THREE.Vector2()
        edgeDir.subVectors(edge.segEndPosition, edge.segStartPosition)
        edgeDir.normalize()
        
        let segAngleRad = edgeDir.angle()
        let objAngles = new THREE.Euler(0, 0, segAngleRad)
        var flagWidth = Math.min(defaultFlagWidth, segLength * 0.50)
        
        // Bring it in from the midpoint
        var segMidPoint = new THREE.Vector2((edge.segEndPosition.x + edge.segStartPosition.x) / 2, (edge.segEndPosition.y + edge.segStartPosition.y) / 2)

        // Show full length edgework if in cutting display mode
        if(this.editor.getViewLevel() == GlassDesignerTypes.EditorViewLevelTypes.cutting) {
          if(edge.startPosition) {
            segMidPoint.set((edge.endPosition.x + edge.startPosition.x) / 2, (edge.endPosition.y + edge.startPosition.y) / 2)
            flagWidth = edge.length
            flagHeight = 3
          }
        }

        let segmentPadding = -flagHeight
        var pos = new THREE.Vector3(segMidPoint.x, segMidPoint.y, 0)
        pos.addScaledVector(new THREE.Vector3(edgeDir.y, -edgeDir.x, 0), -(segmentPadding + flagHeight*0.5))

        var flagGeometry = new THREE.BoxGeometry(flagWidth, flagHeight, 5)
        let flagMaterial = new THREE.MeshBasicMaterial({ color: edge.highlightColour });

        let flagObject = new THREE.Mesh(flagGeometry, flagMaterial)

        // Setup pos
        flagObject.position.set(pos.x, pos.y, pos.z)
        flagObject.setRotationFromEuler(objAngles)

        this.sceneObject.add(flagObject)
        this.edgeworkFlagObjects.push(flagObject)
      }

      // Arcs
      // D-ends / circles can have edgework
      if(edge.segmentType == GlassDesignerTypes.SegmentTypes.arc) {

        let segmentIndex = this.findSegmentIndexById(edge.segmentId)
        if(segmentIndex != -1) {
          let segShape = MathsUtils.generateShapeFromSegment(this.getSegmentForIndex(segmentIndex-1), this.getSegmentForIndex(segmentIndex), this.getSegmentForIndex(segmentIndex+1))
          let segPoints = segShape.getPoints(Config.Settings.arcSubdivisions)

          let edgeworkLineObj = new LineObject({
            colour: edge.highlightColour,
            width: flagHeight*0.5
          })
          edgeworkLineObj.updateLinePath(segPoints)
          edgeworkLineObj.setVisible(true)
          this.sceneObject.add(edgeworkLineObj.getThreeObject())

          this.edgeworkFlagObjects.push(edgeworkLineObj)
        }
      }      
    })
  }


  /**
   * Creates the segment selection object
   * Displayed when the segment is currently been selected
   */
  _createSegmentSelectionObject(segment, segmentIndex) {

    let segShape = MathsUtils.generateShapeFromSegment(this.getSegmentForIndex(segmentIndex-1), this.getSegmentForIndex(segmentIndex), this.getSegmentForIndex(segmentIndex+1))
    let segPoints = segShape.getPoints(Config.Settings.arcSubdivisions)

    let selectedLineObj = new LineObject({
      colour: this.editor.getTheme().selectionColour,
      width: this.editor.getTheme().selectionWidth,
      userData: {
        segmentIndex: segmentIndex
      }
    })
    selectedLineObj.updateLinePath(segPoints)
    selectedLineObj.setVisible(segment.isSegmentSelected)
    this.sceneObject.add(selectedLineObj.getThreeObject())

    this.selectedSegmentObjects.push(selectedLineObj)
  }


  /**
   * Creates the segment start selection & non-selection objects
   */
  _createSegmentStartObjects(segment, segmentIndex) {
    // Don't add in vertex display for stock-linked shapes
    if(this.isStockLinked())
      return

    // Don't display vertices in shape editing mode
    if(this.editor.isShapeEditingMode())
      return

    let vertexSize = 18
    let selectionSize = 18
    // Note: Full white, texture is coloured
    let selectionColour = 0xFFFFFF
    let vertexColour = 0xFFFFFF

    var selectionGeometry = new THREE.BoxGeometry(selectionSize, selectionSize, 5)
    let selectionMaterial = new THREE.MeshBasicMaterial({
      map: this.editor.resources.vertexTextureShapeSelected,
      color: selectionColour,
      transparent: true,
      depthWrite: false,
      depthTest: false
    })

    let selectionObj = new THREE.Mesh(selectionGeometry, selectionMaterial)

    // Setup pos
    selectionObj.position.set(segment.startPosition.x, segment.startPosition.y, 200)
    selectionObj.userData = {
      isSegmentStart: true,
      isSelection: true,
      segmentIndex: segmentIndex
    }

    selectionObj.visible = segment.isSegmentStartSelected
    this.sceneObject.add(selectionObj)

    // Vertex default for non-selections
    var vertexGeometry = new THREE.BoxGeometry(vertexSize, vertexSize, 5)
    let vertexMaterial = new THREE.MeshBasicMaterial({
      map: this.editor.resources.vertexTexture,
      color: vertexColour,
      transparent: true,
      depthWrite: false,
      depthTest: false
    })

    let vertexObj = new THREE.Mesh(vertexGeometry, vertexMaterial)

    // Setup pos
    vertexObj.position.set(segment.startPosition.x, segment.startPosition.y, 200)
    vertexObj.userData = {
      isSegmentStart: true,
      isSelection: false,
      segmentIndex: segmentIndex
    }

    vertexObj.visible = !segment.isSegmentStartSelected
    this.sceneObject.add(vertexObj)
  }


  /**
   * Creates the segment cutout / corner polished object
   * Displayed when the segment is flagged for polish
   */
  _createSegmentPolishedObjects(segment, segmentIndex) {

    let segList = MathsUtils.convertSegmentListCornerRadiusToArcs([segment])
    segList = segList.filter(seg => {
      if(seg.type == GlassDesignerTypes.SegmentTypes.arc && seg.isArcFromCornerRadius)
        return segment.isStartCornerPolished || segment.isCutoutPolished
      return segment.isCutoutPolished
    })

    let segShape = MathsUtils.generateShapeFromSegmentList(segList)
    let segPoints = segShape.getPoints(Config.Settings.arcSubdivisions)

    let polishedLineObj = new LineObject({
      colour: this.editor.getTheme().panelPolishedColour,
      opacity: this.editor.getTheme().panelPolishedOpacity,
      width: this.editor.getTheme().panelPolishedWidth,
      userData: {
        isPolished: true,
        segmentIndex: segmentIndex
      }
    })
    polishedLineObj.updateLinePath(segPoints)
    polishedLineObj.setVisible(true)
    this.sceneObject.add(polishedLineObj.getThreeObject())

    this.polishedSegmentObjects.push(polishedLineObj)
  }


  /**
   * Create the cutting shape info object
   * Displays how the shape is going to be cut out from the source sheet
   */
  _createShapeDetailObjects() {
    var shapeDivisions = Config.Settings.arcSubdivisions
    this.cutShapeInfoObj = new THREE.Object3D()

    // Cutting shape points
    var cuttingLineList = this.calculateCuttingLineList()
    cuttingLineList = cuttingLineList.filter(cutLine => !cutLine.isOnBounds)
    var actualShape = this.generateShape()

    // Source Rectangle
    // The rectangular area cut from the original sheet
    if(this.editor.getViewLevel() == GlassDesignerTypes.EditorViewLevelTypes.cutting) {

      // Total glass rectangle
      var outerBounds = this.calculateGlassCutBounds()

      var outerShape = new THREE.Shape()
      outerShape.moveTo(outerBounds.min.x, outerBounds.min.y)
      outerShape.lineTo(outerBounds.max.x, outerBounds.min.y)
      outerShape.lineTo(outerBounds.max.x, outerBounds.max.y)
      outerShape.lineTo(outerBounds.min.x, outerBounds.max.y)
      outerShape.lineTo(outerBounds.min.x, outerBounds.min.y)

      var geometry = new THREE.ExtrudeGeometry(outerShape, { depth: 2, bevelEnabled: false })
      var material = new THREE.MeshPhongMaterial({
        color: '#FFFFFF',
        emissive: '#4040B0',
        transparent: true,
        flatShading: true,
        opacity: 0.10
      })
      var shapeObject = new THREE.Mesh(geometry, material)
      this.cutShapeInfoObj.add(shapeObject)
    }

    // The cutting planes shape
    // Displays straight line cuts performed in cutting
    // Note: Not all straight line cuts are performed where interior cuts by CNC, but it's hard to guage currently what is left
    if(this.editor.getViewLevel() == GlassDesignerTypes.EditorViewLevelTypes.cutting || 
       this.editor.getViewLevel() == GlassDesignerTypes.EditorViewLevelTypes.cutouts) {
      var vertices = []

      cuttingLineList.forEach(cutLine => {
        vertices.push(cutLine.startPosition.x , cutLine.startPosition.y, 0, cutLine.endPosition.x , cutLine.endPosition.y, 0)
      })

      if(vertices.length > 0) {
        var cuttingShapeGeometry = new THREE.BufferGeometry();
        cuttingShapeGeometry.setAttribute( 'position', new THREE.Float32BufferAttribute(vertices, 3))

        this.cuttingLineMaterial = new THREE.LineDashedMaterial({
          color: this.editor.getTheme().cuttingPlaneColour,
          transparent: true,
          opacity: this.editor.getTheme().cuttingPlaneOpacity,
          dashSize: 8,
          gapSize: 4,
          scale: 1
        })
        var innerShapeObject = new THREE.LineSegments(cuttingShapeGeometry, this.cuttingLineMaterial)
        innerShapeObject.computeLineDistances()
        this.cutShapeInfoObj.add(innerShapeObject)
      }

      // Show actual shape for reference
      vertices = []
      var shapePoints = actualShape.getPoints(shapeDivisions)
      for(var i = 0; i < shapePoints.length - 1; i++)
        vertices.push(shapePoints[i].x , shapePoints[i].y, 0, shapePoints[(i + 1) % shapePoints.length].x , shapePoints[(i + 1) % shapePoints.length].y, 0)

      if(vertices.length > 0) {
        var actualShapeGeometry = new THREE.BufferGeometry();
        actualShapeGeometry.setAttribute( 'position', new THREE.Float32BufferAttribute(vertices, 3))

        var actualShapeMaterial = new THREE.LineBasicMaterial({
          color: this.editor.getTheme().cuttingShapeOutlineColour,
          transparent: true,
          opacity: this.editor.getTheme().cuttingShapeOutlineOpacity
        })
        var actualShapeObject = new THREE.LineSegments(actualShapeGeometry, actualShapeMaterial)
        //actualShapeObject.computeLineDistances()
        this.cutShapeInfoObj.add(actualShapeObject)
      }
    }


    // Cutouts
    if(this.editor.getViewLevel() == GlassDesignerTypes.EditorViewLevelTypes.cutouts) {
      let cutoutList = this.calculateCNCCutoutList()

      cutoutList.forEach(cutoutInfo => {
        var points = cutoutInfo.displayPointList || cutoutInfo.points
        if(MathsUtils.get2DPolygonOrientation(points) > 0)
          points.reverse()

        var cutShape = new THREE.Shape(points)

        var colour = '#00FFFF'
        if(cutoutInfo.type == OrderEditorCutoutTypes.edgeCutOut)
          colour = cutoutInfo.isCutoutPolished ? '#00FF00' : '#40FF40'
        else if(cutoutInfo.type == OrderEditorCutoutTypes.centerCutOut)
          colour = cutoutInfo.isCutoutPolished ? '#FF0000' : '#FF4040'

        var cutGeometry = new THREE.ExtrudeGeometry(cutShape, { depth: 2, bevelEnabled: false })
        var cutMaterial = new THREE.MeshPhongMaterial({
          color: '#FFFFFF',
          emissive: colour,
          transparent: true,
          flatShading: true,
          opacity: cutoutInfo.isCutoutPolished ? 0.8 : 0.20
        })
        var cutShapeObject = new THREE.Mesh(cutGeometry, cutMaterial)

        // Cutout Path
        let cutoutPathLineObj = new LineObject({
          colour: colour,
          width: 2.0
        })
        cutoutPathLineObj.updateLinePath(points)
        cutoutPathLineObj.updateResolution(this.editor.settings.viewportWidth, this.editor.settings.viewportHeight)
        this.cutShapeInfoObj.add(cutShapeObject)
        this.cutShapeInfoObj.add(cutoutPathLineObj.getThreeObject())
      })
    }

    this.sceneObject.add(this.cutShapeInfoObj)
  }


  // onBeforeRender
  // Called prior to rendering of the object
  onBeforeRender() {
    super.onBeforeRender()

    var viewScale = 1.0 / this.editor.camera.zoom
    let selSegmentStartScale = Math.min(viewScale, this.segmentVertexScaleMax)

    // Scale any line objects
    this.selectedSegmentObjects.forEach(segmentSelObj => {
      segmentSelObj.updateResolution(this.editor.settings.viewportWidth, this.editor.settings.viewportHeight)
    })
    this.polishedSegmentObjects.forEach(polishedObj => {
      polishedObj.updateResolution(this.editor.settings.viewportWidth, this.editor.settings.viewportHeight)
    })

    // Scale edgework flag line objects
    this.edgeworkFlagObjects.forEach(flagObj => {
      if(flagObj instanceof LineObject)
        flagObj.updateResolution(this.editor.settings.viewportWidth, this.editor.settings.viewportHeight)
    })

    let segmentStartChildren = this.findSceneObjectChildren({ isSegmentStart: true })
    segmentStartChildren.forEach(childObj => {
      childObj.scale.x = selSegmentStartScale
      childObj.scale.y = selSegmentStartScale
    })

    // Scale dashed line for outline shape
    if(this.shapeHighlightOnly && this.outlineMaterial) {
      this.outlineMaterial.setValues({ scale: this.editor.camera.zoom })
      this.outlineMaterial.needsUpdate = true
    }

    // Scale dashed line for cutting lines
    if(this.editor.getViewLevel() == GlassDesignerTypes.EditorViewLevelTypes.cutting || 
       this.editor.getViewLevel() == GlassDesignerTypes.EditorViewLevelTypes.cutouts) {

      if(this.cuttingLineMaterial) {
        this.cuttingLineMaterial.setValues({ scale: this.editor.camera.zoom })
        this.cuttingLineMaterial.needsUpdate = true
      }
    }
  }


  // onMouseMoveEvent
  onMouseMoveEvent(ev, mouseData, intersectionData = {}) {
    if(this.editor.isSelectEnabled()) {
      if(intersectionData.tag == GlassDesignerTypes.ShapeEditingTypes.shape ||
        intersectionData.tag == GlassDesignerTypes.ShapeEditingTypes.segment || 
        intersectionData.tag == GlassDesignerTypes.ShapeEditingTypes.segmentStart || 
        intersectionData.tag == GlassDesignerTypes.ShapeEditingTypes.hole) {
        this.editor.setMouseCursorHand()
      }
    }
  }

  // onMouseUpEvent
  onMouseUpEvent(ev, mouseData, intersectionData = {}) {
   
  }


  /**
   * Check for intersections between the object and a ray
   * Returns an object containing intersection point & object position
   * Returns null if no intersection occurred
   */
  checkRayIntersection(ray, worldRadius) {
    // Check what is closest to the ray & use that
    // Segment, Vertex or Hole

    // Ignore if not visible or highlight mode
    if(!this.isVisible() || this.shapeHighlightOnly)
      return null


    var closestIntersection = null

    let radiusTest = Math.max(worldRadius, 0.1)
    let segmentRadiusTest = radiusTest * 4
    let vertexRadiusTest = radiusTest * 8

    // Create a plane from the glass shape
    // Assumes the 2D shape is sitting on the plane at origin (Z = 0)
    var shapePlane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0)
    var rayInt = new THREE.Vector3()
    if(!ray.intersectPlane(shapePlane, rayInt))
      return null

    var ray2DPoint = new THREE.Vector2(rayInt.x, rayInt.y)

    // Segments
    this.segmentList.forEach((segment, index) => {
      // Ignore selection of segments / vertices for stock linked shapes
      if(this.isStockLinked())
        return

      let intPoint = segment.projectPointOnSegment(ray2DPoint)
      let dist = ray2DPoint.distanceTo(intPoint)
      
      if(dist <= Math.max(segmentRadiusTest, vertexRadiusTest)) {
        var tag = null
        var tagIndex = index

        // If closer to the start/end vertex, use that
        if(segment.startPosition.distanceTo(ray2DPoint) <= vertexRadiusTest)
          tag = GlassDesignerTypes.ShapeEditingTypes.segmentStart
        else if(segment.endPosition.distanceTo(ray2DPoint) <= vertexRadiusTest) {
          tag = GlassDesignerTypes.ShapeEditingTypes.segmentStart

          // End vertex. Adjust index to move to next segment so we're always editing the start pos to simplify
          tagIndex++
          tagIndex %= this.segmentList.length
        } else if(dist < segmentRadiusTest)
          tag = GlassDesignerTypes.ShapeEditingTypes.segment

        // Check if closer than existing intersection
        if((!closestIntersection || dist < closestIntersection.distance) && tag != null) {
          closestIntersection = {
            intersection: intPoint,
            position: ray2DPoint,
            objectId: this.id,
            distance: dist,
            tag: tag,
            tagIndex: tagIndex
          }
        }
      }
    })

    // Check if inside shape if nothing else selected
    if(!closestIntersection) {
      // Note: Set distance to a large amount so objects over the shape (holes, hole dimensions, etc) get preference for mouse/touch intersection

      if(this.isPointInsideShape(ray2DPoint)) {
        closestIntersection = {
          intersection: ray2DPoint,
          position: ray2DPoint,
          objectId: this.id,
          distance: 1000,
          tag: GlassDesignerTypes.ShapeEditingTypes.shape,
          tagIndex: 0,
          tagObjectId: this.id
        }      
      }
    }



    return closestIntersection
  }


  /**
   * Clone the glass shape to a new object
   */
  clone() {
    var newShape = new GlassShape(this.editor, this.itemData)
    newShape.copy(this)
    return newShape
  }


  /**
   * Copy an object into this shape object
   */
  copy(otherShape) {
    super.copy(otherShape)

    // Copy additional data outside the itemData object
    this.isStamped = otherShape.isStamped
    this.defaultEdgeworkConfigId = otherShape.defaultEdgeworkConfigId

    this.segmentList = otherShape.segmentList.map(s => s.clone())

    // Clone all children
    this.children = otherShape.children.filter(child => {
      // Ignore groups, they need to be done seperately
      if(child instanceof GlassShapeGroup)
        return false
      return true
    })
    .map(child => {
      var newChild = child.clone()
      if(!newChild)
        return null
      newChild.parentObject = this
      return newChild
    })
    .filter(child => child != null)

    // Adjust datum links in holes/panel items to match new IDs for shape / segments
    this.children.forEach(childObj => {
      if(_.isArray(childObj.datumLinks)) {
        childObj.datumLinks.forEach(datumLink => {
          datumLink.sourceShapeId = this.id

          var sourceHoleIndex = otherShape.getCenterCutoutList().findIndex(h => h.id == datumLink.sourceHoleObjectId)
          if(sourceHoleIndex != -1)
            datumLink.sourceHoleObjectId = this.getCenterCutoutList()[sourceHoleIndex].id

          var sourceDatumLineIndex = otherShape.getDatumLineList().findIndex(h => h.id == datumLink.datumLineId)
          if(sourceDatumLineIndex != -1)
            datumLink.datumLineId = this.getDatumLineList()[sourceDatumLineIndex].id

          var segmentIndex = otherShape.segmentList.findIndex(s => s.id == datumLink.segmentId)
          if(segmentIndex != -1)
            datumLink.segmentId = this.segmentList[segmentIndex].id
        })
      }
    })

    // Clone groups
    otherShape.getShapeGroupList().forEach(group => {
      let newGroup = group.clone()

      // Transfer segments over
      let newShapeSegments = group.segmentIdList.map(segmentId => {
        return this.getSegmentForIndex(otherShape.findSegmentIndexById(segmentId))
      })
      newGroup.updateGroupSegments(newShapeSegments)
      this.addChildObject(newGroup)

      // Update extra details now the group is linked to the shape
      newGroup.setGroupCutoutPolished(group.isGroupCutoutPolished())
    })
   
    this.segmentListUpdated()
  }


  // Serialize/Deserialize to/from a JSON object
  serialize() {    
    var outputData = super.serialize()

    outputData.glassColour = this.glassColour
    outputData.defaultEdgeworkConfigId = this.defaultEdgeworkConfigId
    outputData.isStamped = this.isStamped
    outputData.view3DModeOffset = MathsUtils.serializeVector3(this.view3DModeOffset)
    outputData.view3DModeRotation = this.view3DModeRotation
    outputData.assemblyLinkUUID = this.assemblyLinkUUID

    // Stock & order item data
    // Trim out some data from DB objects so we only store absolute minimum & free up database
    outputData.itemData = _.cloneDeep(this.itemData)
    outputData.itemData.stock = _.cloneDeep(_.pick(this.itemData.stock || {}, [
      'id', 'name', 'code', 'thickness', 'glassSalesPriceList', 'glassType', 'isStockUsingFaces', 'isStockUsingGrain', 'stockMaterialType', 'stockColour1', 'stockColour2', 'stockColourOpacity'
    ]))
    outputData.itemData.stockPriceGroup =  _.cloneDeep(_.pick(this.itemData.stockPriceGroup || {}, [
      'id', 'name', 'isActive', 'isStockLinked'
    ]))

    outputData.segmentList = this.segmentList.map(segment => segment.serialize())

    return outputData
  }
  deserialize(object) {
    super.deserialize(object)

    this.glassColour = object.glassColour || this.glassColour
    this.defaultEdgeworkConfigId = object.defaultEdgeworkConfigId
    this.isStamped = !!object.isStamped
    this.view3DModeOffset = MathsUtils.deserializeVector3(object.view3DModeOffset || this.view3DModeOffset)
    this.view3DModeRotation = parseFloat(object.view3DModeRotation || 0)
    this.assemblyLinkUUID = object.assemblyLinkUUID

    // Stock & order item data
    this.itemData = _.cloneDeep(object.itemData)

    // Segments
    this.segmentList = (object.segmentList || []).map(segmentData => {
      var segment = new GlassSegment(segmentData.type)
      segment.deserialize(segmentData)
      return segment
    })

    this.segmentListUpdated()
    this.internalItemsUpdated()
  }


  clearSelection() {
    super.setSelected({}, false)

    this.segmentList.forEach(segment => {
      segment.clearSelection()
    })

    let segmentStartChildren = this.findSceneObjectChildren({ isSegmentStart: true, isSelection: true })
    let segmentStartNormal = this.findSceneObjectChildren({ isSegmentStart: true, isSelection: false })

    this.selectedSegmentObjects.forEach(childObj => childObj.setVisible(false))
    segmentStartChildren.forEach(childObj => childObj.visible = false)
    segmentStartNormal.forEach(childObj => childObj.visible = true)

    // If the object has an assembly parent, notify the parent
    if(this.isPartOfAssemblyGroup() && this.parentObject) {
      this.parentObject.onChildObjectSelectionUpdated(this)
    }
  }
  setSelected(selectionData = {}, isSelected) {
    super.setSelected(selectionData, isSelected)

    if(selectionData.isSegment) {
      this.segmentList[selectionData.segmentIndex].setSegmentSelected(isSelected)

      this.selectedSegmentObjects[selectionData.segmentIndex].setVisible(isSelected)
    }
    else if(selectionData.isSegmentStart) {
      this.segmentList[selectionData.segmentIndex].setSegmentStartSelected(isSelected)

      let segmentChildrenSelection = this.findSceneObjectChildren({ isSegmentStart: true, isSelection: true, segmentIndex: selectionData.segmentIndex })
      let segmentChildrenNormal = this.findSceneObjectChildren({ isSegmentStart: true, isSelection: false, segmentIndex: selectionData.segmentIndex })

      segmentChildrenSelection.forEach(childObj => childObj.visible = isSelected)
      segmentChildrenNormal.forEach(childObj => childObj.visible = !isSelected)
    }
    else if(selectionData.isHole) {

      //let segmentChildren = this.findSceneObjectChildren({ isSegmentStart: true, segmentIndex: selectionData.segmentIndex })
      //segmentChildren.forEach(childObj => childObj.visible = true)
    }

    // If the object has an assembly parent, notify the parent
    if(this.isPartOfAssemblyGroup() && this.parentObject) {
      this.parentObject.onChildObjectSelectionUpdated(this)
    }
  }


  /**
   * Calculate the 2D bounds of the glass shape based on segments only
   * Returns a Box2 object with min/max values for X/Y coordinates
   */
  calculateSegmentBounds() {
    return MathsUtils.calculateGlassSegmentListBounds(this.segmentList)
  }


  /**
   * Calculate the midpoint of the shape based on the bounds
   */
  calculateShapeMidpoint() {
    let bounds = this.calculateSegmentBounds()
    return bounds.getCenter(new THREE.Vector2())
  }


  /**
   * Calculate the minimum height of the sheet required to fit the glass shape
   * Rounds up to the nearest mm
   */
  calculateHeight() {
    let bounds = this.calculateSegmentBounds()
    return Math.ceil(bounds.max.y - bounds.min.y)
  }


  /**
   * Calculate the minimum width of the sheet required to fit the glass shape
   * Rounds up to the nearest mm
   */
  calculateWidth() {
    let bounds = this.calculateSegmentBounds()
    return Math.ceil(bounds.max.x - bounds.min.x)
  }


  /**
   * Get the thickness of the glass panel
   * Rounds to 2 decimal places
   */
  getThickness() {
    return MathsUtils.roundNumber(parseFloat((this.itemData.stock || {}).thickness || 1), 2)
  }


  /**
   * Calculate the area of the final panel area (post cutting/cnc)
   * Returns a value in square millimetres
   */
  calculateFinalArea() {
    var area = 0

    // Area for segments
    area += MathsUtils.calculateSegmentListArea(this.segmentList, Config.Settings.arcSubdivisions)

    // Minus center cutouts
    this.getCenterCutoutList().forEach(centerCutout => {
      area -= centerCutout.calculateArea()
    })

    return area
  }


  /**
   * Calculate the weight of the final sheet (post cutting/cnc), thickness & standard glass weight calculation
   * Returns a value in Kilograms
   */
  calculateFinalWeight() {
    let multiplier = 2.5

    let area = this.calculateFinalArea()
    let thickness = this.getThickness()

    return Utils.convertMillimetreSqToMetreSq(area) * thickness * multiplier
  }


  /**
   * Calculate the total perimeter of the outside edge (including edge/corner cutouts)
   */
  calculatePerimeter() {
    return MathsUtils.calculateSegmentListPerimeter(this.segmentList, Config.Settings.arcSubdivisions)
  }


  /**
   * Get the list of shape groups associated with this glass shape
   */
  getShapeGroupList() {
    return this.children.filter(childObj => childObj instanceof GlassShapeGroup)
  }


  /**
   * Go through all shape groups & recalculate bounds & other info
   * If a shape group points to invalid segments, open the group
   */
  shapeGroupsUpdated(options = {}) {
    let groupList = this.getShapeGroupList()
    groupList.forEach(group => {
      let groupSegments = group.segmentIdList.map(segmentId => {
        return this.getSegmentById(segmentId)
      })

      // If the group contains invalid segments, remove the group (leave segments)
      // This happens if clipping operations happen around the shape & segments are subdivided/added/removed
      // Also occurs if clipping operations loses segment ids through the clipping process
      let containsInvalidSegs = groupSegments.filter(segment => segment == null).length > 0
      if(containsInvalidSegs) {
        let matchThreshold = 0.01

        // If the segment locations are still correct, attempt to fix the links to segments
        // Otherwise remove the group
        let updateSegmentList = _.compact(group.storedSegmentList.map(storedSeg => {
          return this.segmentList.find(shapeSeg => {
            if(storedSeg.type != shapeSeg.type)
              return false

            if(options.ignorePositonMatch)
              return true

            if(!MathsUtils.check2DPointsEqual(storedSeg.startPosition, shapeSeg.startPosition, matchThreshold))
              return false
            if(!MathsUtils.check2DPointsEqual(storedSeg.endPosition, shapeSeg.endPosition, matchThreshold))
              return false

            return true
          })
        }))

        if(updateSegmentList.length == group.storedSegmentList.length) {
          // Update group segments
          group.updateGroupSegments(updateSegmentList)
        } else {
          this.removeShapeGroup(group, { removeSegments: false })
        }
        return
      }

      // Update bounds
      group.updateGroupSegments(groupSegments)
    })
  }


  /**
   * Update the cutout groups list in the shape given the start/entry and end/exit points of the cutout
   * If an existing cutout group exists that covers the given points, merges the new points into the existing cutout
   * Creates a new cutout group if required
   * 
   * Entry position must be before exit position in segment list winding
   */
  updateCutoutGroup(entryPosition, exitPosition, options = {}) {
    let vertexThreshold = 0.1
    let groupSegmentList = []

    let startSegmentIndex = this.calculateClosestSegmentStartToPoint(entryPosition, [], vertexThreshold)
    let endSegmentIndex = this.calculateClosestSegmentStartToPoint(exitPosition, [], vertexThreshold)
    if(startSegmentIndex == -1 || endSegmentIndex == -1)
      return

    var cutoutStarted = false
    for(let i=0; i<this.segmentList.length*2; i++) {
      let index = i % this.segmentList.length

      if(index == startSegmentIndex)
        cutoutStarted = true
      if(cutoutStarted) {
        if(index == endSegmentIndex)
          break

        groupSegmentList.push(this.segmentList[index])
      }
    }

    if(groupSegmentList.length == 0)
      return

    // Make the segments fixed lengths
    groupSegmentList.forEach(segment => {
      segment.isUserFixed = true
      segment.isCutoutPolished = !!options.polished
    })

    // Merge with other shape groups that contain connecting segments
    let existingShapeGroup = null
    let existingGroupStartIndex = 0
    let existingGroupEndIndex = 0
    let mergeStartIndex = startSegmentIndex
    let mergeEndIndex = endSegmentIndex

    for(var curGroup of this.getShapeGroupList()) {

      if(curGroup.storedSegmentList.length > 0) {
        existingGroupStartIndex = this.calculateClosestSegmentStartToPoint(curGroup.storedSegmentList[0].startPosition, [], vertexThreshold)
        existingGroupEndIndex = this.calculateClosestSegmentStartToPoint(curGroup.storedSegmentList[curGroup.storedSegmentList.length - 1].endPosition, [], vertexThreshold)

        if(MathsUtils.isValueBetweenCircularValues(startSegmentIndex, existingGroupStartIndex, existingGroupEndIndex)) {
          existingShapeGroup = curGroup
          mergeStartIndex = existingGroupStartIndex
        }
        if(MathsUtils.isValueBetweenCircularValues(endSegmentIndex, existingGroupStartIndex, existingGroupEndIndex)) {
          existingShapeGroup = curGroup
          mergeEndIndex = existingGroupEndIndex
        }

        if(existingShapeGroup)
          break
      }
    }

    if(existingShapeGroup) {
      // Merge with group
      // Union segments between 2 groups & update group to the new segment list
      var groupStarted = false
      let newSegmentList = []
      for(var i=0; i<this.segmentList.length*2; i++) {
        let index = i % this.segmentList.length

        if(index == mergeStartIndex)
        groupStarted = true
        if(groupStarted) {
          if(index == mergeEndIndex)
            break

          newSegmentList.push(this.segmentList[index])
        }
      }

      // Update group segments info
      newSegmentList.forEach(segment => {
        segment.isUserFixed = true
      })

      existingShapeGroup.updateGroupSegments(newSegmentList)
      existingShapeGroup.setGroupCutoutPolished(!!options.polished)

    } else {

      // New group
      var group = new GlassShapeGroup(this.editor, {
        isCutout: true,
        segmentList: groupSegmentList,
        cutoutType: options.cutoutType || GlassDesignerTypes.CutoutTypes.edge,
        cutoutMeasurementType: options.cutoutMeasurementType || GlassDesignerTypes.CutoutMeasurementTypes.center
      })
      this.addChildObject(group)
      group.setGroupCutoutPolished(!!options.polished)
    }
  }


  /**
   * Remove a shape group object from the shape
   * If set, will also remove the segments associated with the group from the shape itself
   * Otherwise, will just remove the group object
   */
  removeShapeGroup(shapeGroup, options = {}) {
    this._startEditingSegmentList()

    try {
      // Get segments in order the group is set. Very important
      let groupSegments = _.compact(shapeGroup.segmentIdList.map(segmentId => {
        return this.getSegmentById(segmentId)
      }))

      if(options.removeSegments) {

        // Remove segments from the shape if a cutout
        if(shapeGroup.isCutout) {
          // Get start/end segments & cast out until they intersect
          // Stretch start/end segments to meet that & delete all in between
          // If meeting point outside of the group, don't do this
          // If co-linear segments, just delete all in between
          if(groupSegments.length > 0) {
            let firstSegmentIndex = this.findSegmentIndexById(groupSegments[0].id)
            let lastSegmentIndex = this.findSegmentIndexById(groupSegments[groupSegments.length - 1].id)

            let prevSeg = this.getSegmentForIndex(firstSegmentIndex-1)
            let nextSeg = this.getSegmentForIndex(lastSegmentIndex+1)

            let origStartSeg = prevSeg.clone()
            let origEndSeg = nextSeg.clone()

            // Remove segments
            groupSegments.forEach(removeSeg => {
              this.removeSegmentById(removeSeg.id)
            })

            // If points leftover in a line, cleanup the shape
            let cleanupStartSeg = this.getSegmentById(prevSeg.id)
            let cleanupEndSeg = this.getSegmentById(nextSeg.id)

            if(cleanupStartSeg && cleanupEndSeg) {            
              if(cleanupStartSeg.type == GlassDesignerTypes.SegmentTypes.line && cleanupEndSeg.type == GlassDesignerTypes.SegmentTypes.line) {

                // Project the segments out
                // If still near the group object, use the projection as a guess how to remove cutout
                // If too far out, ignore & user will have to fix it
                let projectedConnPoint = MathsUtils.projectLineIntersection(origStartSeg.startPosition, origStartSeg.endPosition, origEndSeg.startPosition, origEndSeg.endPosition)
                if(projectedConnPoint) {
                  let nearProjectThreshold = 10
                  
                  if(shapeGroup.bounds.distanceToPoint(projectedConnPoint) < nearProjectThreshold) {
                    cleanupStartSeg.endPosition.copy(projectedConnPoint)
                    cleanupEndSeg.startPosition.copy(projectedConnPoint)
                  }
                }

                // Future: Add a simplify/optimise function for the shape
                let angle1 = MathsUtils.radiansToDegrees(cleanupStartSeg.getAngle())
                let angle2 = MathsUtils.radiansToDegrees(cleanupEndSeg.getAngle())
                if(Math.abs(angle1 - angle2) < 0.1) {
                  cleanupStartSeg.endPosition = cleanupEndSeg.endPosition
                  cleanupStartSeg.endCornerRadius = cleanupEndSeg.endCornerRadius

                  this.removeSegmentById(cleanupEndSeg.id)
                }
              }
            }
          }
        }

      } else {
        // Remove isUserFixed flag from segments
        groupSegments.forEach(segment => {
          segment.isUserFixed = false
        })
      }
    } catch(err) {
      console.error(err)
    }

    this.removeChildObject(shapeGroup)
    shapeGroup.dispose()

    this._finishEditingSegmentList()
  }


  /**
   * Check if a segment in the shape is contained entirely within a group
   * 
   * Returns GlassShapeGroup that contains the segment, or null if no group is linked to the segment
   */
  isSegmentInGroup(segmentId) {
    let groupList = this.getShapeGroupList()

    return groupList.find(shapeGroup => {
      return shapeGroup.isSegmentInGroup(segmentId)
    }) != null
  }


  /**
   * Check if a segment end point connects to a cutout (with segment start outside of the same group)
   * 
   * Returns GlassShapeGroup that contains the segment end, or null if no group is linked to the segment
   */
  getSegmentEndLinkedGroup(segmentId) {
    let groupList = this.getShapeGroupList()

    let segmentIndex = this.findSegmentIndexById(segmentId)
    if(segmentIndex == -1)
      return null

    let segment = this.getSegmentForIndex(segmentIndex)
    let nextSegment = this.getSegmentForIndex(segmentIndex+1)

    for(let shapeGroup of groupList) {
      // Segment is fully within a group. Ignore the segment completely
      if(shapeGroup.isSegmentInGroup(segment.id))
        return null

      // If next segment is inside this group, use it
      if(shapeGroup.isSegmentInGroup(nextSegment.id))
        return shapeGroup
    }
    return null
  }


  /**
   * Check if a segment start point connects to a cutout (with segment end outside of the same group)
   * 
   * Returns GlassShapeGroup that contains the segment end, or null if no group is linked to the segment
   */
  getSegmentStartLinkedGroup(segmentId) {
    let groupList = this.getShapeGroupList()

    let segmentIndex = this.findSegmentIndexById(segmentId)
    if(segmentIndex == -1)
      return null

    let segment = this.getSegmentForIndex(segmentIndex)
    let prevSegment = this.getSegmentForIndex(segmentIndex-1)

    for(let shapeGroup of groupList) {
      // Segment is fully within a group. Ignore the segment completely
      if(shapeGroup.isSegmentInGroup(segment.id))
        return null

      // If prev segment is inside this group, use it
      if(shapeGroup.isSegmentInGroup(prevSegment.id))
        return shapeGroup
    }
    return null
  }


  /**
   * Calculate the start & end segments that link to a cutout group
   * 
   * Returns an object with segment ids for start & end segments
   */
  getSegmentStartEndLinksForShapeGroup(shapeGroup) {

    return this.getSegmentStartEndLinksForSegmentIdList(shapeGroup.segmentIdList)
  }


  /**
   * Calculate the start & end segments that link to a cutout's segment id list
   * 
   * Returns an object with segment ids for start & end segments
   */
  getSegmentStartEndLinksForSegmentIdList(segmentIdList) {
    var linkInfo = {
      nextSegmentId: null,    // Start point connects to the end of the cutout
      prevSegmentId: null     // End point connects to the start of the cutout
    }

    for(var i=0; i<this.segmentList.length; i++) {
      let segment = this.getSegmentForIndex(i)
      let prevSegment = this.getSegmentForIndex(i-1)
      let nextSegment = this.getSegmentForIndex(i+1)

      var isPrevSegInGroup = segmentIdList.some(listSegId => listSegId == prevSegment.id)
      var isNextSegInGroup = segmentIdList.some(listSegId => listSegId == nextSegment.id)

      if(!segmentIdList.some(listSegId => listSegId == segment.id)) {
        if(isPrevSegInGroup)
          linkInfo.nextSegmentId = segment.id
        if(isNextSegInGroup)
          linkInfo.prevSegmentId = segment.id
      }
    }

    return linkInfo
  }



  /**
   * Sets the lock/unlock status on a segments fixed length flag
   */
  setUserFixedSegmentFixedLength(segmentIndex, isUserFixed) {
    if(segmentIndex < 0 || segmentIndex >= this.segmentList.length)
      return

    var segment = this.segmentList[segmentIndex]
    segment.isUserFixed = isUserFixed
  }


  /**
   * Updates a segment's length based on user interaction
   * Locks the segment in place
   * 
   * Throws an exception if the adjustment failed (due to shape errors) with details about the failure reason
   */
  setSegmentFixedLength(segmentIndex, length, segmentChangeDirection = 1) {
    if(segmentIndex < 0 || segmentIndex >= this.segmentList.length)
      return

    var segment = this.segmentList[segmentIndex]

    let curSegmentLength = segment.getLength()
    let offsetLength = length - curSegmentLength
    var moveDirection = segment.getDirection()

    if(segmentChangeDirection == 0)
      moveDirection.negate()

    // If the length is the same, ignore it
    var precision = 1
    if(Math.abs(MathsUtils.roundNumber(curSegmentLength, precision) - MathsUtils.roundNumber(length, precision)) < 0.01) {
      return
    }

    this._runSegmentAdjustSolver([{
      segmentIndex: segmentIndex,
      moveDistance: offsetLength,
      moveDirection: moveDirection,
      isSegmentEnd: segmentChangeDirection == 1,
      isSegmentStart: segmentChangeDirection == 0,
      solveDirection: segmentChangeDirection == 1 ? 1 : -1
    }])

    this.segmentListUpdated()
    this.updateSceneObject()
  }


  /**
   * Sets the lock/unlock status on a segments fixed angle flag
   */
  setUserFixedSegmentFixedAngle(segmentIndex, isUserFixed) {
    if(segmentIndex < 0 || segmentIndex >= this.segmentList.length)
      return

    var segment = this.segmentList[segmentIndex]
    segment.isAngleUserFixed = isUserFixed
  }


  /**
   * Updates a segment's angle based on user interaction
   * Adjusts the angle between this segment and the next segment. Effectively edits the next segment end position, not this
   * Locks the segment angle in place
   * 
   * Throws an exception if the adjustment failed (due to shape errors) with details about the failure reason
   */
  setSegmentFixedAngle(segmentIndex, angleDegrees, segmentChangeDirection = 1) {
    if(segmentIndex < 0 || segmentIndex >= this.segmentList.length)
      return

    var segment = this.getSegmentForIndex(segmentIndex)
    var nextSegment = this.getSegmentForIndex(segmentIndex+1)

    var angleInfo = MathsUtils.calculateSegmentNonRoundedCornerDetails(segment, nextSegment)
    var currentAngle = angleInfo.angleDegrees
    let rotateDist = angleDegrees - currentAngle

    // If the angle is the same, ignore it
    var precision = 1
    if(Math.abs(MathsUtils.roundNumber(currentAngle, precision) - MathsUtils.roundNumber(angleDegrees, precision)) < 0.01) {
      return
    }

    this._runSegmentAngleAdjustSolver([{
      segmentIndex: segmentIndex,
      rotateAngle: rotateDist,
      isSegmentEnd: segmentChangeDirection == 1,
      isSegmentStart: segmentChangeDirection == 0,
      solveDirection: segmentChangeDirection == 1 ? 1 : -1
    }])

    this.segmentListUpdated()
    this.updateSceneObject()
  }


  /**
   * Run a solver on the segments to adjust the length or angle of a segment that would satisfy the constraints of fixed segments
   * 
   * Returns true if the solver successfully applied the update
   */
  _runSegmentAdjustSolver(segmentAdjustList) {
    let minSegmentLength = 3      // Dont adjust segments to go below this length
    let segmentAngleThreshold = 0.001   // If move/segment vectors are within this value, they are considered same angle
    let minMoveLengthStop = 0.025   // If the move vector is less than this after applying, consider the solver finished

    /**
     * Segment Solver
     * 
     * 1) Adjust the segment clockwise first
     * 2) Calculate the next segment details. If the next length would break the fixed angle/length, retain that segments length/angle & just offset the whole segment
     * 3) move to the next segment clockwise & run #2 check again
     * 4) Keep going until a segment has flexibility to accomodate the move
     * 5) If not possible, go in reverse direction & run same process in reverse
     * 6) Arc segments are considered fixed & cannot be resized, only moved
     */

    // Store all the existing info
    var segmentInfoList = []
    var holeInfoList = []

    this.segmentList.forEach((segment, index) => {
      let segmentCopy = segment.clone()
      segmentCopy.changed = false   // Add in changed flag

      segmentInfoList.push(segmentCopy)
    })

    // Store hole data for checks post solution
    this.getCenterCutoutList().forEach(hole => {

      holeInfoList.push({
        anchorPoint: hole.anchorPoint.clone(),
        datumLinks: hole.datumLinks.map(datum => datum.clone()),
        segmentList: hole.segmentList.map(seg => seg.clone()),
        measureToHorizontal: hole.measureToHorizontal,
        measureToVertical: hole.measureToVertical,
        changed: false
      })
    })

    for(var adjustInfo of segmentAdjustList) {
      var adjustSegmentIndex = adjustInfo.segmentIndex
      var segmentIndex = adjustInfo.segmentIndex
      var moveVec = adjustInfo.moveDirection.clone().multiplyScalar(adjustInfo.moveDistance)
      let solveDirection = adjustInfo.solveDirection

      var solutionFound = false

      for(var i=0; i<this.segmentList.length * 2; i++) {
        var prevSegmentIndex = segmentIndex - 1
        if(prevSegmentIndex < 0)
          prevSegmentIndex += segmentInfoList.length
        else
          prevSegmentIndex %= segmentInfoList.length

        let prevSegInfo = segmentInfoList[prevSegmentIndex]
        var segmentInfo = segmentInfoList[segmentIndex]

        // If nothing left to move, stop
        if(moveVec.length() <= minMoveLengthStop) {
          solutionFound = true
          break
        }

        if(segmentIndex == adjustSegmentIndex) {

          if(adjustInfo.isSegmentStart) {
            // Note: Segment start must be paired with solveDirection == -1
            segmentInfo.startPosition.add(moveVec)
          }
          if(adjustInfo.isSegmentEnd) {
            segmentInfo.endPosition.add(moveVec)
          }

          segmentInfo.changed = true

        } else {

          // If segment is fixed, adjust both positions and keep going
          if(segmentInfo.isUserFixed || segmentInfo.isAngleUserFixed || prevSegInfo.isAngleUserFixed) {
            segmentInfo.changed = true

            segmentInfo.startPosition.add(moveVec)
            segmentInfo.endPosition.add(moveVec)

          } else {

            segmentInfo.changed = true

            var segmentDir = new THREE.Vector2()
            if(solveDirection == 1)
              segmentDir.subVectors(segmentInfo.endPosition, segmentInfo.startPosition)
            else
              segmentDir.subVectors(segmentInfo.startPosition, segmentInfo.endPosition)

            let segLength = segmentDir.length()
            segmentDir.normalize()
            let moveLength = moveVec.length()

            var moveReplaceVec = new THREE.Vector2(0, 0)
            var moveVecDir = moveVec.clone().normalize()
            let angleDot = moveVecDir.dot(segmentDir)

            if(angleDot > 1.0 - segmentAngleThreshold) {

              if(segLength - moveLength < minSegmentLength) {

                let applyMove = segLength - minSegmentLength
                let remainder = moveLength - applyMove

                moveVec = moveVecDir.clone().multiplyScalar(applyMove)
                moveReplaceVec = moveVecDir.clone().multiplyScalar(remainder)
              }
            }

            if(solveDirection == 1)
              segmentInfo.startPosition.add(moveVec)
            else
              segmentInfo.endPosition.add(moveVec)

            moveVec = moveReplaceVec

            // Now move the whole segment by the remainder
            if(moveVec.length() > minMoveLengthStop) {
              segmentInfo.startPosition.add(moveVec)
              segmentInfo.endPosition.add(moveVec)
            }

            // TODO: Get segment length
            // If moveVec would shrink it in the opposite direction, limit the move so the segment is no less than minimum length
            // Reduce moveVec & keep going until moveVec has been consumed fully

            // TODO: Adjust end pos if solveDirection < 0

            // Check if the shape is now invalid (eg, self-intersecting)
            // Will throw an exception if this fails
            //this.checkSegmentListValidShape(segmentInfoList, holeInfoList)

            //solutionFound = true
            //break
          }
        }

        segmentIndex += solveDirection
        if(segmentIndex < 0)
          segmentIndex += this.segmentList.length
        else
          segmentIndex %= this.segmentList.length
      }

      if(!solutionFound)
        return false
    }

    // If a solution found, apply the changes to the shape
    if(solutionFound) {

      // Run a check on the shape
      // Will throw an exception if this fails
      this.checkSegmentListValidShape(segmentInfoList, holeInfoList)

      segmentInfoList.forEach((segmentInfo, index) => {

        if(segmentInfo.changed) {
          let arcPrevHeight = this.segmentList[index].calculateArcHeight()

          this.segmentList[index].startPosition = segmentInfo.startPosition
          this.segmentList[index].endPosition = segmentInfo.endPosition

          // Update arc info
          // The GlassSegment updateArc*() functions should recalculate all the info (angles) with start/end positions changing
          if(this.segmentList[index].type == GlassDesignerTypes.SegmentTypes.arc) {
            if(this.segmentList[index].arcEditType == GlassDesignerTypes.ArcEditTypes.arcHeight) {
              this.segmentList[index].updateArcByHeight(arcPrevHeight)
            } else {
              // Make sure arc radius is at least half the new distance
              let segLength = this.segmentList[index].startPosition.distanceTo(this.segmentList[index].endPosition)
              let arcRadius = Math.max(this.segmentList[index].arcRadius, segLength * 0.5)
              
              this.segmentList[index].updateArcByRadius(arcRadius)
            }
          }
        }
      })

      // TODO: Move holes around based on segments

      // TODO: Flag holes as having errors for holes that are now outside/intersecting with the shape
    }

    return solutionFound
  }


  /**
   * Run a solver on the segments to adjust the length or angle of a segment that would satisfy the constraints of fixed segments
   * 
   * Returns true if the solver successfully applied the update
   */
  _runSegmentAngleAdjustSolver(segmentAdjustList) {
    let minSegmentLength = 3      // Dont adjust segments to go below this length
    let segmentAngleThreshold = 0.001   // If move/segment vectors are within this value, they are considered same angle
    let minMoveLengthStop = 0.025   // If the move vector is less than this after applying, consider the solver finished

    /**
     * Segment Solver
     * 
     * 1) Adjust the segment clockwise first
     * 2) Calculate the next segment details. If the next length would break the fixed angle/length, retain that segments length/angle & just offset the whole segment
     * 3) move to the next segment clockwise & run #2 check again
     * 4) Keep going until a segment has flexibility to accomodate the move
     * 5) If not possible, go in reverse direction & run same process in reverse
     * 6) Arc segments are considered fixed & cannot be resized, only moved
     */

    // TODO: Finish this
    var segmentInfoList = this.segmentList.map(segment => {
      let segmentCopy = segment.clone()
      segmentCopy.changed = false   // Add in changed flag
      return segmentCopy
    })

    // Store hole data for checks post solution
    var holeInfoList = this.getCenterCutoutList().map(hole => {
      return {
        anchorPoint: hole.anchorPoint.clone(),
        datumLinks: hole.datumLinks.map(datum => datum.clone()),
        segmentList: hole.segmentList.map(seg => seg.clone()),
        measureToHorizontal: hole.measureToHorizontal,
        measureToVertical: hole.measureToVertical,
        changed: false
      }
    })

    var currentPivotPoint = null

    for(var adjustInfo of segmentAdjustList) {
      let adjustSegmentIndex = adjustInfo.segmentIndex
      let segmentIndex = adjustInfo.segmentIndex
      let solveDirection = adjustInfo.solveDirection
      let moveAngle = adjustInfo.rotateAngle

      if(solveDirection < 0)
        moveAngle = -moveAngle

      // Note: Editing will always be around the end point of the segment being adjusted

      // Arc angle
      currentPivotPoint = segmentInfoList[adjustSegmentIndex].endPosition.clone()

      // Start from the next segment
      let nextAdjustIndex = adjustSegmentIndex
      if(solveDirection > 0)
        nextAdjustIndex = MathsUtils.wrapValue(adjustSegmentIndex + solveDirection, segmentInfoList.length)

      // TODO: Calculate pivot point for angle change based on segment start/end & direction
      
      var solutionFound = false

      for(var i=0; i<this.segmentList.length * 2; i++) {
        var nextSegmentIndex = MathsUtils.wrapValue(segmentIndex + solveDirection, segmentInfoList.length)
        var prevSegmentIndex = MathsUtils.wrapValue(segmentIndex - solveDirection, segmentInfoList.length)

        var segmentInfo = segmentInfoList[segmentIndex]
        var nextSegmentInfo = segmentInfoList[nextSegmentIndex]
        var prevSegmentInfo = segmentInfoList[prevSegmentIndex]

        // If nothing left to rotate, stop
        if(Math.abs(moveAngle) < 0.01) {
          solutionFound = true
          break
        }

        let moveRadians = MathsUtils.degreesToRadians(moveAngle)

        if(segmentIndex == nextAdjustIndex) {
          // TODO: Cannot adjust the segment if it's connected to an arc prev/next

          // Rotate segment around pivot point
          let origSegStart = this.segmentList[segmentIndex].startPosition
          let origSegEnd = this.segmentList[segmentIndex].endPosition
          segmentInfo.startPosition = MathsUtils.rotate2DPoint(currentPivotPoint, origSegStart, -moveRadians)
          segmentInfo.endPosition = MathsUtils.rotate2DPoint(currentPivotPoint, origSegEnd, -moveRadians)
          segmentInfo.changed = true

          // Cast the rotated line to find the intersection point with the next segment
          // Ensures we manipulate the shape as little as posible
          // Only do this unless this segment is fixed length
          if((!segmentInfo.isUserFixed && !segmentInfo.isAngleUserFixed && solveDirection > 0) || (!nextSegmentInfo.isUserFixed && !nextSegmentInfo.isAngleUserFixed && solveDirection < 0)) {

            if(solveDirection > 0) {
              let rotateIntPoint = MathsUtils.calculate2DLinePlaneIntersection(segmentInfo.startPosition, segmentInfo.getDirection(), nextSegmentInfo.getOutDirection(), nextSegmentInfo.startPosition)
              if(!rotateIntPoint) {
                // No intersection found, cannot continue
                return false
              }

              segmentInfo.endPosition.copy(rotateIntPoint)

            } else {
              let rotateIntPoint = MathsUtils.calculate2DLinePlaneIntersection(segmentInfo.startPosition, segmentInfo.getDirection(), nextSegmentInfo.getOutDirection(), nextSegmentInfo.startPosition)
              if(!rotateIntPoint) {
                // No intersection found, cannot continue
                return false
              }

              segmentInfo.startPosition.copy(rotateIntPoint)
            }
          }

          if(solveDirection > 0) {
            nextSegmentInfo.startPosition.copy(segmentInfo.endPosition)
            prevSegmentInfo.endPosition.copy(segmentInfo.startPosition)
          }
          else {
            nextSegmentInfo.endPosition.copy(segmentInfo.startPosition)
            prevSegmentInfo.startPosition.copy(segmentInfo.endPosition)
          }

          nextSegmentInfo.changed = true

          // TODO: If this segment is not fixed length, adjust length here to accommodate angle change
          // TODO: Get next segment & cast it backwards so we only adjust this segments length, not the next segment unnessesarily

          // If the next segment is fixed (angle or length) then we have to keep going & rotate that as well
          if(solveDirection > 0) {
            if(!nextSegmentInfo.isUserFixed && !segmentInfo.isAngleUserFixed) {
              moveAngle -= moveAngle
            }
          } else {
            if(!segmentInfo.isUserFixed && !nextSegmentInfo.isAngleUserFixed) {
              moveAngle -= moveAngle
            }
          }

          nextAdjustIndex = MathsUtils.wrapValue(nextAdjustIndex + solveDirection, segmentInfoList.length)
        }

        segmentIndex = MathsUtils.wrapValue(segmentIndex + solveDirection, this.segmentList.length)
      }

      if(!solutionFound)
        return false
    }

    // If a solution found, apply the changes to the shape
    if(solutionFound) {
      // Run a check on the shape
      // Will throw an exception if this fails
      this.checkSegmentListValidShape(segmentInfoList, holeInfoList)

      segmentInfoList.forEach((segmentInfo, index) => {

        if(segmentInfo.changed) {
          let arcPrevHeight = this.segmentList[index].calculateArcHeight()

          this.segmentList[index].startPosition = segmentInfo.startPosition
          this.segmentList[index].endPosition = segmentInfo.endPosition

          // Update arc info
          // The GlassSegment updateArc*() functions should recalculate all the info (angles) with start/end positions changing
          if(this.segmentList[index].type == GlassDesignerTypes.SegmentTypes.arc) {
            if(this.segmentList[index].arcEditType == GlassDesignerTypes.ArcEditTypes.arcHeight) {
              this.segmentList[index].updateArcByHeight(arcPrevHeight)
            } else {
              // Make sure arc radius is at least half the new distance
              let segLength = this.segmentList[index].startPosition.distanceTo(this.segmentList[index].endPosition)
              let arcRadius = Math.max(this.segmentList[index].arcRadius, segLength * 0.5)
              
              this.segmentList[index].updateArcByRadius(arcRadius)
            }
          }
        }
      })
    }

    return solutionFound
  }


  /**
   * Move a segment in its perpendicular direction either forwards or backwards
   * Adjusts neighbouring segment start/end points to allow this segment to move as a whole line
   * Will attempt to run the solver to move other segments around to maintain segment length
   * 
   * Throws an exception with the details if the segment list represents an invalid shape
   */
  moveSegment(segmentIndex, distance, isPerpendicular) {
    if(segmentIndex < 0 || segmentIndex >= this.segmentList.length)
      return
    
    // If not enough of a move, ignore it
    if(Math.abs(distance) < 0.25)
      return

    let segment = this.segmentList[segmentIndex]

    var moveDirection = segment.getOutDirection()
    if(!isPerpendicular)
      moveDirection = segment.getDirection()


    // Move both vertices & run constraint solver
    this._runSegmentAdjustSolver([{
      segmentIndex: segmentIndex,
      moveDistance: distance,
      moveDirection: moveDirection,
      isSegmentStart: true,
      solveDirection: -1
    }, {
      segmentIndex: segmentIndex,
      moveDistance: distance,
      moveDirection: moveDirection,
      isSegmentEnd: true,
      solveDirection: 1
    }])

    this.segmentListUpdated()
    this.updateSceneObject()
  }


  /**
   * Shrink/Extend a segment by a distance
   * Adjusts neighbouring segment start/end points to allow this segment to be adjusted
   * Will attempt to run the solver to move other segments around
   * 
   * Throws an exception with the details if the segment list represents an invalid shape
   */
  shrinkExtendSegment(segmentIndex, distance, adjustStartPosition) {
    if(segmentIndex < 0 || segmentIndex >= this.segmentList.length)
      return
    
    // If not enough of a move, ignore it
    if(Math.abs(distance) < 0.25)
      return

    let segment = this.segmentList[segmentIndex]

    var dir = segment.getDirection()
    if(adjustStartPosition)
      dir.negate()

    // Move start of end vertex only & run constraint solver
    this._runSegmentAdjustSolver([{
      segmentIndex: segmentIndex,
      moveDistance: distance,
      moveDirection: dir,
      isSegmentStart: adjustStartPosition,
      isSegmentEnd: !adjustStartPosition,
      solveDirection: adjustStartPosition ? -1 : 1
    }])

    this.segmentListUpdated()
    this.updateSceneObject()
  }


  /**
   * Move a segment start point in the shape
   * 
   * Throws an exception with the details if the segment list represents an invalid shape
   */
  moveSegmentStart(segmentIndex, distance, moveDirType) {
    if(segmentIndex < 0 || segmentIndex >= this.segmentList.length)
      return
    
    // If not enough of a move, ignore it
    if(Math.abs(distance) < 0.25)
      return

    var dir

    // Move axially
    switch(moveDirType) {
      case GlassDesignerTypes.MoveEditDirectionTypes.up:
        dir = new THREE.Vector2(0, -1)
        break
      case GlassDesignerTypes.MoveEditDirectionTypes.right:
        dir = new THREE.Vector2(1, 0)
        break
      case GlassDesignerTypes.MoveEditDirectionTypes.down:
        dir = new THREE.Vector2(0, 1)
        break
      case GlassDesignerTypes.MoveEditDirectionTypes.left:
        dir = new THREE.Vector2(-1, 0)
        break        
    }

    // Move start of end vertex only & run constraint solver
    try {
      let segment = this.segmentList[segmentIndex]

      let adjustList = [{
        segmentIndex: segmentIndex,
        moveDistance: distance,
        moveDirection: dir,
        isSegmentStart: true,
        solveDirection: -1
      }]

      // If the segment is fixed, we have to move both points
      if(segment.isUserFixed) {
        adjustList.push({
          segmentIndex: segmentIndex,
          moveDistance: distance,
          moveDirection: dir,
          isSegmentEnd: true,
          solveDirection: 1
        })
      }

      this._runSegmentAdjustSolver(adjustList)
    } catch(err) {
      // Attempt reverse direction if normal direction fails
      var prevSegmentIndex = segmentIndex-1
      if(prevSegmentIndex < 0)
        prevSegmentIndex += this.segmentList.length

      this._runSegmentAdjustSolver([{
        segmentIndex: prevSegmentIndex,
        moveDistance: distance,
        moveDirection: dir,
        isSegmentEnd: true,
        solveDirection: 1
      }])
      
    }

    this.segmentListUpdated()
    this.updateSceneObject()
  }


  /**
   * Extrude a segment in its perpendicular direction either forwards or backwards
   * Creates 2 extra segments linking the shape to this segment so it can extrude
   * Does not run the solver as all other segments are unaffected
   * 
   * Throws an exception with the details if the segment list represents an invalid shape
   */
  extrudeSegment(segmentIndex, distance) {
    let perpTolerance = 0.01

    if(segmentIndex < 0 || segmentIndex >= this.segmentList.length)
      return
    
    // If not enough of a move, ignore it
    if(Math.abs(distance) < 0.25)
      return

    let segment = this.getSegmentForIndex(segmentIndex)
    let prevSegment = this.getSegmentForIndex(segmentIndex-1)
    let nextSegment = this.getSegmentForIndex(segmentIndex+1)

    let outDir = segment.getOutDirection()

    var addLeftSegment = false
    var addRightSegment = false

    // Don't add left/right segments if prev/next segments are perpendicular
    if(Math.abs(prevSegment.getDirection().dot(outDir)) < 1.0 - perpTolerance)
      addLeftSegment = true
    if(Math.abs(nextSegment.getDirection().dot(outDir)) < 1.0 - perpTolerance)
      addRightSegment = true

    if(addRightSegment) {
      var rightSegment = segment.clone()
      rightSegment.startPosition.set(rightSegment.endPosition.x, rightSegment.endPosition.y).addScaledVector(outDir, distance)
      rightSegment.startCornerRadius = rightSegment.endCornerRadius

      this.segmentList.splice(segmentIndex+1, 0, rightSegment)
    } else {
      nextSegment.startPosition.addScaledVector(outDir, distance)
    }

    if(addLeftSegment) {
      var leftSegment = segment.clone()
      leftSegment.endPosition.set(leftSegment.startPosition.x, leftSegment.startPosition.y).addScaledVector(outDir, distance)
      leftSegment.endCornerRadius = leftSegment.startCornerRadius

      
      this.segmentList.splice(segmentIndex, 0, leftSegment)
    } else {
      prevSegment.endPosition.addScaledVector(outDir, distance)
    }

    // Move this segments start/end positions
    segment.startPosition.addScaledVector(outDir, distance)
    segment.endPosition.addScaledVector(outDir, distance)

    this.segmentListUpdated()
    this.updateSceneObject()
  }


  /**
   * Taper a segment in its perpendicular direction either forwards or backwards
   * Tapers the start/end positions of the segment based on previous/next segments in the shape
   */
  taperSegment(segmentIndex, distance) {
    let tangentIgnoreThreshold = 0.15

    if(segmentIndex < 0 || segmentIndex >= this.segmentList.length)
      return
    
    // If not enough of a move, ignore it
    if(Math.abs(distance) < 0.25)
      return

    let segment = this.getSegmentForIndex(segmentIndex)
    let prevSegment = this.getSegmentForIndex(segmentIndex-1)
    let nextSegment = this.getSegmentForIndex(segmentIndex+1)

    var startPointDir = prevSegment.getDirection()
    var endPointDir = nextSegment.getDirection().negate()
    let directionScalar = distance > 0 ? 1.0 : -1.0

    let planeNormal = segment.getOutDirection()

    if(startPointDir.dot(planeNormal) < 0)
      startPointDir.negate()
    if(endPointDir.dot(planeNormal) < 0)
      endPointDir.negate()

    // If point directions are completely different directions, ignore them
    if(Math.abs(startPointDir.dot(planeNormal)) < tangentIgnoreThreshold)
      startPointDir = planeNormal.clone()
    if(Math.abs(endPointDir.dot(planeNormal)) < tangentIgnoreThreshold)
      endPointDir = planeNormal.clone()

    // Cast points out onto a plane & find the move distance for each point
    let segMidPoint = segment.getMidpoint()    
    let planePoint = segMidPoint.clone().addScaledVector(planeNormal, distance)

    let startDestPos = MathsUtils.calculate2DLineSegmentPlaneIntersection(segment.startPosition, segment.startPosition.clone().addScaledVector(startPointDir, distance*2), planeNormal, planePoint)
    let endDestPos = MathsUtils.calculate2DLineSegmentPlaneIntersection(segment.endPosition, segment.endPosition.clone().addScaledVector(endPointDir, distance*2), planeNormal, planePoint)

    var startMoveDist = distance
    var endMoveDist = distance

    if(startDestPos)
      startMoveDist = segment.startPosition.distanceTo(startDestPos) * directionScalar
    if(endDestPos)
      endMoveDist = segment.endPosition.distanceTo(endDestPos) * directionScalar

    // Move start & end vertex only & run constraint solver
    this._runSegmentAdjustSolver([{
      segmentIndex: segmentIndex,
      moveDistance: startMoveDist,
      moveDirection: startPointDir,
      isSegmentStart: true,
      solveDirection: -1
    }, {
      segmentIndex: segmentIndex,
      moveDistance: endMoveDist,
      moveDirection: endPointDir,
      isSegmentEnd: true,
      solveDirection: 1
    }])

    this.segmentListUpdated()
    this.updateSceneObject()
  }


  /**
   * Inserts a new point into a segment of the shape
   * Projects to the closest point on the segment
   * Ignored if the point is within x mm's from the ends
   */
  insertSegmentPoint(segmentIndex, closestPoint) {
    if(segmentIndex < 0 || segmentIndex >= this.segmentList.length)
      throw new EditorUserError('Invalid segment')

    var segment = this.segmentList[segmentIndex]
    let minDist = 0.5

    // TODO: Arc

    if(segment.type == GlassDesignerTypes.SegmentTypes.line) {
      var projectedPoint = segment.projectPointOnSegment(new THREE.Vector2(closestPoint.x, closestPoint.y))

      let distToStart = segment.startPosition.distanceTo(projectedPoint)
      let distToEnd = segment.endPosition.distanceTo(projectedPoint)

      if(distToStart < minDist)
        throw new EditorUserError('Insert point too close to segment start point')
      if(distToEnd < minDist)
        throw new EditorUserError('Insert point too close to segment end point')
      
      this.subdivideSegment(segmentIndex, projectedPoint)
    }
  }


  /**
   * Subdivide a segment into 2 segments
   * If subdivision point not set, splits directly in the middle
   */
  subdivideSegment(segmentIndex, subdivisionPoint = null) {
    var segment = this.segmentList[segmentIndex]

    var newSegment = segment.clone()

    if(!subdivisionPoint)
      subdivisionPoint = segment.getMidpoint()

    segment.endPosition.set(subdivisionPoint.x, subdivisionPoint.y)
    segment.endCornerRadius = 0
    
    newSegment.startCornerRadius = 0
    newSegment.startPosition.set(subdivisionPoint.x, subdivisionPoint.y)

    if(segment.type == GlassDesignerTypes.SegmentTypes.arc) {
      segment.arcEndAngle = MathsUtils.calculateArcPointAngle(segment.arcCenterPosition, segment.endPosition)
      newSegment.arcStartAngle = MathsUtils.calculateArcPointAngle(newSegment.arcCenterPosition, newSegment.startPosition)

      segment.arcUpdated()
      newSegment.arcUpdated()
    }

    this.segmentList.splice(segmentIndex+1, 0, newSegment)

    this.segmentListUpdated()
    this.updateSceneObject()
  }


  /**
   * Removes a segment from the shape
   * Fuses the previous & next segments together
   * Throws an error if the segment cannot be removed (eg, shape is 3 segments in size)
   */
  removeSegment(segmentIndex, options = {}) {
    if(segmentIndex < 0 || segmentIndex >= this.segmentList.length)
      throw new EditorUserError('Invalid segment')

    // Shape requires at least 3 line segments, or 2 segments (if at least one is an arc)
    var lineCount = this.segmentList.filter(segment => segment.type == GlassDesignerTypes.SegmentTypes.line).length
    var arcCount = this.segmentList.filter(segment => segment.type == GlassDesignerTypes.SegmentTypes.arc).length

    if(arcCount == 0 && lineCount <= 3)
      throw new EditorUserError('Shape has too few sides to remove a segment')
    if(arcCount > 0 && lineCount <= 1)
      throw new EditorUserError('Shape has too few sides to remove a segment')

    var previousIndex = segmentIndex - 1
    let nextIndex = (segmentIndex + 1) % this.segmentList.length
    if(previousIndex < 0)
      previousIndex += this.segmentList.length
    
    this.segmentList[previousIndex].endPosition = this.segmentList[segmentIndex].endPosition.clone()

    // Reset corner radiuses
    this.segmentList[previousIndex].endCornerRadius = 0
    this.segmentList[nextIndex].startCornerRadius = 0
    
    this.segmentList.splice(segmentIndex, 1)

    // Recalculate arc info
    this.segmentList.forEach((segment, index) => {
      if(segment.type == GlassDesignerTypes.SegmentTypes.arc) {
        let segLength = segment.startPosition.distanceTo(segment.endPosition)

        // Adjusts arc if the arc configuration is no longer valid (eg, arc start/end point was moved)
        if(segment.arcRadius < Math.floor(segLength/2)) {
          segment.arcRadius = segLength/2
        }
        segment.updateArcByRadius(segment.arcRadius)
      }
    })

    if(options.ignoreSegmentListUpdates)
      return
    
    this.segmentListUpdated()
    this.updateSceneObject()
  }


  /**
   * Remove a segment from the shape given a segment ID
   * Fuses the previous & next segments together
   * Throws an error if the segment cannot be removed (eg, shape is 3 segments in size)
   */
  removeSegmentById(id, options = {}) {
    let segmentIndex = this.segmentList.findIndex(segment => segment.id == id)
    if(segmentIndex == -1)
      throw new EditorUserError('Invalid segment')

    this.removeSegment(segmentIndex, options)
  }


  /**
   * Update the corner radius of a segment start vertex
   * Also updates the previous segments endCornerRadius value
   */
  updateSegmentStartCornerRadius(segmentIndex, cornerRadius) {
    let segment = this.getSegmentForIndex(segmentIndex)
    let prevSegment = this.getSegmentForIndex(segmentIndex-1)

    segment.startCornerRadius = MathsUtils.roundNumber(parseFloat(cornerRadius || 0), 1)
    prevSegment.endCornerRadius = MathsUtils.roundNumber(parseFloat(cornerRadius || 0), 1)
  }


  /**
   * Update the corner polished of a segment start vertex
   */
  updateSegmentStartCornerPolished(segmentIndex, cornerPolished) {
    let segment = this.getSegmentForIndex(segmentIndex)

    segment.isStartCornerPolished = !!cornerPolished
  }



  /**
   * Move the shape by a 2D offset vector
   * Moves all segments, holes, groups, etc by the offset vector
   */
  moveShape(offset) {
    // Round the offset to nearest mm to avoid issues with items (eg, split lines) not rounded to nearest mm after moving shapes
    offset.x = Math.round(offset.x)
    offset.y = Math.round(offset.y)

    this.segmentList.forEach(segment => {
      segment.startPosition.add(offset)
      segment.endPosition.add(offset)
      segment.arcCenterPosition.add(offset)

      if(segment.type == GlassDesignerTypes.SegmentTypes.arc)
        segment.arcUpdated()
    })

    this.getCenterCutoutList().forEach(hole => {
      hole.rebuildHoleShape()
    })
    this.getSplitLineList().forEach(splitLine => {
      splitLine.rebuildSplitLine()
    })

    this.segmentListUpdated()
    this.updateSceneObject()
  }


  /**
   * Resize the shape
   * Handled by the actual shape classes to rebuild the shape
   */
  resizeShape() {
    // Note: Custom shapes cannot be resized. Access via the top level shape class
  }


  /**
   * Rebuild the shape
   * Handled by the actual shape classes to rebuild the shape
   */
  reBuildShape(offsetPosition) {
    // Note: Custom shapes cannot be rebuilt. Access via the top level shape class
  }


  /**
   * Rotate the shape by 90 degrees clockwise
   */
  rotateShape() {
    var angleDegrees = 90
    var angleRads = MathsUtils.degreesToRadians(angleDegrees)
    var shapeCenter = this.calculateShapeMidpoint()

    // Rotate segments around center point
    MathsUtils.rotateSegmentList(this.segmentList, shapeCenter, angleRads)

    // Rotate grain
    if(this.isStockUsingGrain()) {
      this.itemData.grainDirection = (this.itemData.grainDirection == StockGrainDirectionTypes.horizontal) ? StockGrainDirectionTypes.vertical : StockGrainDirectionTypes.horizontal
    }

    // Rotate cutouts. Datums will stay the same
    // Fixed to 90 degrees, as the width/height settings are just swapped in rectangles
    this.getCenterCutoutList().forEach(cutout => {
      cutout.rotateHole(angleDegrees)
    })

    // Rotate datum lines
    this.getDatumLineList().forEach(datumLine => {
      datumLine.startPosition = MathsUtils.rotate2DPoint(shapeCenter, datumLine.startPosition, angleRads)
      datumLine.endPosition = MathsUtils.rotate2DPoint(shapeCenter, datumLine.endPosition, angleRads)
      datumLine.updateSceneObject()
    })

    this.internalItemsUpdated()
  }


  /**
   * Mirror a glass shape on X or Y axis
   */
  mirrorShape(mirrorX, mirrorY) {
    var shapeCenter = this.calculateShapeMidpoint()

    // Mirror segments around center point
    this.segmentList = MathsUtils.mirrorSegmentList(this.segmentList, shapeCenter, mirrorX, mirrorY)
    
    // Mirror center cutouts
    this.getCenterCutoutList().forEach(cutout => {
      let worldPoint = cutout.worldPoint.clone()
      worldPoint = MathsUtils.mirrorPoint(worldPoint, shapeCenter, mirrorX, mirrorY)

      cutout.mirrorHole(shapeCenter, mirrorX, mirrorY)

      // Move & recalc new datum links
      this.moveCenterCutout(cutout, worldPoint)
    })

    // Mirror datum lines
    this.getDatumLineList().forEach(datumLine => {
      MathsUtils.mirrorPoint(datumLine.startPosition, shapeCenter, mirrorX, mirrorY)
      MathsUtils.mirrorPoint(datumLine.endPosition, shapeCenter, mirrorX, mirrorY)
      datumLine.updateSceneObject()
    })

    // Mirror panel items
    this.getPanelItemList().forEach(item => {
      let worldPoint = item.worldPoint.clone()
      worldPoint = MathsUtils.mirrorPoint(worldPoint, shapeCenter, mirrorX, mirrorY)

      this.movePanelItem(item, worldPoint)
    })

    // Rejig shape groups to reflect mirrored segments
    // Note: This will lose the shape group boxes, but for the moment this is acceptable
    this.shapeGroupsUpdated()
    
    this.internalItemsUpdated()
  }


  /**
   * Cleanup segments by removing duplicate or redundant segments based on thresholds
   */
  cleanupSegments() {
    let angleThreshold = 0.1   // Degrees
    let duplicateThreshold = 0.5 // mm

    this.segmentList = MathsUtils.optimizeSegmentList(this.segmentList, {
      optimizeDuplicatePoints: true,
      optimizeLines: true,
      optimizeArcs: true,
      angleThreshold: angleThreshold,
      duplicateThreshold: duplicateThreshold
    })

    this.segmentListUpdated()

    // Rejig shape groups to reflect mirrored segments
    // Note: This will lose the shape group boxes, but for the moment this is acceptable
    this.shapeGroupsUpdated()

    this.internalItemsUpdated()
  }


  /**
   * Snap all segment points to a grid
   */
  snapSegmentsToGrid(gridSnapDistance = 1.0) {
    
    MathsUtils.snapSegmentListToGrid(this.segmentList, gridSnapDistance)

    this.segmentListUpdated()

    // Rejig shape groups to reflect mirrored segments
    // Note: This will lose the shape group boxes, but for the moment this is acceptable
    this.shapeGroupsUpdated()
    this.internalItemsUpdated()
  }


  /**
   * Correct rotation of a shape by rotating the segments so the largest bottom segment is horizontal
   */
  correctRotation() {
    // Find suitable bottom segment
    var segBounds = this.calculateSegmentBounds()

    // Find the lowest line segment that is at least 50% of the shape width & is within 15 degrees of horizontal
    var sortedLineList = [...this.segmentList].filter(segment => segment.type == GlassDesignerTypes.SegmentTypes.line)
    sortedLineList.sort((a, b) => b.getMidpoint().y - a.getMidpoint().y)

    var suitableSegment = sortedLineList.find(segment => {
      let segLength = segment.getLength()
      let shapeWidth = segBounds.max.x - segBounds.min.x
      let angleTolerance = 15

      if(segLength < shapeWidth * 0.5)
        return false

      return MathsUtils.angleWithinRangeDegrees(MathsUtils.radiansToDegrees(segment.getAngle()), -angleTolerance, angleTolerance) || MathsUtils.angleWithinRangeDegrees(MathsUtils.radiansToDegrees(segment.getAngle()), 180-angleTolerance, 180+angleTolerance)
    })
    if(!suitableSegment)
      throw new EditorUserError('No suitable bottom line found for rotation correction')
    
    let segAngle = suitableSegment.getAngle()
    
    // Adjust if pointing 180 degrees
    if(Math.abs(segAngle) > Math.PI / 2)
      segAngle = (Math.PI * Math.sign(segAngle)) - segAngle

    let rotateAngleRads = segAngle

    var shapeCenter = this.calculateShapeMidpoint()

    // Rotate segments around center point
    MathsUtils.rotateSegmentList(this.segmentList, shapeCenter, rotateAngleRads)

    // Don't rotate anything else. This is just a correction of segment rotation
    this.segmentListUpdated()

    // Rejig shape groups to reflect mirrored segments
    // Note: This will lose the shape group boxes, but for the moment this is acceptable
    this.shapeGroupsUpdated()
    this.internalItemsUpdated()
  }


  /**
   * Splice the shape by a line
   * Any segments intersecting the line will be subdivided at the intersection point
   */
  spliceLine(lineStartPoint, lineEndPoint) {
    let vertexThreshold = 2   // Don't splice if intersections are near existing vertices
    let pointOnLineThreshold = 0.1

    // Keep looping until no subdivisions occuring
    // Do this as new segments get created that may now be intersecting the line (eg, arcs)
    var subdividedShape = true
    var safety = this.segmentList.length * 2
    while(subdividedShape && safety-- > 0) {
      subdividedShape = false

      for(var i=0; i<this.segmentList.length && !subdividedShape; i++) {
        let segment = this.segmentList[i]

        var intPointList = segment.calculateLineIntersection(lineStartPoint, lineEndPoint)
        
        // Check datum start/end points that connect directly to the segment
        let startPointOnLine = segment.projectPointOnSegment(lineStartPoint)
        let endPointOnLine = segment.projectPointOnSegment(lineEndPoint)

        if(startPointOnLine.distanceTo(lineStartPoint) <= pointOnLineThreshold)
          intPointList.push(startPointOnLine)
        if(endPointOnLine.distanceTo(lineEndPoint) <= pointOnLineThreshold)
          intPointList.push(endPointOnLine)

        for(var intPoint of intPointList) {

          // If near a segment vertex, ignore it
          if(intPoint.distanceTo(segment.startPosition) <= vertexThreshold || intPoint.distanceTo(segment.endPosition) <= vertexThreshold)
            continue

          this.subdivideSegment(i, intPoint)
          subdividedShape = true
          break
        }
      }
    }
  }


  /**
   * Cut the shape by a plane
   * All parts of the shape on the inside are kept
   * 
   * Throws an error if the cut would create an invalid shape
   */
  cutShapeByPlane(planeNormal, coPlanarPoint, options = {}) {
    let bounds = this.calculateSegmentBounds()
    let boundMaxSize = Math.max(bounds.max.x - bounds.min.x, bounds.max.y - bounds.min.y) * 2.0

    var cuttingNormal = planeNormal.clone().negate()

    // Check if any center cutouts will be outside the shape and flag an error
    this.getCenterCutoutList().forEach(cutout => {
      if(MathsUtils.calculateDistanceToPlane2D(new THREE.Vector2(cuttingNormal.x, cuttingNormal.y), new THREE.Vector2(coPlanarPoint.x, coPlanarPoint.y), cutout.worldPoint) >= 0) {

        // If requested, just remove the center cutout on the other side of the plane
        // Otherwise throw an error
        if(options.removeCenterCutouts) {
          this.removeCenterCutout(cutout)

        } else {
          throw new EditorUserError('Center cutouts exist outside cutting area')
        }
      }
    })


    // Generate a rectangle for the cutting plane & use the clipper lib
    let planeRight = new THREE.Vector2(cuttingNormal.y, -cuttingNormal.x)
    let clipRectPntA = coPlanarPoint.clone().addScaledVector(planeRight, boundMaxSize)
    let clipRectPntB = coPlanarPoint.clone().addScaledVector(planeRight, -boundMaxSize)
    let clipRectPntC = clipRectPntB.clone().addScaledVector(cuttingNormal, boundMaxSize)
    let clipRectPntD = clipRectPntA.clone().addScaledVector(cuttingNormal, boundMaxSize)

    // Make sure it's clockwise
    var clipPointList = [clipRectPntA, clipRectPntB, clipRectPntC, clipRectPntD]
    if(MathsUtils.get2DPolygonOrientation(clipPointList) > 0)
      clipPointList.reverse()

    var clipSegmentList = [
      new GlassSegment(GlassDesignerTypes.SegmentTypes.line, { startPosition: clipPointList[0], endPosition: clipPointList[1] }),
      new GlassSegment(GlassDesignerTypes.SegmentTypes.line, { startPosition: clipPointList[1], endPosition: clipPointList[2] }),
      new GlassSegment(GlassDesignerTypes.SegmentTypes.line, { startPosition: clipPointList[2], endPosition: clipPointList[3] }),
      new GlassSegment(GlassDesignerTypes.SegmentTypes.line, { startPosition: clipPointList[3], endPosition: clipPointList[0] })
    ]


    let clipper = new EditorClipper()
    let clipResult = clipper.performSegmentListClippingOperation(this.segmentList, clipSegmentList, false)
    if(clipResult.segmentList.length == 0)
      throw new EditorUserError('Could not cut the shape')

    // TEMP: Disabled while testing
    //this.checkSegmentListValidShape(clipResult.segmentList)

    // Apply cut segments to shape
    this.segmentList = clipResult.segmentList

    this.segmentListUpdated()
    this.updateSceneObject()

    return true
  }


  /**
   * Clip the shape by a line
   * Parts on the keep normal direction will be kept & the other parts will be removed
   */
  clipShapeByLine(lineStartPoint, lineEndPoint, keepNormalDir) {

    // Run intersection check & grab the 2 furthest intersection lines for clipping
    let intersections = this.calculateIntersectionWithLine(lineStartPoint, lineEndPoint, { ignoreDatumLines: true, ignoreSplitLines: true })
    if(intersections.length < 2)
      throw new EditorUserError('Clipping line does not intersect with the shape')

    lineStartPoint = intersections[0]
    lineEndPoint = intersections[intersections.length - 1]

    // Swap points if right dir is opposite the keep normal dir
    var dir = lineEndPoint.clone().sub(lineStartPoint)
    dir.normalize()

    let right = new THREE.Vector2(dir.y, -dir.x)
    if(right.dot(keepNormalDir) > 0) {
      var tmp = lineStartPoint.clone()
      lineStartPoint = lineEndPoint
      lineEndPoint = tmp
    }

    let clipper = new EditorClipper()
    let clipResult = clipper.performLineClippingOperation(this.segmentList, lineStartPoint, lineEndPoint)
    if(clipResult.segmentList.length == 0)
      throw new EditorUserError('Could not cut the shape')

    // TEMP: Disabled while testing
    //this.checkSegmentListValidShape(clipResult.segmentList)

    // Apply cut segments to shape
    this.segmentList = clipResult.segmentList

    this.segmentListUpdated()
    this.updateSceneObject()

    return true
  }


  /**
   * Expand/Shrink the shape by a distance in each axis
   * Positive distance expands outwards, negative distance shrinks inwards
   */
  expandShape(expandX, expandY) {
    // Half to apply in each direction
    var halfX = expandX / 2.0
    var halfY = expandY / 2.0

    var shapeCenter = this.calculateShapeMidpoint()

    let _expandPosition = (position) => {
      if(position.x >= shapeCenter.x)
        position.x += halfX
      else
        position.x -= halfX
      if(position.y >= shapeCenter.y)
        position.y += halfY
      else
        position.y -= halfY
    }

    // Move each segment point outwards/inwards based on its normal direction
    this.segmentList.forEach(segment => {
      _expandPosition(segment.startPosition)
      _expandPosition(segment.endPosition)

      if(segment.type == GlassDesignerTypes.SegmentTypes.arc) {
        _expandPosition(segment.arcCenterPosition)

        segment.arcStartAngle = MathsUtils.calculateArcPointAngle(segment.arcCenterPosition, segment.startPosition)
        segment.arcEndAngle = MathsUtils.calculateArcPointAngle(segment.arcCenterPosition, segment.endPosition)
        segment.arcUpdated()
      }
    })

    this.getCenterCutoutList().forEach(hole => {
      hole.rebuildHoleShape()
    })
    this.getSplitLineList().forEach(splitLine => {
      splitLine.rebuildSplitLine()
    })

    this.segmentListUpdated()

    // Update shape groups & internal items
    this.updateSceneObject()
  }



  /**
   * Check if a shape using a list of segments is valid
   * Ensures the shape does not self intersect, use counterclockwise winding or is not contiguous
   * 
   * Throws an exception with the details if the segment list represents an invalid shape
   */
  checkSegmentListValidShape(testSegmentList, testHoleList) {
    var polygonPointList = []

    testSegmentList.forEach(segment => {
      polygonPointList.push(segment.startPosition)

      // Add in 1/3 + 2/3 points of the arc
      // Allows us to have a valid shape with only a couple segments (if they're arcs)
      if(segment.type == GlassDesignerTypes.SegmentTypes.arc) {
        polygonPointList.push(segment.getPoint(0.33))
        polygonPointList.push(segment.getPoint(0.66))
      }
    })

    if(polygonPointList.length < 3)
      throw new EditorUserError('Shape is invalid')

    // Check orientation
    // Must be clockwise
    let orientation = MathsUtils.get2DPolygonOrientation(polygonPointList)
    if(orientation >= 0)
      throw new EditorUserError('Shape is invalid')

    // Check for intersecting segments
    // Ignores neighbouring segments
    for(var i=0; i<polygonPointList.length; i++) {
      let startPoint = polygonPointList[i]
      let endPoint = polygonPointList[(i+1) % polygonPointList.length]

      for(var n=i+2; n<polygonPointList.length; n++) {
        let otherStartPoint = polygonPointList[n]
        let otherEndPoint = polygonPointList[(n+1) % polygonPointList.length]

        // Ignore start/end segments
        if(i == 0 && n == polygonPointList.length-1)
          continue

        if(MathsUtils.checkSegmentSegmentIntersection(startPoint, endPoint, otherStartPoint, otherEndPoint)) {
          throw new EditorUserError('Shape intersects with itself')
        }
      }
    }

    return true
  }


  /**
   * Calculate the closest point on a segment to a given point
   * Includes options for calculating closest edge. Options include:
   *    segmentPointOnly - If set, will only use the segment's start point and ignore the segment edge
   *    holeRefPointOnly - If set, will only use the 9 reference points of a hole instead of all edges along the hole bounding box
   *    ignoreDatumLines - If set, will ignore datum lines in the shape
   *    ignoreSplitLines - If set, will ignore split lines in the shape
   *    ignoreHoleId - If set, ignores a specific center cutout hole given an id
   *    ignoreHoles - If set, ignores all center cutout holes
   * 
   * Returns an object with details about the closest item. Object includes:
   *    point - Closest point
   *    distance - Distance from given point to closest point
   *    segmentId - If set, the closest point is a segment & this contains the ID of the segment
   *    datumLineId - If set, the closest point is a datum line & this contains the ID of the datum line
   *    holeId - If set, the closest point is a hole & this contains the ID of the hole
   *    holeHorzType - If a hole and holeRefPointOnly true, this will be the horizontal measurement type of the closest point on the hole
   *    holeVertType - If a hole and holeRefPointOnly true, this will be the vertical measurement type of the closest point on the hole
   *    
   * Returns null if nothing is close to the point
   */
  calculateClosestEdgePoint(point, options = {}) {
    var closestDist = 0
    var closestPoint = null
    var closestInfo = null

    // Shape Segments
    this.segmentList.forEach(segment => {
      var nearestPnt = null
      if(options.segmentPointOnly)
        nearestPnt = segment.startPosition.clone()
      else
        nearestPnt = segment.projectPointOnSegment(point)

      let dist = nearestPnt.distanceTo(point)
      if(closestPoint == null || dist < closestDist) {
        closestDist = dist
        closestPoint = nearestPnt

        closestInfo = {
          point: nearestPnt,
          distance: dist,
          segmentId: segment.id
        }
      }
    })

    // Datum lines
    if(!options.ignoreDatumLines) {
      this.getDatumLineList().forEach(datumLine => {
        var nearestPnt = datumLine.projectPointOnDatumLine(point)
        let dist = nearestPnt.distanceTo(point)
        if(closestPoint == null || dist < closestDist) {
          closestDist = dist
          closestPoint = nearestPnt

          closestInfo = {
            point: nearestPnt,
            distance: dist,
            datumLineId: datumLine.id
          }
        }
      })
    }

    // Split lines
    if(!options.ignoreSplitLines) {
      this.getSplitLineList().forEach(splitLine => {
        var nearestPnt = splitLine.projectPointOnSplitLine(point)
        let dist = nearestPnt.distanceTo(point)
        if(closestPoint == null || dist < closestDist) {
          closestDist = dist
          closestPoint = nearestPnt

          closestInfo = {
            point: nearestPnt,
            distance: dist,
            splitLineId: splitLine.id
          }
        }
      })
    }

    // Holes
    if(!options.ignoreHoles) {
      this.getCenterCutoutList().forEach(hole => {
        if(hole.id == options.ignoreHoleId)
          return

        let holeBounds = hole.calculateSegmentBounds()

        if(options.holeRefPointOnly) {
          let nearestPnt = MathsUtils.calculateClosestBoxReferencePointToPosition2D(holeBounds, point)
          let dist = nearestPnt.distanceTo(point)
          if(closestPoint == null || dist < closestDist) {
            closestDist = dist
            closestPoint = nearestPnt

            closestInfo = {
              point: nearestPnt,
              distance: dist,
              holeId: hole.id,
              holeHorzType: MathsUtils.calculateHoleMeasureType(holeBounds, point, true),
              holeVertType: MathsUtils.calculateHoleMeasureType(holeBounds, point, false)
            }
          }
        } else {
          let nearestPnt = MathsUtils.calculateClosestBoxEdgePointToPosition2D(holeBounds, point)
          let dist = nearestPnt.distanceTo(point)
          if(closestPoint == null || dist < closestDist) {
            closestDist = dist
            closestPoint = nearestPnt

            closestInfo = {
              point: nearestPnt,
              distance: dist,
              holeId: hole.id
            }
          }
        }
      })
    }

    return closestInfo
  }


  /**
   * Calculate all intersections between a line (given a start & end point) and the shape edges
   * Sorts the intersections by distance along the line from the start
   * Uses options to control what is included in the intersections. Options include:
   *    ignoreDatumLines - If set, will ignore datum lines
   *    ignoreSplitLines - If set, will ignore split lines
   *    ignoreSplitLineIdList - If set, array of IDs for split lines to ignore
   * 
   * Returns an array of Vector2 objects
   */
  calculateIntersectionWithLine(lineStartPoint, lineEndPoint, options = {}) {
    let pointOnLineTolerance = 0.01
    var intersectionList = []

    // Segments
    this.segmentList.forEach(segment => {
      var intPointList = segment.calculateLineIntersection(lineStartPoint, lineEndPoint)
      // If no points intersecting, check if the start/end points are resting on the segment itself
      if(intPointList.length == 0) {
        let startProj = segment.projectPointOnSegment(lineStartPoint)
        let endProj = segment.projectPointOnSegment(lineEndPoint)

        if(startProj.distanceTo(lineStartPoint) < pointOnLineTolerance)
          intPointList.push(lineStartPoint.clone())
        if(endProj.distanceTo(lineEndPoint) < pointOnLineTolerance)
          intPointList.push(lineEndPoint.clone())
      }

      intPointList.forEach(intPoint => {
        intersectionList.push(intPoint)
      })
    })

    // Datum lines
    if(!options.ignoreDatumLines) {
      this.getDatumLineList().forEach(datumLine => {
        let intPoint = datumLine.calculateLineIntersection(lineStartPoint, lineEndPoint)

        // If no points intersecting, check if the start/end points are resting on the datum line itself
        if(!intPoint) {
          let startProj = datumLine.projectPointOnDatumLine(lineStartPoint)
          let endProj = datumLine.projectPointOnDatumLine(lineEndPoint)

          if(startProj.distanceTo(lineStartPoint) < pointOnLineTolerance)
            intersectionList.push(lineStartPoint.clone())
          if(endProj.distanceTo(lineEndPoint) < pointOnLineTolerance)
            intersectionList.push(lineEndPoint.clone())
        }
        
        if(intPoint) {
          intersectionList.push(intPoint)
        }
      })
    }

    // Split lines
    if(!options.ignoreSplitLines) {
      this.getSplitLineList().forEach(splitLine => {
        if(_.isArray(options.ignoreSplitLineIdList)) {
          if(options.ignoreSplitLineIdList.indexOf(splitLine.id) != -1)
            return
        }

        let intPoint = splitLine.calculateLineIntersection(lineStartPoint, lineEndPoint)

        // If no points intersecting, check if the start/end points are resting on the split line itself
        if(!intPoint) {
          let startProj = splitLine.projectPointOnSplitLine(lineStartPoint)
          let endProj = splitLine.projectPointOnSplitLine(lineEndPoint)

          if(startProj.distanceTo(lineStartPoint) < pointOnLineTolerance)
            intersectionList.push(lineStartPoint.clone())
          if(endProj.distanceTo(lineEndPoint) < pointOnLineTolerance)
            intersectionList.push(lineEndPoint.clone())
        }

        if(intPoint) {
          intersectionList.push(intPoint)
        }
      })
    }

    
    // Sort the list by distance
    intersectionList.sort((pntA, pntB) => {
      return pntA.distanceTo(lineStartPoint) - pntB.distanceTo(lineStartPoint)
    })

    return intersectionList
  }


  /**
   * Calculate closest segment to a point
   * Returns segment index of the closest segment
   * Returns -1 if no segments are in the shape
   */
  calculateClosestSegmentToPoint(point, ignoreSegmentList = []) {
    var closestDist = 0
    var closestSegmentIndex = -1

    this.segmentList.forEach((segment, segmentIndex) => {
      if(ignoreSegmentList.indexOf(segmentIndex) != -1)
        return
        
      var nearestPnt = segment.projectPointOnSegment(point)
      var dist = nearestPnt.distanceTo(point)

      if(closestSegmentIndex == -1 || dist < closestDist) {
        closestDist = dist
        closestSegmentIndex = segmentIndex
      }
    })

    return closestSegmentIndex
  }


  /**
   * Calculate closest segment start point to a point
   * Returns segment index of the closest segment
   * Returns -1 if no segments are in the shape
   */
  calculateClosestSegmentStartToPoint(point, ignoreSegmentList = [], maxDistance = -1) {
    var closestDist = 0
    var closestSegmentIndex = -1

    this.segmentList.forEach((segment, segmentIndex) => {
      if(ignoreSegmentList.indexOf(segmentIndex) != -1)
        return
        
      var nearestPnt = segment.startPosition
      var dist = nearestPnt.distanceTo(point)

      // Ignore if outside max distance
      if(maxDistance >= 0 && dist > maxDistance)
        return

      if(closestSegmentIndex == -1 || dist < closestDist) {
        closestDist = dist
        closestSegmentIndex = segmentIndex
      }
    })

    return closestSegmentIndex
  }


  /**
   * Check if GlassSegment object intersects with this shape segments
   * Returns a Vector2 of the first intersection point found
   * Returns null if no intersection occured
   */
  checkGlassSegmentIntersection(segment) {
    for(var shapeSegment of this.segmentList) {
      let intersectionList = shapeSegment.calculateGlassSegmentIntersection(segment)
      if(intersectionList.length > 0)
        return intersectionList[0]
    }

    return null
  }


  /**
   * Check if a 2D point is inside the shape polygon
   * Note: May not provide accurate results if the point is on a segment line
   * Note: Does not currently support arcs
   * 
   * Returns true if the point is inside
   */
  isPointInsideShape(point) {
    return MathsUtils.pointIn2DPolygon(point, this.cachedPointList)
  }


  /**
   * Calculate the shape pricing type
   * Returns an ID of the OrderEditorShapePriceType record
   * Returns null if not found
   */
  calculateShapePriceTypeId() {
    
    var cuttingLineList = this.calculateCuttingLineList()
    let totalLinesCount = cuttingLineList.length
    let straightLineCutCount = cuttingLineList.filter(cutLine => !cutLine.isOnBounds).length

    // Calculate perfect arc count
    // If multiple perfect arcs are consecutive (eg, circle), consider that a single arc until broken up by a line segment
    var perfectArcCount = this.calculatePerfectArcCount()

    // Go through all the shape price type records. Sort by lowest value first
    let shapePriceTypeList = _.cloneDeep(this.editor.getEditorConfigurationData().shapePriceTypeList || [])

    // Sort by lowest values first
    shapePriceTypeList.sort((shapeTypeA, shapeTypeB) => {
      let edgeCountDiff = shapeTypeA.straightEdgeCountLimit - shapeTypeB.straightEdgeCountLimit
      let straightLineCutDiff = shapeTypeA.straightLineCutCountLimit - shapeTypeB.straightLineCutCountLimit
      let perfectArcDiff = shapeTypeA.perfectArcCountLimit - shapeTypeB.perfectArcCountLimit

      if(edgeCountDiff != 0)
        return edgeCountDiff
      if(straightLineCutDiff != 0)
        return straightLineCutDiff
      return perfectArcDiff
    })

    let validShapeTypes = shapePriceTypeList.filter(shapeType => shapeType.isActive)
    let shapePriceType = validShapeTypes.find(shapeType => {
      if(totalLinesCount > shapeType.straightEdgeCountLimit)
        return false
      if(straightLineCutCount > shapeType.straightLineCutCountLimit)
        return false
      if(perfectArcCount > shapeType.perfectArcCountLimit)
        return false
      return true      
    })

    if(shapePriceType)
      return shapePriceType.id

    return null
  }


  /**
   * Calculate the cutting plane point list
   * Calculates the minimal number of cutting planes required to cutout the convex hull of the shape
   * Interior cuts will be calculated based on the cutting shape
   * 
   * Returns an array of objects representing the cutting lines & details
   */
  calculateCuttingLineList() {
    let normalThreshold = 0.0001
    let midDistThreshold = 0.1
    let maxDistToBounds = parseInt(this.editor.getEditorConfigurationData().orderEditorSettings[AccountSettingKeys.orderEditorMaxSnappingCutLineSize] || 150)

    var hullPoints = EditorHullUtils.generateSegmentListConvexHull(this.segmentList)
    if(MathsUtils.is2DPolygonClosed(hullPoints))
      hullPoints.pop()
    if(MathsUtils.get2DPolygonOrientation(hullPoints) > 0)
      hullPoints.reverse()

    if(hullPoints.length <= 2)
      return []
    
    var bounds = MathsUtils.calculate2DPointListBounds(hullPoints)
    let maxCastSize = Math.max(bounds.max.x - bounds.min.x, bounds.max.y - bounds.min.y) * 2.0

    var boundPlanes = [
      { normal: new THREE.Vector2(0, -1), point: bounds.min.clone() },
      { normal: new THREE.Vector2(1, 0), point: bounds.max.clone() },
      { normal: new THREE.Vector2(0, 1), point: bounds.max.clone() },
      { normal: new THREE.Vector2(-1, 0), point: bounds.min.clone() },
    ]
    var maxConfiguredDistPlanes = []
    if(maxDistToBounds > 0) {
      maxConfiguredDistPlanes = [
        { normal: new THREE.Vector2(0, -1), point: bounds.min.clone().addScaledVector(new THREE.Vector2(0, -1), maxDistToBounds) },
        { normal: new THREE.Vector2(1, 0), point: bounds.max.clone().addScaledVector(new THREE.Vector2(1, 0), maxDistToBounds) },
        { normal: new THREE.Vector2(0, 1), point: bounds.max.clone().addScaledVector(new THREE.Vector2(0, 1), maxDistToBounds) },
        { normal: new THREE.Vector2(-1, 0), point: bounds.min.clone().addScaledVector(new THREE.Vector2(-1, 0), maxDistToBounds) },
      ]
    }

    // All segments on the hull have to have cutting planes
    var cutLineList = []
    var shapeLineSegList = this.segmentList.filter(segment => segment.type == GlassDesignerTypes.SegmentTypes.line)

    // Only lines get straight line cuts
    shapeLineSegList.forEach(segment => {
      // Check if on the hull outline
      let segMidPoint = segment.getMidpoint()
      let segDir = segment.getDirection()
      let segLength = segment.getLength()

      var isOnHull = false
      for(var j=0; j<hullPoints.length && !isOnHull; j++) {
        let hullPnt1 = hullPoints[j]
        let hullPnt2 = hullPoints[(j+1) % hullPoints.length]

        isOnHull = MathsUtils.point2DOnLineSegment(hullPnt1, hullPnt2, segMidPoint, 0.1) || isOnHull
      }

      // Add all cutting planes
      if(isOnHull) {
        var isOnBounds = false
        var dirThreshold = 0.90
        var boundsDistThreshold = 0.5
        var endpointDistThreshold = 0.75
        var colinearDirThreshold = 0.90

        // Check if on the bounds
        if(Math.abs(segDir.dot(new THREE.Vector2(1,0))) > dirThreshold) {
          if(Math.abs(segMidPoint.y - bounds.min.y) < boundsDistThreshold || Math.abs(segMidPoint.y - bounds.max.y) < boundsDistThreshold)
            isOnBounds = true
        }
        if(Math.abs(segDir.dot(new THREE.Vector2(0,1))) > dirThreshold) {
          if(Math.abs(segMidPoint.x - bounds.min.x) < boundsDistThreshold || Math.abs(segMidPoint.x - bounds.max.x) < boundsDistThreshold)
            isOnBounds = true
        }

        // Extend out to the bounds
        var start = segment.startPosition.clone()
        var end = segment.endPosition.clone()

        var castStart = start.clone().addScaledVector(segDir, -maxCastSize)
        var castEnd = end.clone().addScaledVector(segDir, maxCastSize)

        // If this cutting line cuts into parts of the shape, do not use it
        // See splashback order for Order #42820 on live. Segment is on hull on left side, but cutting line cuts into far right part of splashback
        // Need to ensure intersections are away from co-planar points along lines as it can run flush against a segment
        // Also need to ensure intersections are away from any endpoints touching the cast line
        var intersectionPoint = new THREE.Vector2()
        let hasSelfIntersection = shapeLineSegList.some(otherSeg => {
          if(otherSeg.id == segment.id)
            return false

          let otherSegDir = otherSeg.getDirection()

          // Ignore segments that are co-linear
          if(Math.abs(segDir.dot(otherSegDir)) > colinearDirThreshold)
            return false

          // Check intersection
          // Intersection must be away from endpoints of cast line & the other segment
          if(MathsUtils.checkSegmentSegmentIntersection(castStart, castEnd, otherSeg.startPosition, otherSeg.endPosition, intersectionPoint, endpointDistThreshold)) {
            return true
          }

          return false
        })

        if(hasSelfIntersection)
          return

        cutLineList.push({
          id: uuidv4(),
          isOnBounds: isOnBounds,
          segDir: segDir,
          planeNormal: new THREE.Vector2(segDir.y, -segDir.x),  // Facing out
          segMidPoint: segMidPoint,
          segLength: segLength,
          startPosition: start,
          endPosition: end,
          castStart: castStart,
          castEnd: castEnd,
          srcSegments: [segment]
        })
      }
    })

    var appliedCutList = []

    let applyCutLine = (cutList, cutIndex, cutLine) => {
      // Cut the new line by all the existing cutting planes. Keep the inside half
      var lineStart = cutLine.castStart.clone()
      var lineMid = cutLine.segMidPoint.clone()
      var lineEnd = cutLine.castEnd.clone()

      var hasStartClip = false
      var hasEndClip = false

      // Force clipping of cutting lines by the bounds of the shape
      // JB: 12/07/2024
      // Really don't know why this wasn't done originally. 
      // The original idea (before i knew how cutting table works) was to intersect 2 cutting lines at some point (up to a maximum of 'orderEditorMaxSnappingCutLineSize' setting) outside of the bounds to help with snapping
      // But the cutting tables always cut the bounds rectangle anyway, so there is zero need for 2 cutting lines to intersect. They can just hit the bounds rectangle & be fine
      // Without this, some cutting lines extended out beyond bounds & resulted it extra charges for panel / edgework sizes for no reason. Still cut / waterjetted the same final result
      var forceBoundsClip = true

      // Run cut against all other cutting lines
      cutLineList.forEach((otherCut, index) => {
        if(index == cutIndex)
          return

        // Ignore cutting lines on the same plane
        let matchingPlanes = MathsUtils.comparePlanes(cutLine.planeNormal, cutLine.segMidPoint, otherCut.planeNormal, otherCut.segMidPoint, normalThreshold, midDistThreshold)
        if(matchingPlanes)
          return

        // Shrink by all cuts
        var endClip = MathsUtils.calculate2DLineSegmentPlaneIntersection(lineMid, lineEnd, otherCut.planeNormal, otherCut.segMidPoint)
        if(endClip) {
          hasEndClip = true
          lineEnd = endClip
        }

        var startClip = MathsUtils.calculate2DLineSegmentPlaneIntersection(lineStart, lineMid, otherCut.planeNormal, otherCut.segMidPoint)
        if(startClip) {
          hasStartClip = true
          lineStart = startClip
        }
      })

      // If the line wasn't clipped on a side at all (or force bounds clip is set), clip by the bounds
      if(!hasStartClip || forceBoundsClip) {
        boundPlanes.forEach(boundPlane => {
          let startClip = MathsUtils.calculate2DLineSegmentPlaneIntersection(lineStart, lineMid, boundPlane.normal, boundPlane.point)
          if(startClip) {
            hasStartClip = true
            lineStart = startClip
          }
        })
      }
      if(!hasEndClip || forceBoundsClip) {
        boundPlanes.forEach(boundPlane => {
          let endClip = MathsUtils.calculate2DLineSegmentPlaneIntersection(lineMid, lineEnd, boundPlane.normal, boundPlane.point)
          if(endClip) {
            hasEndClip = true
            lineEnd = endClip
          }
        })
      }

      if(!hasStartClip || !hasEndClip)
        return

      // Limit by the maximum distance planes
      // Note: Not required if force bounds clip is set
      maxConfiguredDistPlanes.forEach(maxBoundPlane => {
        let startClip = MathsUtils.calculate2DLineSegmentPlaneIntersection(lineStart, lineMid, maxBoundPlane.normal, maxBoundPlane.point)
        if(startClip)
          lineStart = startClip
      })
      maxConfiguredDistPlanes.forEach(maxBoundPlane => {
        let endClip = MathsUtils.calculate2DLineSegmentPlaneIntersection(lineMid, lineEnd, maxBoundPlane.normal, maxBoundPlane.point)
        if(endClip)
          lineEnd = endClip
      })

      // If the line is too short, do not add it
      if(lineStart.distanceTo(lineEnd) < 0.001)
        return

      cutList.push({
        id: cutLine.id,
        isOnBounds: cutLine.isOnBounds,
        segDir: cutLine.segDir,
        planeNormal: cutLine.planeNormal,
        segMidPoint: cutLine.segMidPoint,
        startPosition: lineStart,
        endPosition: lineEnd,
        castStart: cutLine.castStart,
        castEnd: cutLine.castEnd,
        srcSegments: cutLine.srcSegments
      })
    }
    
    // Apply cuts (largest first) against all other cuts
    // Sort by largest first
    cutLineList.sort((lineA, lineB) => {
      return lineB.segLength - lineA.segLength
    })
    cutLineList.forEach((cutLine, index) => {
      applyCutLine(appliedCutList, index, cutLine)
    })

    // Remove duplicates
    // Merge source segments into the matching cutting line
    appliedCutList = appliedCutList.reduce((list, cut) => {
      const existingCut = list.find(existing =>
        existing.isOnBounds === cut.isOnBounds &&
        MathsUtils.check2DPointsEqual(existing.startPosition, cut.startPosition) &&
        MathsUtils.check2DPointsEqual(existing.endPosition, cut.endPosition)
      )

      if (existingCut) {
        existingCut.srcSegments = [
          ...(existingCut.srcSegments || []),
          ...(cut.srcSegments || [])
        ]
      } else {
        list.push({
          ...cut,
          srcSegments: [...(cut.srcSegments || [])]
        })
      }

      return list
    }, [])

    // Merge duplicate cut lines that exist on the same cutting planes
    var outputCutList = []
    appliedCutList.forEach(cutLine => {
      let existingLine = outputCutList.find(existingLine => {

        return MathsUtils.comparePlanes(cutLine.planeNormal, cutLine.segMidPoint, existingLine.planeNormal, existingLine.segMidPoint, normalThreshold, midDistThreshold)
      })
      if(!existingLine) {
        outputCutList.push(cutLine)
      } else {
        // If an existing line exists, update some overriding details
        if(existingLine.isOnBounds && !cutLine.isOnBounds)
          existingLine.isOnBounds = false

        existingLine.srcSegments = _.concat(existingLine.srcSegments, cutLine.srcSegments)
      }
    })

    // TODO: May need to extend cutting lines all the way out to the newly calculated bounds from cut list

    return outputCutList
  }


  /**
   * Calculate the shaped cuts for the cutting process
   * These are the shaped cuts on the shape outline before the CNC cutout process
   * This does not include the straight line cuts (which are special, edge-to-edge snapping cuts), as such this will return almost always arcs
   * 
   * @param {Array<Object>} cncCutoutList - List of CNC based cutouts 
   * 
   * Returns an array of GlassSegment objects for each shaped cut. The list may be non-contiguous paths
   */
  calculateCuttingShapedList(cncCutoutList) {
    let outputShapedCutList = []
    let proxTestThreshold = 0.1      

    // Go through the arc segments of the shape.
    // For any arc on the convex hull & not part of the CNC cutout list, include it
    // Note: Corner radiuses are now dealt with in edgeworkCutting list not here in shaped cuttings

    // Recalculate the edgework availability flags for segments
    var hullPoints = EditorHullUtils.generateSegmentListConvexHull(this.segmentList)
    if(MathsUtils.is2DPolygonClosed(hullPoints))
      hullPoints.pop()
    if(MathsUtils.get2DPolygonOrientation(hullPoints) > 0)
      hullPoints.reverse()

    this.segmentList.forEach(segment => {
      if(segment.type != GlassDesignerTypes.SegmentTypes.arc)
        return
      
      // Take 3 points of the arc (25%, 50%, 75%)
      // If any of these points are on or near a part of the convex hull, it is a shaped cut
      let segPointA = segment.getPoint(0.25)
      let segPointB = segment.getPoint(0.50)
      let segPointC = segment.getPoint(0.75)

      let isOnHull = false
      for(let j=0; j<hullPoints.length && !isOnHull; j++) {
        let hullPnt1 = hullPoints[j]
        let hullPnt2 = hullPoints[(j+1) % hullPoints.length]

        isOnHull = MathsUtils.point2DOnLineSegment(hullPnt1, hullPnt2, segPointA, proxTestThreshold) || isOnHull
        isOnHull = MathsUtils.point2DOnLineSegment(hullPnt1, hullPnt2, segPointB, proxTestThreshold) || isOnHull
        isOnHull = MathsUtils.point2DOnLineSegment(hullPnt1, hullPnt2, segPointC, proxTestThreshold) || isOnHull
      }

      if(!isOnHull)
        return

      // Make sure it is not part of the cnc cutout list
      let matchingCutout = cncCutoutList.find(cutout => {
        let cutoutArcs = cutout.pathSegmentList.filter(cutoutSeg => cutoutSeg.type == GlassDesignerTypes.SegmentTypes.arc)

        // Check if middle point of the arc is on the cutout arc
        let matchingArc = cutoutArcs.find(cutoutArc => {
          let cutoutArcPnt = cutoutArc.projectPointOnSegment(segPointB, false)
          if(cutoutArcPnt.distanceTo(segPointB) < proxTestThreshold)
            return true
        })
        if(matchingArc)
          return true

        return false
      })
      if(matchingCutout)
        return

      outputShapedCutList.push(segment)
    })

    return outputShapedCutList
  }


  /**
   * Calculate the size of glass required to begin cutting
   * Based on cutting lines for snaps
   * NOTE: Does NOT account for optimisation guidelines (trim, etc)
   * 
   * Returns a Box2 object with bounds set
   */
  calculateGlassCutBounds() {
    var cuttingLineList = this.calculateCuttingLineList()
    var outerBounds = this.calculateSegmentBounds()

    // Exclude bound cuts
    cuttingLineList = cuttingLineList.filter(cutLine => !cutLine.isOnBounds)
    cuttingLineList.forEach(cutLine => {
      outerBounds.expandByPoint(cutLine.startPosition)
      outerBounds.expandByPoint(cutLine.endPosition)
    })

    return outerBounds
  }


  /**
   * Calculating the final cutting and edgework details
   * Accounts for edgework allowances
   * Used by the optimiser / cutting processes / edgework processes
   * 
   * Returns a list of objects containing fields same as GlassSegment with additional edgework details
   * Segments returned are a loose array of segments and will not be a contigous path
   */
  calculateCuttingEdgeworkList() {
    let minCuttingCornerRadius = parseInt(this.editor.getEditorConfigurationData().orderEditorSettings[AccountSettingKeys.orderEditorMinCuttingCornerRadius] || 5)

    var cncCutoutList = this.calculateCNCCutoutList()
    var shapedCuts = this.calculateCuttingShapedList(cncCutoutList)
    var edgeworkEdgeList = this.calculateEdgeworkEdgeList()
    var shapeBounds = this.calculateGlassCutBounds()

    // Copy segment list, fill in edgework details & convert corner radiuses to arcs
    var outputSegments = []
    this.segmentList.map((segment, segmentIndex) => {
      let outputSeg = segment.clone()
      outputSeg.hasEdgework = false
      outputSeg.isCuttingSegment = false
      outputSeg.isBoundarySegment = false
      outputSeg.boundaryIndex = 0

      // Find edgework info for the segment (if any exists)
      let edgeworkInfo = edgeworkEdgeList.segmentList.find(edgeSegment => edgeSegment.segmentId == segment.id)
      if(edgeworkInfo) {
        outputSeg.hasEdgework = true
        outputSeg.orderEditorEdgeworkConfigId = edgeworkInfo.orderEditorEdgeworkConfigId
        outputSeg.edgeworkStockId = edgeworkInfo.edgeworkStockId
        outputSeg.edgeworkColour = edgeworkInfo.highlightColour
        outputSeg.edgeworkAllowance = edgeworkInfo.edgeworkAllowance
        outputSeg.edgeworkValue = edgeworkInfo.edgeworkValue
        outputSeg.edgeworkFeedHeight = edgeworkInfo.edgeworkFeedHeight
      }

      // Straight line cuts
      if(segment.type == GlassDesignerTypes.SegmentTypes.line) {
        // Convert line start corner radius
        // If over the minimum threshold for corner radiuses
        if(outputSeg.startCornerRadius > 0 && outputSeg.startCornerRadius >= minCuttingCornerRadius) {
          
          // Only do this if the corner is not part of an internal shaped cutout
          let cncCutout = cncCutoutList.find(cncCutout => cncCutout.segmentIdList.indexOf(segment.id) != -1)
          if(!cncCutout) {
            var startCornerArc = MathsUtils.generateSegmentCornerRadiusArc(segment, true, false);
            if(startCornerArc) {
              startCornerArc.isCuttingSegment = true
              startCornerArc.isBoundarySegment = outputSeg.isBoundarySegment
              startCornerArc.boundaryIndex = outputSeg.boundaryIndex

              if(edgeworkInfo) {
                startCornerArc.hasEdgework = true
                startCornerArc.orderEditorEdgeworkConfigId = edgeworkInfo.orderEditorEdgeworkConfigId
                startCornerArc.edgeworkStockId = edgeworkInfo.edgeworkStockId
                startCornerArc.edgeworkColour = edgeworkInfo.highlightColour
                startCornerArc.edgeworkAllowance = edgeworkInfo.cornerEdgeworkAllowance   // Note: Changed to arc allowance for corner radiuses
                startCornerArc.edgeworkValue = edgeworkInfo.edgeworkValue
                startCornerArc.edgeworkFeedHeight = edgeworkInfo.edgeworkFeedHeight
              }

              outputSegments.push(startCornerArc)

              // Move segment start point inwards
              outputSeg.startPosition.copy(startCornerArc.endPosition)
              outputSeg.startCornerRadius = 0
            }
          }
        }

        // Only convert start corner radiuses
        // We just bring in the end corner radius
        if(outputSeg.endCornerRadius > 0) {
          var endCornerArc = MathsUtils.generateSegmentCornerRadiusArc(segment, false, false);
          if(endCornerArc) {
            outputSeg.endPosition.copy(endCornerArc.startPosition)
            outputSeg.endCornerRadius = 0
          }
        }

        outputSegments.push(outputSeg)
      }

      // Shaped cuts
      if(segment.type == GlassDesignerTypes.SegmentTypes.arc) {
        if(shapedCuts.find(shapedSegment => shapedSegment.id == segment.id) != null) {
          outputSeg.isCuttingSegment = true
        }

        // If the arc is greater than 180 degrees, subdivide it
        // Intermac OTD export requires <= 180 shaped cuts
        if(segment.calculateArcAngleDistance() > 180) {
          // Subdivide into two arcs
          let subdivisionPoint = segment.getMidpoint()
          let firstSeg = segment.clone()
          var secondSeg = segment.clone()

          firstSeg.endPosition.set(subdivisionPoint.x, subdivisionPoint.y)
          secondSeg.startPosition.set(subdivisionPoint.x, subdivisionPoint.y)

          firstSeg.arcEndAngle = MathsUtils.calculateArcPointAngle(firstSeg.arcCenterPosition, firstSeg.endPosition)
          secondSeg.arcStartAngle = MathsUtils.calculateArcPointAngle(secondSeg.arcCenterPosition, secondSeg.startPosition)
          firstSeg.arcUpdated()
          secondSeg.arcUpdated()

          if(edgeworkInfo) {
            firstSeg.hasEdgework = true
            firstSeg.orderEditorEdgeworkConfigId = edgeworkInfo.orderEditorEdgeworkConfigId
            firstSeg.edgeworkStockId = edgeworkInfo.edgeworkStockId
            firstSeg.edgeworkColour = edgeworkInfo.highlightColour
            firstSeg.edgeworkAllowance = edgeworkInfo.edgeworkAllowance
            firstSeg.edgeworkValue = edgeworkInfo.edgeworkValue
            firstSeg.edgeworkFeedHeight = edgeworkInfo.edgeworkFeedHeight
            secondSeg.hasEdgework = true
            secondSeg.orderEditorEdgeworkConfigId = edgeworkInfo.orderEditorEdgeworkConfigId
            secondSeg.edgeworkStockId = edgeworkInfo.edgeworkStockId
            secondSeg.edgeworkColour = edgeworkInfo.highlightColour
            secondSeg.edgeworkAllowance = edgeworkInfo.edgeworkAllowance
            secondSeg.edgeworkValue = edgeworkInfo.edgeworkValue
            secondSeg.edgeworkFeedHeight = edgeworkInfo.edgeworkFeedHeight
          }

          firstSeg.isCuttingSegment = outputSeg.isCuttingSegment
          secondSeg.isCuttingSegment = outputSeg.isCuttingSegment

          outputSegments.push(firstSeg)
          outputSegments.push(secondSeg)
        } else {
          outputSegments.push(outputSeg)
        }
      }
    })

    // Adjust the cutting & boundary lines to pull back away from arcs
    // Find all arcs flagged with isCuttingSegment that have start or end points resting on the cutting line & pull the cutting line back
    // This is done so we cut across edge & corner cutouts for the initial cutting stage of the panel, but don't cut across arcs that are flagged for cutting
    var cuttingLineList = this.calculateCuttingLineList()
    var cuttingArcs = outputSegments.filter(seg => seg.type == GlassDesignerTypes.SegmentTypes.arc && seg.isCuttingSegment)
    cuttingLineList.forEach(cuttingLine => {

      // Need to find all line segments resting on this cutting line
      // Get total start/end bounds
      let cuttingLineCoverage = this._calculateCuttingLineCoverage(cuttingLine)
      if(!cuttingLineCoverage)
        return

      // If the cutting line is connected to an arc, pull back the cutting line to the coverage
      let setCuttingToStart = false
      let setCuttingToEnd = false

      cuttingArcs.forEach(cutArc => {
        let lineMid = new THREE.Vector2((cuttingLineCoverage.max.x + cuttingLineCoverage.min.x) / 2, (cuttingLineCoverage.max.y + cuttingLineCoverage.min.y) / 2)

        let arcStartDist = MathsUtils.calculateDistanceToPlane2D(cuttingLine.planeNormal, cuttingLine.segMidPoint, cutArc.startPosition)
        let arcEndDist = MathsUtils.calculateDistanceToPlane2D(cuttingLine.planeNormal, cuttingLine.segMidPoint, cutArc.endPosition)
        
        let startConnDist = MathsUtils.calculateDistanceToPlane2D(cuttingLine.segDir, lineMid, cutArc.startPosition)
        let endConnDist = MathsUtils.calculateDistanceToPlane2D(cuttingLine.segDir, lineMid, cutArc.endPosition)

        if(Math.abs(arcStartDist) < 1.0) {
          if(startConnDist > 0)
            setCuttingToEnd = true
          else
            setCuttingToStart = true
        }
        if(Math.abs(arcEndDist) < 1.0) {
          if(endConnDist > 0)
            setCuttingToEnd = true
          else
            setCuttingToStart = true
        }
      })

      if(setCuttingToStart) {
        cuttingLine.startPosition = cuttingLineCoverage.min.clone()
      }
      if(setCuttingToEnd) {
        cuttingLine.endPosition = cuttingLineCoverage.max.clone()
      }
    })

    var boundsCuttingLineList = cuttingLineList.filter(cutLine => cutLine.isOnBounds)
    cuttingLineList = cuttingLineList.filter(cutLine => !cutLine.isOnBounds)

    // Calculate the segments on the cutting lines / boundary lines
    outputSegments.forEach((outputSeg, segmentIndex) => {
      if(outputSeg.type == GlassDesignerTypes.SegmentTypes.line) {
        let cuttingLine = cuttingLineList.find(cutLine => {
          // Segment must match cutting line
          if(Math.abs(cutLine.segDir.dot(outputSeg.getDirection())) > 0.95) {
            let cutDist = MathsUtils.calculateDistanceToPlane2D(cutLine.planeNormal, cutLine.segMidPoint, outputSeg.getMidpoint())
            if(Math.abs(cutDist) < 1.0)
              return true
          }

          return false
        })

        if(cuttingLine) {
          outputSeg.isCuttingSegment = true

          // Extend the segment to the full cutting line
          outputSeg.origStartPosition = outputSeg.startPosition
          outputSeg.origEndPosition = outputSeg.endPosition

          outputSeg.startPosition = cuttingLine.startPosition.clone()
          outputSeg.endPosition = cuttingLine.endPosition.clone()
        }

        // Check if the segment is resting on the boundary of the panel bounds
        // This is used for cutting if trim is added to the panel in optimising
        if(!outputSeg.isCuttingSegment) {
          var dirThreshold = 0.90
          var boundsDistThreshold = 0.5
          
          var segMidPoint = outputSeg.getMidpoint()
          var segDir = outputSeg.getDirection()

          // Check if on the bounds
          if(Math.abs(segDir.dot(new THREE.Vector2(1,0))) > dirThreshold) {
            if(Math.abs(segMidPoint.y - shapeBounds.min.y) < boundsDistThreshold) {
              outputSeg.isBoundarySegment = true
              outputSeg.boundaryIndex = 0
            }
            if(Math.abs(segMidPoint.y - shapeBounds.max.y) < boundsDistThreshold) {
              outputSeg.isBoundarySegment = true
              outputSeg.boundaryIndex = 2
            }
          }
          if(Math.abs(segDir.dot(new THREE.Vector2(0,1))) > dirThreshold) {
            if(Math.abs(segMidPoint.x - shapeBounds.min.x) < boundsDistThreshold) {
              outputSeg.isBoundarySegment = true
              outputSeg.boundaryIndex = 3
            }
            if(Math.abs(segMidPoint.x - shapeBounds.max.x) < boundsDistThreshold) {
              outputSeg.isBoundarySegment = true
              outputSeg.boundaryIndex = 1
            }
          }

          if(outputSeg.isBoundarySegment) {
            // Match up with a boundary cutting line & extend out if required
            // Done if multiple segments on a boundary (eg, around a side cutout)
            let boundaryCuttingLine = boundsCuttingLineList.find(cutLine => {
              // Segment must match cutting line
              if(Math.abs(cutLine.segDir.dot(outputSeg.getDirection())) > 0.95) {
                let cutDist = MathsUtils.calculateDistanceToPlane2D(cutLine.planeNormal, cutLine.segMidPoint, outputSeg.getMidpoint())
                if(Math.abs(cutDist) < 1.0)
                  return true
              }
    
              return false
            })

            if(boundaryCuttingLine) {
              outputSeg.origStartPosition = outputSeg.startPosition
              outputSeg.origEndPosition = outputSeg.endPosition

              outputSeg.startPosition = boundaryCuttingLine.startPosition.clone()
              outputSeg.endPosition = boundaryCuttingLine.endPosition.clone()
            }
          }
        }
      }
    })

    // Remove duplicate straight lines
    // Occurs when multiple lines are colinear on the same cutting lines & projected out
    const duplicatePointThreshold = 0.00001
    const duplicateFields = ['hasEdgework', 'isCuttingSegment', 'isBoundarySegment', 'orderEditorEdgeworkConfigId']
    outputSegments = _.uniqWith(outputSegments, (seg1, seg2) => {
      if(seg1.type != GlassDesignerTypes.SegmentTypes.line)
        return false
      if(seg1.type != seg2.type)
        return false

      if(duplicateFields.filter(field => seg1[field] == seg2[field]).length != duplicateFields.length)
        return false
      if(!MathsUtils.check2DPointsEqual(seg1.startPosition, seg2.startPosition, duplicatePointThreshold))
        return false
      if(!MathsUtils.check2DPointsEqual(seg1.endPosition, seg2.endPosition, duplicatePointThreshold))
        return false
      
      return true
    })

    // Setup neighbours for connection tests
    outputSegments.forEach((segment, segmentIndex) => {
      let prevSegment = outputSegments[segmentIndex == 0 ? outputSegments.length-1 : segmentIndex-1]
      let nextSegment = outputSegments[(segmentIndex + 1) % outputSegments.length]

      segment.prevSegmentId = prevSegment.id
      segment.nextSegmentId = nextSegment.id
    })

    // Ignore segments that don't have edgework or cutting info
    outputSegments = outputSegments.filter(segment => {
      return segment.isCuttingSegment || segment.hasEdgework || segment.isBoundarySegment
    })

    // Adjust segments for the allowances
    outputSegments.forEach(segment => {
      if(segment.hasEdgework && segment.edgeworkAllowance > 0) {

        // Add edgework allowances to the line positions
        if(segment.type == GlassDesignerTypes.SegmentTypes.line) {
          let outDirAllowance = segment.getOutDirection().multiplyScalar(segment.edgeworkAllowance)

          segment.startPosition.add(outDirAllowance)
          segment.endPosition.add(outDirAllowance)
        }
        if(segment.type == GlassDesignerTypes.SegmentTypes.arc) {
          // Expand arc outwards
          segment.arcRadius += segment.edgeworkAllowance
          segment.arcUpdated()
        }
      }
    })

    // For all lines that were pushed out due to edgework allowances, stretch out neighbouring lines to connect with the moved lines
    // Ignore if the segment is no longer connected to neighbours (ie, due to segment not being valid)
    var connectionTolerance = 0.1
    var maxStretchDistance = 100    // Should never exceed this. Prevents invalid projections
    outputSegments.forEach(segment => {
      let prevSegment = outputSegments.find(seg => seg.id == segment.prevSegmentId && seg.type == GlassDesignerTypes.SegmentTypes.line)
      let nextSegment = outputSegments.find(seg => seg.id == segment.nextSegmentId && seg.type == GlassDesignerTypes.SegmentTypes.line)

      if(segment.type == GlassDesignerTypes.SegmentTypes.line) {

        // Cast the 2 segments out as rays & calculate the intersection of the 2 rays
        if(prevSegment) {
          let startConnPoint = MathsUtils.projectLineIntersection(segment.startPosition, segment.endPosition, prevSegment.startPosition, prevSegment.endPosition)
          if(startConnPoint && startConnPoint.distanceTo(segment.startPosition) > connectionTolerance && startConnPoint.distanceTo(segment.startPosition) < maxStretchDistance) {
            segment.startPosition.copy(startConnPoint)
          }
        }
        if(nextSegment) {
          let endConnPoint = MathsUtils.projectLineIntersection(segment.startPosition, segment.endPosition, nextSegment.startPosition, nextSegment.endPosition)
          if(endConnPoint && endConnPoint.distanceTo(segment.endPosition) > connectionTolerance && endConnPoint.distanceTo(segment.endPosition) < maxStretchDistance) {
            segment.endPosition.copy(endConnPoint)
          }
        }
      }
    })

    // Adjust arcs that have neighbouring lines that have moved due
    // Only handles arcs between 2 lines (eg, corner radiuses, arch tops, d ends)
    // We can assume the prev / next lines will have identical allowances so the arc isn't warped to one side
    try {
      outputSegments.forEach(segment => {
        let prevSegment = outputSegments.find(seg => seg.id == segment.prevSegmentId && seg.type == GlassDesignerTypes.SegmentTypes.line)
        let nextSegment = outputSegments.find(seg => seg.id == segment.nextSegmentId && seg.type == GlassDesignerTypes.SegmentTypes.line)

        if(segment.type == GlassDesignerTypes.SegmentTypes.arc && prevSegment && nextSegment) {
          if(prevSegment.endPosition.distanceTo(segment.startPosition) > connectionTolerance && nextSegment.startPosition.distanceTo(segment.endPosition) > connectionTolerance) {

            var arcHeight = segment.calculateArcHeight()

            // Calculate the distance moved & remove it from arc height so the points moved, but the radius doesn't change
            var arcChordMid = new THREE.Vector2((segment.endPosition.x + segment.startPosition.x) / 2, (segment.endPosition.y + segment.startPosition.y) / 2)
            
            segment.startPosition.copy(prevSegment.endPosition)
            segment.endPosition.copy(nextSegment.startPosition)

            var newArcChordMid = new THREE.Vector2((segment.endPosition.x + segment.startPosition.x) / 2, (segment.endPosition.y + segment.startPosition.y) / 2)
            var chordMidDiff = newArcChordMid.distanceTo(arcChordMid)

            if(arcHeight - chordMidDiff > 0) {
              arcHeight = arcHeight - chordMidDiff
            }
            
            try {
              // Attempt to change
              // Will throw an error if the arc would be invalid with the change. Ignores changing it if there is an error
              // This update is only relevant by 1 or 2 millimetres, so really not super important
              segment.updateArcByHeight(arcHeight)
            } catch(err) {
            }
          }
        }
      })
    } catch(err) {
      console.error(err)
    }

    return outputSegments
  }


  /**
   * Calculate the edge point list
   * Used to calculate the shape after cutting but before CNC / Waterjet processes
   * 
   * Returns an array of Vector2 objects
   */
  calculateEdgePointList() {    
    var minPointDist = 0.1
    var outerBounds = this.calculateGlassCutBounds()

    var cuttingLineList = this.calculateCuttingLineList()
    cuttingLineList = cuttingLineList.filter(cutLine => !cutLine.isOnBounds)

    // Create 4 segments for the rectangle. Apply straight line cuts (not bounds) to split the segments
    // Throw out segments outside of cutting planes
    // Generate hull based on remaining segments

    var boundsSegmentList = [
      new GlassSegment(GlassDesignerTypes.SegmentTypes.line, { startPosition: new THREE.Vector2(outerBounds.min.x, outerBounds.min.y), endPosition: new THREE.Vector2(outerBounds.max.x, outerBounds.min.y) }),
      new GlassSegment(GlassDesignerTypes.SegmentTypes.line, { startPosition: new THREE.Vector2(outerBounds.max.x, outerBounds.min.y), endPosition: new THREE.Vector2(outerBounds.max.x, outerBounds.max.y) }),
      new GlassSegment(GlassDesignerTypes.SegmentTypes.line, { startPosition: new THREE.Vector2(outerBounds.max.x, outerBounds.max.y), endPosition: new THREE.Vector2(outerBounds.min.x, outerBounds.max.y) }),
      new GlassSegment(GlassDesignerTypes.SegmentTypes.line, { startPosition: new THREE.Vector2(outerBounds.min.x, outerBounds.max.y), endPosition: new THREE.Vector2(outerBounds.min.x, outerBounds.min.y) })
    ]

    // Split segments based on cutting planes. Throw out segments outside of the planes
    cuttingLineList.forEach(cuttingLine => {
      
      // Split segments
      for(var i=0; i<boundsSegmentList.length; i++) {
        var boundsSeg = boundsSegmentList[i]

        var intPoint = MathsUtils.calculate2DLineSegmentPlaneIntersection(boundsSeg.startPosition, boundsSeg.endPosition, cuttingLine.planeNormal, cuttingLine.segMidPoint)
        if(intPoint) {
          // If int point is too close to bounds segment points, ignore it
          if(boundsSeg.startPosition.distanceTo(intPoint) < minPointDist || boundsSeg.endPosition.distanceTo(intPoint) < minPointDist)
            continue

          // Split
          var newSeg = boundsSeg.clone()
          boundsSeg.endPosition.copy(intPoint)
          newSeg.startPosition.copy(intPoint)

          boundsSegmentList.splice(i+1, 0, newSeg)
        }
      }

      // Remove segments outside of the cutting line
      boundsSegmentList = boundsSegmentList.filter(boundsSeg => {
        return MathsUtils.calculateDistanceToPlane2D(cuttingLine.planeNormal, cuttingLine.segMidPoint, boundsSeg.getMidpoint()) < 0
      })
    })

    var segmentPoints = []
    boundsSegmentList.forEach(boundsSeg => {
      segmentPoints.push(boundsSeg.startPosition)
      segmentPoints.push(boundsSeg.endPosition)
    })
    cuttingLineList.forEach(cuttingLine => {
      segmentPoints.push(cuttingLine.startPosition)
      segmentPoints.push(cuttingLine.endPosition)
    })
    var edgeHullPoints = EditorHullUtils.generatePointListConvexHull(segmentPoints)
    edgeHullPoints = MathsUtils.optimizePointList(edgeHullPoints)

    return edgeHullPoints
  }


  /**
   * Calculate the total number of perfect arcs in the shape
   * A perfect arc is one that has the start/end point of the arc on the panel boundary edge
   * If multiple perfect arcs are consecutive (eg, circle), consider that a single arc until broken up by a line segment
   */
  calculatePerfectArcCount() {
    let boundTolerance = 5    // Within 5mm tolerance
    let hullTolerance = 5
    let perfectArcRadiusThreshold = 5

    let bounds = this.calculateSegmentBounds()
    var hullPoints = EditorHullUtils.generateSegmentListConvexHull(this.segmentList)

    var perfectArcList = this.segmentList.filter(segment => {
      if(segment.type != GlassDesignerTypes.SegmentTypes.arc)
        return false
      // Ignore counter-clockwise arcs
      if(!segment.arcClockwise)
        return false

      // If start & end on the bounds
      if(MathsUtils.pointOnBoundsEdge(segment.startPosition, bounds, boundTolerance) && MathsUtils.pointOnBoundsEdge(segment.endPosition, bounds, boundTolerance))
        return true

      // If on the hull
      let startClosest = MathsUtils.calculateClosestPointOn2DPolygon(segment.startPosition, hullPoints)
      let endClosest = MathsUtils.calculateClosestPointOn2DPolygon(segment.endPosition, hullPoints)
      if(!startClosest || !endClosest)
        return false

      if(startClosest.distanceTo(segment.startPosition) < hullTolerance && endClosest.distanceTo(segment.endPosition) < hullTolerance)
        return true

      return false
    })

    // Combine consecutive perfect arcs
    let perfectArcIndices = perfectArcList.map(segment => {
      return this.segmentList.findIndex(otherSeg => otherSeg.id == segment.id)
    })

    var blockList = []
    perfectArcIndices.forEach(perfectArcIndex => {
      if(blockList.length > 0) {
        // Check previous arc or handle wrapped around
        if(blockList[blockList.length-1] == perfectArcIndex-1 || blockList[blockList.length-1] == (perfectArcIndex+1) % perfectArcList.length) {
          // If arc radiuses change, it's not a consecutive arc
          let prevRadius = this.segmentList[blockList[blockList.length-1]].arcRadius
          let curRadius = this.segmentList[perfectArcIndex].arcRadius

          if(Math.abs(prevRadius - curRadius) > perfectArcRadiusThreshold)
            blockList.push(perfectArcIndex)
          else
            blockList[blockList.length-1] = perfectArcIndex

        } else {
          blockList.push(perfectArcIndex)
        }
      } else {
        blockList.push(perfectArcIndex)
      }
    })
    
    return blockList.length
  }


  /**
   * Builds the 'shapeData' field object stored for the glass panel in the glass item's OrderItem record
   * The shape data is common data for the glass panel containing all relevant info required by production modules
   * Includes outline, cutouts, holes, straight line cuts, splits, hull shape, etc
   * See orderItem.js in the glass-admin-api codebase for full documentation on the object
   * 
   * The order editor regenerates this data and does not require to read it
   * It's purely for production module purposes
   * 
   * Returns an Object containing all data required by production module for the glass panel
   */
  generateShapeData() {
    var shapeData = {
      path: [],
      holes: [],
      cutouts: [],
      cuttingEdgeworkList: [],
      straightCuts: [],
      shapedCuts: [],
      edgePoints: [],
      hullPoints: [],
      cuttingList: [],
      splitLines: []
    }

    // Calculate origin to offset all positions by
    // Based on the rectangle required for cutting (before all straight line cuts, etc)
    var shapeRoundPlaces = 4
    var outerBounds = this.calculateGlassCutBounds()
    var origin = outerBounds.min

    let convertSegmentToPathItem = (segment) => {
      var pathSegmentData = {
        type: segment.type,
        startPosition: MathsUtils.serializeVector2(MathsUtils.round2DVector(segment.startPosition.clone().sub(origin), shapeRoundPlaces)),
        endPosition: MathsUtils.serializeVector2(MathsUtils.round2DVector(segment.endPosition.clone().sub(origin), shapeRoundPlaces)),
        startCornerRadius: MathsUtils.roundNumber(segment.startCornerRadius, shapeRoundPlaces),
        endCornerRadius: MathsUtils.roundNumber(segment.endCornerRadius, shapeRoundPlaces),
      }

      // Add in extra info for corner radiuses
      if(segment.type == GlassDesignerTypes.SegmentTypes.line) {
        if(pathSegmentData.startCornerRadius > 0 || pathSegmentData.endCornerRadius > 0) {
          pathSegmentData.startCornerRadiusDelta = MathsUtils.serializeVector2(MathsUtils.round2DVector(segment.startCornerRadiusDelta))
          pathSegmentData.endCornerRadiusDelta = MathsUtils.serializeVector2(MathsUtils.round2DVector(segment.endCornerRadiusDelta))
        }
      }

      if(segment.type == GlassDesignerTypes.SegmentTypes.arc) {
        pathSegmentData.arcCenterPosition = MathsUtils.serializeVector2(MathsUtils.round2DVector(segment.arcCenterPosition.clone().sub(origin), shapeRoundPlaces))
        pathSegmentData.arcRadius = MathsUtils.roundNumber(segment.arcRadius, shapeRoundPlaces)
        pathSegmentData.arcStartAngle = MathsUtils.roundNumber(segment.arcStartAngle, shapeRoundPlaces)
        pathSegmentData.arcEndAngle = MathsUtils.roundNumber(segment.arcEndAngle, shapeRoundPlaces)
        pathSegmentData.arcClockwise = segment.arcClockwise
      }

      return pathSegmentData
    }

    // Outline path including edge/corner cutouts
    // Represents what final shape will look like (minus holes)
    shapeData.path = this.segmentList.map(segment => {
      return convertSegmentToPathItem(segment)
    })

    // Holes
    // Used for display purposes inside the main shape path
    shapeData.holes = this.getCenterCutoutList().map(glassHole => {
      return {
        path: glassHole.segmentList.map(segment => convertSegmentToPathItem(segment))
      }
    })

    // Cutout List
    // The cutouts on the shape that require CNC cutting. Includes all edge/corner/center cutouts
    var cncCutoutList = this.calculateCNCCutoutList()
    shapeData.cutouts = cncCutoutList.map(cncCutout => {
      var cutoutInfo = {
        type: cncCutout.type,
        path: cncCutout.pathSegmentList.map(segment => convertSegmentToPathItem(segment)),

        // For waterjet cutting
        geometryShapeType: cncCutout.geometryShapeType,
        radius: cncCutout.radius,
        displayPointList: cncCutout.displayPointList.map(point => {
          let dispPnt = new THREE.Vector2(MathsUtils.roundNumber(point.x, 2), MathsUtils.roundNumber(point.y, 2))
          dispPnt.sub(origin)
          return MathsUtils.serializeVector2(MathsUtils.round2DVector(dispPnt, shapeRoundPlaces))
        })
      }

      return cutoutInfo
    })

    // Cutting + Edgework List
    // Contains the cutting info including edgework & allowances. Used by optimiser
    let cuttingEdgeworkList = this.calculateCuttingEdgeworkList()
    shapeData.cuttingEdgeworkList = cuttingEdgeworkList.map(cuttingSegment => {
      let outputSegment = convertSegmentToPathItem(cuttingSegment)

      // Add in the additional data
      outputSegment.isCuttingSegment = cuttingSegment.isCuttingSegment
      outputSegment.hasEdgework = cuttingSegment.hasEdgework
      outputSegment.isBoundarySegment = cuttingSegment.isBoundarySegment
      outputSegment.boundaryIndex = cuttingSegment.boundaryIndex
      outputSegment.orderEditorEdgeworkConfigId = cuttingSegment.orderEditorEdgeworkConfigId
      outputSegment.edgeworkStockId = cuttingSegment.edgeworkStockId
      outputSegment.edgeworkColour = cuttingSegment.edgeworkColour
      outputSegment.edgeworkAllowance = cuttingSegment.edgeworkAllowance
      outputSegment.edgeworkValue = cuttingSegment.edgeworkValue
      outputSegment.edgeworkFeedHeight = cuttingSegment.edgeworkFeedHeight

      return outputSegment
    })

    // Hull Points
    // Points around the convex hull
    var hullPoints = EditorHullUtils.generateSegmentListConvexHull(this.segmentList)
    if(MathsUtils.is2DPolygonClosed(hullPoints))
      hullPoints.pop()
    if(MathsUtils.get2DPolygonOrientation(hullPoints) > 0)
      hullPoints.reverse()

    shapeData.hullPoints = hullPoints.map(hullPoint => {
      return MathsUtils.serializeVector2(MathsUtils.round2DVector(hullPoint.clone().sub(origin), shapeRoundPlaces))
    })

    // Edge Points
    // Points around the edges after cutting & edgework, before cutouts
    var edgePoints = this.calculateEdgePointList()

    shapeData.edgePoints = edgePoints.map(hullPoint => {
      return MathsUtils.serializeVector2(MathsUtils.round2DVector(hullPoint.clone().sub(origin), shapeRoundPlaces))
    })

    // Split Lines
    shapeData.splitLines = this.getSplitLineList().map(splitLine => {
      return {
        startPosition: MathsUtils.serializeVector2(MathsUtils.round2DVector(splitLine.startPosition.clone().sub(origin), shapeRoundPlaces)),
        endPosition: MathsUtils.serializeVector2(MathsUtils.round2DVector(splitLine.endPosition.clone().sub(origin), shapeRoundPlaces))
      }
    })


    return shapeData
  }


  /**
   * Calculate the 4 corner info of the panel
   * Calculates if the corners have stamps and is square
   * Used for diagram generation for labels
   * 
   */
  calculateCornerData() {
    let angleDisplayPrecision = 2
    var minSegmentCornerDist = 1    // 1mm from corner for segments

    var outerBounds = this.calculateGlassCutBounds()
    
    var stampPanelItem = this.getPanelItemList().find(panelItem => {
      return panelItem.isOrderStampItem
    })

    // Find the major corners near each point of the bounds
    // TL, TR, BR, BL
    var cornerList = [
      { name: 'TopLeft', description: 'Top Left', isSquare: false, isStamp: false, point: outerBounds.min },
      { name: 'TopRight', description: 'Top Right', isSquare: false, isStamp: false, point: new THREE.Vector2(outerBounds.max.x, outerBounds.min.y) },
      { name: 'BottomRight', description: 'Bottom Right', isSquare: false, isStamp: false, point: outerBounds.max },
      { name: 'BottomLeft', description: 'Bottom Left', isSquare: false, isStamp: false, point: new THREE.Vector2(outerBounds.min.x, outerBounds.max.y) },
    ]

    this.segmentList.forEach((segment, index) => {
      if(segment.type != GlassDesignerTypes.SegmentTypes.line)
        return

      var prevSegment = this.getSegmentForIndex(index - 1)
      let primaryDirDegrees = MathsUtils.wrapAngleDegrees(MathsUtils.radiansToDegrees(MathsUtils.get2DVectorAngle(prevSegment.getDirection())))
      let angleDirDegrees = MathsUtils.wrapAngleDegrees(MathsUtils.radiansToDegrees(MathsUtils.get2DVectorAngle(segment.getDirection())))

      let angle = MathsUtils.roundNumber(MathsUtils.calculateShortestAngleDistanceDegrees(primaryDirDegrees, angleDirDegrees), angleDisplayPrecision)

      // Don't show anything if 0 or 180 degrees
      if(MathsUtils.roundNumber(Math.abs(angle - 180.0), angleDisplayPrecision) == 0 || MathsUtils.roundNumber(Math.abs(angle), angleDisplayPrecision) == 0)
        return

      let isSquareAngle = MathsUtils.roundNumber(Math.abs(angle - 90), angleDisplayPrecision) == 0
      if(isSquareAngle) {

        // Flag nearby corners
        cornerList.forEach(corner => {
          if(segment.startPosition.distanceTo(corner.point) < minSegmentCornerDist) {
            corner.isSquare = true
          }
        })
      }
    })

    // Find closest corner the stamp is at
    if(stampPanelItem) {
      var stampPos = stampPanelItem.calculateBounds().getCenter(new THREE.Vector2())
      var stampClosestCorner = cornerList.reduce((prevValue, curValue) => curValue.point.distanceTo(stampPos) < prevValue.point.distanceTo(stampPos) ? curValue : prevValue)
      stampClosestCorner.isStamp = true
    }

    return cornerList
  }


  /**
   * Generate a hash value for the shape data to use to detect changes in the glass layout
   * Only take into account data in the shape that would affect production via shape of the glass
   * 
   * @return {Promise<String>} Returns a promise resolved with a string for the shape data hash
   */
  async generateShapeLayoutHash(shapeData, panelWidth, panelHeight) {

    // Only use outer shape layout details (straight line cuts / edgework allowances)
    // We ignore interior / edge / corner cutouts at waterjet
    // Those can be altered after cutting, and we assume the user knows what they are doing when editing
    // We also only check cutting status during order item edit checks

    // Only copy over values from the shape data that affects the actual size & layout of the glass
    var layoutData = _.cloneDeep({
      cuttingEdgeworkList: shapeData.cuttingEdgeworkList || [],
      splitLines: shapeData.splitLines || [],
      width: panelWidth,
      height: panelHeight
    })

    // Sort the cutting edgework list
    // The segment order will get rotated around due to the editor clipping lib & screw up the hash even if the layout didn't change shape
    layoutData.cuttingEdgeworkList.sort((seg1, seg2) => {
      if(Math.abs(seg1.startPosition.x - seg2.startPosition.x) < 0.001)
        return seg1.startPosition.y - seg2.startPosition.y
      return seg1.startPosition.x - seg2.startPosition.x
    })

    // Move boundary segments into boundary map
    // Only use edgework info for boundary segments
    var boundarySegs = layoutData.cuttingEdgeworkList.filter(seg => seg.isBoundarySegment && seg.hasEdgework)
    layoutData.cuttingEdgeworkList = layoutData.cuttingEdgeworkList.filter(seg => !seg.isBoundarySegment)

    layoutData.boundaryMap = {}
    boundarySegs.forEach(seg => {
      layoutData.boundaryMap[seg.boundaryIndex] = {
        edgeworkAllowance: seg.edgeworkAllowance
      }
    })

    // Remove edgework name/colours/links. We only care about the allowance values
    // Lets people change edgework (or edgework settings) on a segment without recutting a panel (if the allowances are the same)
    layoutData.cuttingEdgeworkList = layoutData.cuttingEdgeworkList.map(edgeworkInfo => {
      return _.pick(edgeworkInfo, ['type', 'startPosition', 'endPosition', 'edgeworkAllowance', 'isCuttingSegment', 'isBoundarySegment'])
    })

    var layoutHash = await Utils.generateStringHash(JSON.stringify(layoutData))

    return Promise.resolve(layoutHash)
  }


  /**
   * Reset the review approval list for the shape
   * Done when the shape has been modified in a way that requires re-approval by the review sidebar
   */
  resetReviewApprovals() {
    this.reviewApprovalList = []
    this.isReviewApproved = false
    this.reviewApprovalOverride = false

    this.editor.flagRenderRequired()
  }


  /**
   * Flag the shape review approvals in the review process
   */
  updateReviewApprovals(approvalList) {
    // Turn off override when updating from review
    this.reviewApprovalOverride = false

    this.reviewApprovalList = approvalList
    this.isReviewApproved = this.reviewApprovalList.every(approval => approval.approved)

    this.updateSceneObject()
    this.editor.flagRenderRequired()
  }


  /**
   * Get the flag if the shape is fully approved in review
   */
  isShapeReviewApproved() {
    return this.isReviewApproved || this.reviewApprovalOverride
  }


  /**
   * Find a matching segment in the CNC cutout list for the shape
   * 
   * Returns the cutout object if found, otherwise null if the segment does not match a segment in any cutouts
   */
  _findSegmentInCNCCutoutList(cncCutoutList, segment) {
    let proxTestThreshold = 0.1

    var segMidpoint = segment.getPoint(0.5);

    let matchingCutout = cncCutoutList.find(cutout => {
      let cutoutSegList = cutout.pathSegmentList.filter(cutoutSeg => cutoutSeg.type == segment.type)

      let matchingArc = cutoutSegList.find(cutoutSeg => {
        // Check if middle point of the arc is on the cutout arc
        if(cutoutSeg.type == GlassDesignerTypes.SegmentTypes.arc) {
          let cutoutArcPnt = cutoutSeg.projectPointOnSegment(segMidpoint, false)
          if(cutoutArcPnt.distanceTo(segMidpoint) < proxTestThreshold)
            return true
        }
        // Check if the start/end points rest on the cutout line
        if(cutoutSeg.type == GlassDesignerTypes.SegmentTypes.line) {
          if(segment.startPosition.distanceTo(cutoutSeg.startPosition) < proxTestThreshold && segment.endPosition.distanceTo(cutoutSeg.endPosition) < proxTestThreshold)
            return true
        }
        return false
      })
      if(matchingArc)
        return true

      return false
    })

    return matchingCutout
  }


  /**
   * Calculate the edgework flag (short, long, etc) for a segment in the shape
   * Used to generate basic edgework descriptions for labels / sheets
   * 
   * Attempts to use A/B versions of shorts & longs based on position relative to the shape
   * This is purely estimated and only used to give production an indication of edgework position
   * 
   * Returns type from OrderItemEdgeworkTypes
   */
  _calculateEdgeworkFlag(segmentStart, segmentEnd) {

    // Calculate the bounds ignoring arcs, only using the vertices
    // We do this as large arcs (eg, arch tops, D ends) is throwing off the long/short calculation here
    var bounds = new THREE.Box2()
    this.segmentList.forEach(segment => {
      bounds.expandByPoint(segment.startPosition)
      bounds.expandByPoint(segment.endPosition)
    })
    var boundsCenter = bounds.getCenter(new THREE.Vector2())

    var edgeworkLength = segmentStart.distanceTo(segmentEnd)
    var edgeworkCenter = MathsUtils.calculateLineMidpoint(segmentStart, segmentEnd)

    var boundsWidth = bounds.max.x - bounds.min.x
    var boundsHeight = bounds.max.y - bounds.min.y
    var longSize = Math.max(boundsWidth, boundsHeight)
    var shortSize = Math.min(boundsWidth, boundsHeight)
    var isNarrowShape = longSize >= shortSize * 3.5    // If long side is more than 3.5x the short side, it's narrow

    // Long if within 95% of largest bounds. Otherwise short
    // If the panel is narrow we reduce threshold to 80%
    // This is based on various shapes in orders that had issues
    // See orders: #O59871
    var longThreshold = 0.95
    if(isNarrowShape)
      longThreshold = 0.8

    var isLong = edgeworkLength > longSize * longThreshold

    // Left/Top is A. Right/Bottom side is B
    if(MathsUtils.calculateDistanceToPlane2D(new THREE.Vector2(1, 1), boundsCenter, edgeworkCenter) > 0) {
      return isLong ? OrderItemEdgeworkTypes.longA : OrderItemEdgeworkTypes.shortA
    } else {
      return isLong ? OrderItemEdgeworkTypes.longB : OrderItemEdgeworkTypes.shortB
    }
  }


  /**
   * Calculate the segment coverage min/max points within a cutting line
   * Used where multiple segments (ie, split by edge cutouts) make up a cutting line runnign along a panel edge
   */
  _calculateCuttingLineCoverage(cuttingLine) {
    if(!_.isArray(cuttingLine.srcSegments) || cuttingLine.srcSegments.length == 0)
      return null

    var minDist = 0
    var maxDist = 0

    cuttingLine.srcSegments.forEach((segment, index) => {
      let startDist = MathsUtils.calculateDistanceToPlane2D(cuttingLine.segDir, cuttingLine.startPosition, segment.startPosition)
      let endDist = MathsUtils.calculateDistanceToPlane2D(cuttingLine.segDir, cuttingLine.startPosition, segment.endPosition)

      // If the segment has start/end corner radius, pull back from that
      if(segment.type == GlassDesignerTypes.SegmentTypes.line) {
        if(segment.startCornerRadius > 0) {
          let roundCornerData = MathsUtils.calculateSegmentDeltaCornerRounding(segment, true)
          if(roundCornerData) {
            startDist = MathsUtils.calculateDistanceToPlane2D(cuttingLine.segDir, cuttingLine.startPosition, roundCornerData.segment2Start)
          }
        }
        if(segment.endCornerRadius > 0) {
          let roundCornerData = MathsUtils.calculateSegmentDeltaCornerRounding(segment, false)
          if(roundCornerData) {
            endDist = MathsUtils.calculateDistanceToPlane2D(cuttingLine.segDir, cuttingLine.startPosition, roundCornerData.segment1End)
          }
        }
      }

      if(index == 0) {
        minDist = Math.min(startDist, endDist)
        maxDist = Math.max(startDist, endDist)
      } else {
        minDist = Math.min(minDist, startDist, endDist)
        maxDist = Math.max(maxDist, startDist, endDist)
      }
    })

    return {
      min: cuttingLine.startPosition.clone().addScaledVector(cuttingLine.segDir, minDist),
      max: cuttingLine.startPosition.clone().addScaledVector(cuttingLine.segDir, maxDist)
    }
  }

  

}